/*
 * Authenticated API client for the Xellerate AI Platform backend.
 *
 * - Base URL from VITE_API_BASE_URL (empty = same-origin; the dev server
 *   proxies /api/* to http://localhost:8000).
 * - Attaches ``Authorization: Bearer <access_token>``.
 * - On 401: attempts POST /api/v1/auth/refresh ONCE (rotating refresh —
 *   the new refresh token replaces the old one), then retries the
 *   original request. If the refresh fails, local auth state is cleared
 *   and the registered auth-failure handler runs (redirect to /login).
 */

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

/** Parse an API timestamp. Backend datetimes are UTC but serialized
 *  without a timezone suffix — treat suffix-less strings as UTC. */
export function parseApiDate(value: string | number | Date | null | undefined): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") return new Date(value);
  let s = String(value).trim();
  // Already has timezone info? (Z or ±HH:MM / ±HHMM at the end)
  const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);
  if (!hasTz && /\d{2}:\d{2}/.test(s)) s = s.replace(" ", "T") + "Z";
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// ── Types ───────────────────────────────────────────────────────────

/*
 * Platform-level role (``users.role``). ``org_admin`` (O3) sits between
 * super_admin and dept_admin: the backend ladder
 * (``deps.role_satisfies``) lets an org_admin satisfy every dept_admin
 * check, scoped to their own organization — so anywhere the UI gates on
 * "dept_admin or above" it must list org_admin too.
 */
/** The six access levels.
 *
 *  The first three are PLATFORM tiers — they see every organisation's rows
 *  — and the last three are TENANT tiers, bounded by an organisation, a
 *  department and a person respectively. What separates the platform three
 *  from one another is not tenancy but which surfaces they reach, which is
 *  the server's `SURFACE_ROLES` table and is editable at /role-mapping. */
export type Role =
  | "super_admin"
  | "platform_admin"
  | "platform_developer"
  | "org_admin"
  | "dept_admin"
  | "member";

// ── Cross-org sharing (O4a) ─────────────────────────────────────────
//
// One table on the server, so one set of types here and one component in
// components/ShareSheetDialog.vue. `ShareableType` must stay in step with
// `RESOURCE_TYPES` in backend/app/models/organizations.py.

export type ShareableType = "agent" | "workflow" | "knowledge_collection" | "form";
/** `edit` exists in the schema but the API refuses to create one — see
 *  the module docstring of backend/app/api/shares.py. */
export type ShareAccess = "use" | "view" | "edit";

/**
 * Origin badge fields, embedded in every shareable resource row.
 *
 * All server-computed. `definition_hidden` in particular is the flag that
 * says WHY an editor is not on offer; a client that only saw the null
 * `graph`/`schema`/`tools_config` would render a broken object.
 */
export interface ResourceOrigin {
  organization_id?: string | null;
  owner_organization_name?: string | null;
  owner_organization_slug?: string | null;
  /** True when this row was reached through a share, not by owning it. */
  is_shared?: boolean;
  share_access?: ShareAccess | null;
  definition_hidden?: boolean;
  /** May the current user administer THIS row's shares? */
  can_share?: boolean;
}

/** One outgoing grant — "we gave X to org Y". */
export interface ResourceShare {
  id: string;
  resource_type: ShareableType;
  resource_id: string;
  resource_name?: string | null;
  resource_slug?: string | null;
  owner_organization_id: string;
  owner_organization_name?: string | null;
  owner_organization_slug?: string | null;
  target_organization_id: string;
  target_organization_name?: string | null;
  target_organization_slug?: string | null;
  access: ShareAccess;
  created_by?: string | null;
  created_by_name?: string | null;
  created_at: string;
  can_administer?: boolean;
}

/** An organization this user may share INTO. */
export interface ShareTarget {
  id: string;
  name: string;
  slug: string;
}

/** One thing a consumer would gain reach over, beyond the resource itself. */
export interface ShareImpactItem {
  id: string;
  name: string;
  detail: string | null;
}

/** What sharing a resource exposes BESIDES the resource (O4c). */
export interface ShareImpact {
  resource_type: ShareableType;
  resource_id: string;
  knowledge_collections: ShareImpactItem[];
}

/** One incoming grant — "org Y gave us X". */
export interface SharedResource {
  share_id: string;
  resource_type: ShareableType;
  resource_id: string;
  resource_name?: string | null;
  resource_slug?: string | null;
  access: ShareAccess;
  definition_hidden?: boolean;
  owner_organization_id: string;
  owner_organization_name?: string | null;
  owner_organization_slug?: string | null;
  created_at: string;
}

export interface ShareSummary {
  counts: { outgoing: number; incoming: number };
  outgoing: ResourceShare[];
  incoming: SharedResource[];
}

/** One entry of the header's org switcher (auth payloads only). */
export interface OrgMembership {
  id: string;
  slug: string;
  name: string;
  /** Role INSIDE this organization — may differ from the platform role. */
  role: string;
  is_default?: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  department_id?: string | null;
  department_name?: string | null;
  is_active?: boolean;
  last_login?: string | null;
  /*
   * O3 organization context. Populated by the AUTH endpoints only
   * (login / refresh / me / switch-org); ``GET /users`` leaves them
   * empty, because listing users is not a session.
   */
  organization_id?: string | null;
  organization_name?: string | null;
  organization_role?: string | null;
  organizations?: OrgMembership[];
  /*
   * WHICH SURFACES THIS SESSION MAY REACH — computed by the server from
   * `app/services/surface_policy.py`, the same table the API refuses
   * requests with. The nav filter and the route guard read this list
   * instead of restating the mapping here, so a re-mapping is one edit
   * on the server. It is legibility, never authorization: a typed URL is
   * refused by the middleware whatever this array says.
   */
  surfaces?: string[];
  /** Where to land when a page is out of reach (`/dashboard` is not universal). */
  home_path?: string | null;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  user: AuthUser;
}

// ── Organizations (O3) ──────────────────────────────────────────────

/** What an org owns — rendered by the admin list and by the delete guard. */
export interface OrganizationCounts {
  users: number;
  departments: number;
  agents: number;
  workflows: number;
  forms: number;
}

export interface OrgDomain {
  id: string;
  organization_id: string;
  domain: string;
  /** super_admin-only flag — gates future self-signup/SSO auto-join. */
  is_verified: boolean;
  created_at: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  settings?: Record<string, unknown> | null;
  created_at: string;
  updated_at?: string | null;
  /**
   * Claimed email domains as FULL objects (O3c). They used to be bare
   * strings, which cost the detail pane a `GET /organizations/{id}/domains`
   * purely to learn each row's `id` — the only thing `DELETE
   * .../domains/{domain_id}` needs. One list call now carries it.
   */
  domains: OrgDomain[];
  counts: OrganizationCounts;
  /**
   * The caller's role inside this org — the `organization_members.role`
   * row and nothing more. Honest, and NOT the question the page is asking:
   * the platform super_admin's Wavetec membership genuinely says "member".
   * Render `effective_role` instead; keep this for the membership column.
   */
  my_role?: string | null;
  /**
   * What the caller's authority in this org actually IS: `super_admin` for
   * a platform owner regardless of the membership row, otherwise the
   * membership role. Server-computed (O3c).
   */
  effective_role?: string | null;
  /**
   * May the caller administer this org — domains, members, settings?
   * Computed by the same backend helper that ENFORCES it
   * (`scoping.can_administer_organization`), so the UI never re-derives an
   * authorization decision. Before O3c the browser recomputed this in
   * `auth.canAdministerOrg`; it does not any more.
   */
  can_administer?: boolean;
  /**
   * True when the org holds no `org_admin` membership. Not broken — every
   * super_admin can still administer it — but it has no administrator of
   * its own, which is what the page prompts to fix. `POST /organizations`
   * always returns true: no account is minted for you.
   */
  needs_org_admin?: boolean;
}

export interface OrgMember {
  id: string;
  organization_id: string;
  user_id: string;
  email: string;
  name: string;
  /** Role inside this org (``organization_members.role``). */
  role: string;
  /** Platform role (``users.role``) — the two are deliberately distinct. */
  platform_role: string;
  department_id?: string | null;
  department_name?: string | null;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
}

export interface OrgDepartment {
  id: string;
  organization_id?: string | null;
  name: string;
  slug: string;
  description?: string | null;
  is_active: boolean;
  monthly_budget_usd?: number | null;
  member_count: number;
  created_at: string;
}

export interface Department {
  id: string;
  name: string;
  slug?: string;
  description?: string | null;
  monthly_budget_usd?: number | null;
  is_active?: boolean;
  member_count?: number;
}

export interface ProviderKey {
  id: string;
  provider_id?: string;
  label: string;
  key_hint?: string;
  is_active?: boolean;
  created_at?: string;
  rotated_at?: string | null;
  /** O5 — the owning organization, and whether it is that org's elected default. */
  organization_id?: string | null;
  is_org_default?: boolean;
  /**
   * O5b — could the SERVER read this key back?
   *
   * `false` means the stored ciphertext no longer decrypts under the
   * running `XELLERATE_SECRET_KEY`: the key looks configured, is skipped
   * by the resolver at run time, and the organization is silently on the
   * platform's environment credential. Render it as broken — that is the
   * whole reason the flag exists.
   */
  can_decrypt?: boolean;
}

export type ProviderType =
  | "anthropic"
  | "openai"
  /** M1 — Grok, and the provider real-time voice hangs off in M3. */
  | "xai"
  | "azure_openai"
  | "ollama"
  | "custom";

export interface Provider {
  id: string;
  type: ProviderType;
  name: string;
  base_url?: string | null;
  default_model?: string | null;
  router_model?: string | null;
  is_active?: boolean;
  keys?: ProviderKey[];
  /** O5 — null organization_id means the shared platform catalogue. */
  organization_id?: string | null;
  is_platform?: boolean;
  /** Server's answer to "may I edit this row?" — never re-derived here. */
  can_manage?: boolean;
}

export interface ProviderKeySecret {
  id: string;
  label: string;
  key_hint?: string;
  api_key?: string | null;
}

/*
 * `DeptKeyAssignment` was removed in M2 together with
 * `GET`/`PUT /departments/{id}/provider-keys`. The endpoints went with
 * `department_provider_keys` in M1 — the resolution chain is
 * `organization key -> environment` — and the Departments page's
 * assignment dialog, their only caller, went with them here.
 */

/** M2 — one row of the model catalogue (`llm_models`). */
export interface LlmModel {
  id: string;
  provider_id: string;
  provider_name?: string | null;
  provider_type?: ProviderType | null;
  /** null = the shared PLATFORM catalogue; otherwise the owning tenant. */
  organization_id?: string | null;
  organization_name?: string | null;
  is_platform?: boolean;
  /** The string sent to the vendor. */
  model_id: string;
  display_name: string;
  capabilities?: ModelCapability[];
  is_active?: boolean;
  is_default?: boolean;
  input_price_per_1m?: number | null;
  output_price_per_1m?: number | null;
  /**
   * USD per MINUTE of audio. Non-null means this model is billed on the
   * clock, not by the token, and the two prices above are only its
   * fallback floor. Realtime voice is sold this way and until now the
   * rate lived in a backend constant no operator could reach.
   */
  price_per_minute?: number | null;
  /** USD per billable item. Null = unsourced, which resolves to zero. */
  price_per_item?: number | null;
  created_at?: string;
  /** Server's answer to "may I edit this row?" — never re-derived here. */
  can_manage?: boolean;
}

/** Mirrors `MODEL_CAPABILITIES` in app/models/providers.py. */
export type ModelCapability = "chat" | "vision" | "voice" | "embedding" | "reasoning";

/** M2 — what an agent will actually run on, and why. Server-computed. */
export interface ModelResolution {
  agent_id: string;
  provider_id?: string | null;
  model_id?: string | null;
  model_override?: string | null;
  router_model_override?: string | null;
  effective_model?: string | null;
  effective_model_label?: string | null;
  source: "override" | "catalogue" | "provider_default" | "environment" | "none";
  /** One sentence, rendered verbatim — the client never composes it. */
  explanation: string;
  key_source: "organization" | "environment";
  key_label?: string | null;
  key_provider_name?: string | null;
  key_broken?: boolean;
}

export type AgentType = "master" | "agent" | "sub_agent";

export interface AgentNode extends ResourceOrigin {
  id: string;
  slug?: string;
  name: string;
  description?: string | null;
  type: AgentType;
  parent_id?: string | null;
  intent_key?: string | null;
  intent_description?: string | null;
  intent_keywords?: string[] | null;
  /** M2 — the agent's catalogue selection, as `llm_*` row ids. */
  provider_id?: string | null;
  model_id?: string | null;
  model_override?: string | null;
  router_model_override?: string | null;
  /**
   * M3 — read-only here. Voice is WRITTEN through `voiceApi.putConfig`,
   * because a voice save is two columns plus one key inside the
   * free-form `tools_config` blob and the generic agent PATCH replaces
   * that blob wholesale. Present on this payload so the tree can show a
   * badge without a request per agent.
   */
  voice_enabled?: boolean;
  voice_model_id?: string | null;
  tools_config?: Record<string, unknown> | null;
  system_prompt?: string | null;
  is_published?: boolean;
  position?: number;
  department_ids?: string[];
  children?: AgentNode[];
}

export interface AgentImportResult {
  agent_id: string;
  name: string;
  tools: string[];
  /** Missing secrets, connections, workflows, models — each a whole sentence. */
  warnings: string[];
}

// ── Agent tools ─────────────────────────────────────────────────────

export type AgentToolKind = "rest" | "mcp" | "transfer" | "workflow" | "email";
export type AgentToolAuthType = "none" | "bearer" | "header" | "basic";
export type AgentToolParamIn = "path" | "query" | "body";

export interface AgentToolAuth {
  type: AgentToolAuthType;
  header_name?: string;
  /** Masked hint only — the backend never returns the secret itself. */
  secret_hint?: string;
  /** Plaintext, sent on create/update only. */
  secret?: string;
}

export interface AgentToolParam {
  name: string;
  type?: string;
  required?: boolean;
  description?: string;
  in?: AgentToolParamIn;
}

export interface AgentToolConfig {
  // transfer — "Transfer to person" (voice): an allow-list of PBX Portal targets
  connection?: string | null;
  targets?: { value: string; label?: string; description?: string }[];
  hold_music?: string | null;
  ring_timeout_ms?: number;
  // rest
  method?: string;
  url?: string;
  params?: AgentToolParam[];
  auth?: AgentToolAuth;
  timeout_s?: number;
  // mcp
  transport?: string;
  [key: string]: unknown;
}

export interface AgentTool {
  id: string;
  agent_id?: string;
  name: string;
  description?: string | null;
  kind: AgentToolKind;
  enabled?: boolean;
  position?: number;
  config?: AgentToolConfig | null;
  created_at?: string;
}

export interface ToolRunTestResult {
  status_code?: number;
  ok?: boolean;
  duration_ms?: number;
  response_snippet?: string;
}

export interface SandboxResponse {
  reply?: string;
  agent_id?: string;
  intent?: string;
  confidence?: number;
  model?: string;
  cost?: number;
  latency_ms?: number;
  tool_calls?: any[];
  error?: string;
  /** Inline pipeline trace spans (same shape as GET /api/v1/traces/{run_id}). */
  spans?: Span[];
}

export type PromptKind = "system" | "tone_rules" | "tool_instructions" | "output_format";

export interface PromptVersion {
  id: string;
  kind?: PromptKind;
  version?: number;
  version_number?: number;
  content: string;
  note?: string | null;
  is_active?: boolean;
  created_at?: string;
}

// ── Knowledgebase ───────────────────────────────────────────────────

export type EmbeddingMode = "fts" | "hybrid";
export type KnowledgeDocumentStatus = "pending" | "ingested" | "failed";

export interface KnowledgeCollection extends ResourceOrigin {
  id: string;
  name: string;
  slug?: string;
  description?: string | null;
  embedding_mode?: EmbeddingMode;
  /**
   * The language the DOCUMENTS in this collection are written in
   * (ISO-639-1). Not the user's language — the corpus's. Retrieval is
   * lexical, so a question asked in a different script matches nothing
   * unless the backend can bridge it, and this is the value it bridges
   * TO. Always present on the way out; NULL rows read as "en".
   */
  language?: string;
  document_count?: number;
  total_size_bytes?: number;
  agent_ids?: string[];
  created_at?: string;
}

export interface KnowledgeDocument {
  id: string;
  filename: string;
  size_bytes?: number;
  chunk_count?: number;
  status?: KnowledgeDocumentStatus;
  error?: string | null;
  created_at?: string;
}

export interface KnowledgeSearchResult {
  text: string;
  score?: number;
  source?: string;
}

// ── Errors ──────────────────────────────────────────────────────────

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any) {
    const detail =
      (body && (body.detail || body.error || body.message)) || "HTTP " + status;
    super(typeof detail === "string" ? detail : JSON.stringify(detail));
    this.status = status;
    this.body = body;
  }
}

// ── Token + user persistence ────────────────────────────────────────

const LS_ACCESS = "xai_access_token";
const LS_REFRESH = "xai_refresh_token";
const LS_USER = "xai_user";

export function getAccessToken(): string | null {
  return localStorage.getItem(LS_ACCESS);
}
export function getRefreshToken(): string | null {
  return localStorage.getItem(LS_REFRESH);
}
export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(LS_ACCESS, access);
  localStorage.setItem(LS_REFRESH, refresh);
}
export function clearTokens(): void {
  localStorage.removeItem(LS_ACCESS);
  localStorage.removeItem(LS_REFRESH);
}
export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(LS_USER);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}
export function setStoredUser(user: AuthUser | null): void {
  if (user) localStorage.setItem(LS_USER, JSON.stringify(user));
  else localStorage.removeItem(LS_USER);
}

// Registered by main.ts — runs when a refresh attempt fails (session dead).
let onAuthFailure: (() => void) | null = null;
export function setOnAuthFailure(fn: () => void): void {
  onAuthFailure = fn;
}

function handleAuthFailure(): void {
  clearTokens();
  setStoredUser(null);
  if (onAuthFailure) onAuthFailure();
}

// ── Refresh (single-flight) ─────────────────────────────────────────

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      const rt = getRefreshToken();
      if (!rt) return false;
      try {
        const res = await fetch(API_BASE_URL + "/api/v1/auth/refresh", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: rt }),
        });
        if (!res.ok) return false;
        const data = (await res.json()) as TokenPair;
        // Rotating refresh: always persist BOTH new tokens.
        setTokens(data.access_token, data.refresh_token);
        if (data.user) setStoredUser(data.user);
        return true;
      } catch {
        return false;
      }
    })();
    refreshInFlight.finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// ── Core request wrapper ────────────────────────────────────────────

export interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip Authorization header (login/refresh). */
  noAuth?: boolean;
}

export async function api<T = any>(path: string, opts: ApiOptions = {}, retried = false): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(opts.headers || {}),
  };
  const token = getAccessToken();
  if (token && !opts.noAuth) headers["Authorization"] = "Bearer " + token;

  const res = await fetch(API_BASE_URL + path, {
    method: opts.method || "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401 && !opts.noAuth && !retried) {
    const refreshed = await tryRefresh();
    if (refreshed) return api<T>(path, opts, true);
    handleAuthFailure();
  }

  const text = await res.text();
  let json: any;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = { raw: text };
  }
  if (!res.ok) throw new ApiError(res.status, json);
  return json as T;
}

/*
 * Multipart upload path. The shared `api()` wrapper always JSON-encodes
 * the body and forces Content-Type — for file uploads we hand fetch a
 * FormData and let the browser set the multipart boundary, while keeping
 * the same Bearer header + single 401-refresh-and-retry behaviour.
 */
export async function apiUpload<T = any>(
  path: string,
  form: FormData,
  opts: { method?: string; signal?: AbortSignal } = {},
  retried = false
): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers["Authorization"] = "Bearer " + token;

  const res = await fetch(API_BASE_URL + path, {
    method: opts.method || "POST",
    headers,
    body: form,
    signal: opts.signal,
  });

  if (res.status === 401 && !retried) {
    const refreshed = await tryRefresh();
    if (refreshed) return apiUpload<T>(path, form, opts, true);
    handleAuthFailure();
  }

  const text = await res.text();
  let json: any;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = { raw: text };
  }
  if (!res.ok) throw new ApiError(res.status, json);
  return json as T;
}

/*
 * Raw binary GET (e.g. CSV export). Same Bearer header + single
 * 401-refresh-and-retry behaviour as api(), but returns the response
 * body as a Blob so callers can trigger a client-side download.
 */
export async function apiBlob(path: string, retried = false): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers["Authorization"] = "Bearer " + token;

  const res = await fetch(API_BASE_URL + path, { headers });

  if (res.status === 401 && !retried) {
    const refreshed = await tryRefresh();
    if (refreshed) return apiBlob(path, true);
    handleAuthFailure();
  }

  if (!res.ok) {
    const text = await res.text();
    let json: any;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = { raw: text };
    }
    throw new ApiError(res.status, json);
  }
  return res.blob();
}

/*
 * SSE POST (W3 workflow live runs). Same Bearer header + single
 * 401-refresh-and-retry behaviour as apiBlob(), but reads the response
 * as a text/event-stream and invokes `onEvent` with each parsed
 * `data:` JSON payload. Rejects with ApiError on non-2xx; resolves when
 * the stream ends. Abort via the optional AbortSignal.
 */
export async function apiSse(
  path: string,
  body: unknown,
  onEvent: (event: any) => void,
  signal?: AbortSignal,
  retried = false
): Promise<void> {
  // FormData passes through UNTOUCHED, and without a Content-Type: the
  // browser has to set that header itself so it can append the multipart
  // boundary, and setting it by hand produces a body the server cannot
  // parse. This branch exists because the one Ask AI call that most
  // needs a progress report is the one with a requirements PDF attached
  // — encoding that into JSON to reach this helper would mean base64
  // inflating a 3 MB diagram into a 4 MB string the server must parse
  // before it can even reject it, which is why the multipart route
  // exists in the first place.
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const headers: Record<string, string> = { Accept: "text/event-stream" };
  if (!isForm) headers["Content-Type"] = "application/json";
  const token = getAccessToken();
  if (token) headers["Authorization"] = "Bearer " + token;

  const res = await fetch(API_BASE_URL + path, {
    method: "POST",
    headers,
    body: isForm
      ? (body as FormData)
      : body !== undefined
        ? JSON.stringify(body)
        : undefined,
    signal,
  });

  if (res.status === 401 && !retried) {
    const refreshed = await tryRefresh();
    if (refreshed) return apiSse(path, body, onEvent, signal, true);
    handleAuthFailure();
  }

  if (!res.ok) {
    const text = await res.text();
    let json: any;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      json = { raw: text };
    }
    throw new ApiError(res.status, json);
  }

  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  const flush = (chunk: string) => {
    for (const line of chunk.split("\n")) {
      if (!line.startsWith("data: ")) continue;
      try {
        onEvent(JSON.parse(line.slice(6)));
      } catch {
        /* malformed event — skip */
      }
    }
  };
  // SSE frames are separated by a blank line ("\n\n").
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      flush(buf.slice(0, idx));
      buf = buf.slice(idx + 2);
    }
  }
  if (buf.trim()) flush(buf);
}

// `boolean` is in the union for `voiceApi.list({ recorded: false })`: a
// deliberate `false` must reach the wire as `recorded=false` (only calls
// WITHOUT audio), which is a different question from omitting it. The
// `v !== ""` guard below is what keeps that from being dropped.
function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? "?" + s : "";
}

// ── Endpoint groups ─────────────────────────────────────────────────

export const authApi = {
  login: (email: string, password: string) =>
    api<TokenPair>("/api/v1/auth/login", { method: "POST", body: { email, password }, noAuth: true }),
  refresh: (refresh_token: string) =>
    api<TokenPair>("/api/v1/auth/refresh", { method: "POST", body: { refresh_token }, noAuth: true }),
  logout: (refresh_token: string) =>
    api("/api/v1/auth/logout", { method: "POST", body: { refresh_token } }),
  me: () => api<AuthUser>("/api/v1/auth/me"),
  /** Act as a different organization. Returns a FRESH token pair — the
   *  caller must persist both, or the next request keeps the old org. */
  switchOrg: (organization_id: string) =>
    api<TokenPair>("/api/v1/auth/switch-org", {
      method: "POST",
      body: { organization_id },
    }),
};

/*
 * Organizations admin API (O3a). Reach vs. tenant are two different
 * gates on the backend: the role ladder decides whether you reach the
 * endpoint, ``can_administer_organization`` decides which tenant you may
 * touch there. In practice that means an org_admin can only administer
 * the org they are CURRENTLY ACTING AS — everything below returns 403
 * for another org they merely hold a membership in, so the UI must send
 * them through the switcher first rather than offering a Manage button
 * that cannot work.
 */
export const organizationsApi = {
  /** Every org for a super_admin; only your own memberships otherwise. */
  list: () => api<Organization[]>("/api/v1/organizations"),
  get: (id: string) => api<Organization>(`/api/v1/organizations/${encodeURIComponent(id)}`),
  /** super_admin only (403 otherwise). */
  create: (body: {
    name: string;
    slug?: string | null;
    is_active?: boolean;
    domains?: string[];
  }) => api<Organization>("/api/v1/organizations", { method: "POST", body }),
  /** ``slug`` is immutable; ``is_active: false`` is super_admin-only. */
  update: (id: string, body: { name?: string; is_active?: boolean }) =>
    api<Organization>(`/api/v1/organizations/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),
  /** super_admin only. **409 while the org still owns anything** — the
   *  detail names every non-empty table and is meant to be shown. */
  remove: (id: string) =>
    api(`/api/v1/organizations/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /**
   * Standalone domain list. Since O3c the admin page does NOT use it —
   * `GET /organizations` embeds the same objects on every row. Kept
   * because it is the addressable resource and third parties may hold it.
   */
  listDomains: (id: string) =>
    api<OrgDomain[]>(`/api/v1/organizations/${encodeURIComponent(id)}/domains`),
  /** 409 if another org already claims the domain (detail names the owner). */
  addDomain: (id: string, body: { domain: string; is_verified?: boolean }) =>
    api<OrgDomain>(`/api/v1/organizations/${encodeURIComponent(id)}/domains`, {
      method: "POST",
      body,
    }),
  removeDomain: (id: string, domainId: string) =>
    api(
      `/api/v1/organizations/${encodeURIComponent(id)}/domains/${encodeURIComponent(domainId)}`,
      { method: "DELETE" }
    ),

  listMembers: (id: string, params: { q?: string } = {}) =>
    api<OrgMember[]>(`/api/v1/organizations/${encodeURIComponent(id)}/members` + qs(params)),
  /**
   * THE INVITE OVERRIDE: no domain check, so a wavetec.com contractor can
   * be added to ELFA Electric.
   *
   * Resolves an existing user by email; with `create_user: true` (plus
   * `name` and `password`) it CREATES the account instead of 404-ing —
   * O3c, because "add someone to my organization" is how people join one
   * and the detour via the Users page was the primary flow. The opt-in is
   * explicit so a typo'd address cannot quietly mint a login. The created
   * user is pinned to THIS org; their email domain does not redirect them.
   */
  addMember: (
    id: string,
    body: {
      email?: string;
      user_id?: string;
      role?: string;
      is_default?: boolean;
      create_user?: boolean;
      name?: string;
      /** Plaintext, chosen by the admin — there is no mail on this platform. */
      password?: string;
      department_id?: string | null;
    }
  ) =>
    api<OrgMember>(`/api/v1/organizations/${encodeURIComponent(id)}/members`, {
      method: "POST",
      body,
    }),
  /** 409 when it would demote the org's last administrator. */
  updateMember: (
    id: string,
    userId: string,
    body: { role?: string; is_default?: boolean }
  ) =>
    api<OrgMember>(
      `/api/v1/organizations/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
      { method: "PATCH", body }
    ),
  /** 409 when it would remove the org's last administrator. */
  removeMember: (id: string, userId: string) =>
    api(
      `/api/v1/organizations/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
      { method: "DELETE" }
    ),

  listDepartments: (id: string) =>
    api<OrgDepartment[]>(`/api/v1/organizations/${encodeURIComponent(id)}/departments`),
};

/**
 * Cross-org resource sharing (O4a).
 *
 * Both directions, because the two questions have two audiences: a
 * Wavetec admin asks "what have we given away, and to whom?"
 * (`listOutgoing`, `listForResource`) and an ELFA admin asks "what have
 * we been given?" (`listIncoming`).
 *
 * `create`/`update` refuse `access: "edit"` with a 422 that explains why —
 * a consumer editing a shared object would change it for its owner and for
 * every other org it is shared with. Surface that message; do not filter
 * `edit` out of the UI silently, or the refusal becomes unexplainable.
 */
export const sharesApi = {
  /** Both directions in one call. */
  summary: () => api<ShareSummary>("/api/v1/shares"),
  listIncoming: (params: { resource_type?: ShareableType } = {}) =>
    api<SharedResource[]>("/api/v1/shares/incoming" + qs(params)),
  listOutgoing: (params: { resource_type?: ShareableType } = {}) =>
    api<ResourceShare[]>("/api/v1/shares/outgoing" + qs(params)),
  /** Organizations this user may share into (never their own). */
  listTargets: () => api<ShareTarget[]>("/api/v1/shares/targets"),
  /** The share sheet's own query. 404s for a resource you do not own. */
  listForResource: (resourceType: ShareableType, resourceId: string) =>
    api<ResourceShare[]>(
      `/api/v1/shares/resource/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}`
    ),
  /**
   * What sharing this ALSO exposes (O4c). For an agent: the knowledge
   * collections it reads at run time. A consumer at `use` cannot
   * enumerate or search them, but the agent still retrieves from them
   * and retrieved chunks reach the model, so their contents become
   * quotable. Shown before the share is created, not after.
   */
  impact: (resourceType: ShareableType, resourceId: string) =>
    api<ShareImpact>(
      `/api/v1/shares/impact/${encodeURIComponent(resourceType)}/${encodeURIComponent(resourceId)}`
    ),
  create: (body: {
    resource_type: ShareableType;
    resource_id: string;
    target_organization_id: string;
    access?: ShareAccess;
  }) => api<ResourceShare>("/api/v1/shares", { method: "POST", body }),
  update: (shareId: string, body: { access: ShareAccess }) =>
    api<ResourceShare>(`/api/v1/shares/${encodeURIComponent(shareId)}`, {
      method: "PATCH",
      body,
    }),
  /** Revoke. Effective on the consumer's very next request. */
  remove: (shareId: string) =>
    api(`/api/v1/shares/${encodeURIComponent(shareId)}`, { method: "DELETE" }),
};

export const departmentsApi = {
  list: () => api<Department[]>("/api/v1/departments"),
  create: (body: Partial<Department>) =>
    api<Department>("/api/v1/departments", { method: "POST", body }),
  update: (id: string, body: Partial<Department>) =>
    api<Department>(`/api/v1/departments/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  remove: (id: string) =>
    api(`/api/v1/departments/${encodeURIComponent(id)}`, { method: "DELETE" }),
  // `getProviderKeys` / `setProviderKeys` were removed in M2. The two
  // endpoints they called went with `department_provider_keys` in M1;
  // they had been 404ing ever since. The successor question — "which
  // credential does this organization spend?" — is
  // `providersApi.setOrgDefault`.
};

/* ── R1: NAMED ROLES ("CFO"), which are not access levels ──────────────
 *
 * TWO THINGS IN THIS PRODUCT ARE CALLED A ROLE, AND THEY ARE NOT THE SAME
 * THING. The vocabulary, decided once and used everywhere in the SPA:
 *
 *   Role          this — "CFO", "Head of Quality". A named job inside a
 *                 department, used to ROUTE approval gates. GRANTS NOTHING.
 *   Access level  ``users.role`` — Super Admin / Org Admin / Dept Admin /
 *                 Member. The permission ladder ``auth.canReach`` reads.
 *
 * "Role" goes to this one because it is the customer's own word and the one
 * they use daily; the ladder gets "Access level" because that is literally
 * what its four values are. Anywhere the SPA shows ``users.role`` it says
 * "Access level" — see UsersView and StepConfigDrawer.
 *
 * Server: app/models/roles.py, app/api/department_roles.py.
 */

export interface RoleHolder {
  user_id: string;
  name: string;
  email: string;
  is_active: boolean;
  /** ``users.role`` — shown so an admin can SEE the two are independent.
   *  A CFO who is a "member" is correct, not a misconfiguration. */
  access_level: Role;
  assigned_at?: string | null;
}

/** One approval/form_edit gate, in one workflow, naming this role. */
export interface RoleGateRef {
  workflow_id: string;
  workflow_name: string;
  version: number;
  step_id: string;
  step_name: string;
  step_type: string;
}

/** What the workflow builder needs to fill an assignee picker — ids and
 *  display strings, never the rows themselves. */
export interface BuilderDirectory {
  departments: { id: string; name: string; member_count: number }[];
  roles: {
    id: string;
    name: string;
    department_id: string;
    department_name?: string | null;
    holder_count: number;
  }[];
  users: { id: string; label: string; department_id?: string | null }[];
}

export interface DepartmentRole {
  id: string;
  organization_id: string;
  department_id: string;
  department_name?: string | null;
  name: string;
  slug: string;
  description?: string | null;
  is_active: boolean;
  /** ACTIVE holders only — the number a gate would really resolve to. */
  holder_count: number;
  created_at?: string | null;
}

/**
 * R4 — what ONE holder actually gets out of this role's mapping.
 *
 * A grant lifts the reach gate and lifts nothing else, so assigning
 * somebody whose access level is below the endpoints' own floor creates a
 * page that 403s on load — the same defect the Role Mapping page now
 * warns about, arrived at from the other direction. Nobody is refused for
 * it (a role exists to route approvals whether or not it grants a page),
 * but the dialog that does the assigning has to be able to say it.
 */
export interface HolderReach {
  user_id: string;
  email: string;
  access_level: Role;
  loads: string[];
  dead_on_load: string[];
}

export interface DepartmentRoleDetail extends DepartmentRole {
  holders: RoleHolder[];
  used_by: RoleGateRef[];
  pending_task_count: number;
  /** What holding this role ADDS, as `surface_policy` will honour it. */
  granted_surfaces: string[];
  holder_reach: HolderReach[];
  /** `null` when the role grants nothing — which is every role until mapped. */
  reach_warning: string | null;
}

export const departmentRolesApi = {
  list: (
    params: {
      department_id?: string;
      user_id?: string;
      q?: string;
      include_inactive?: boolean;
    } = {}
  ) =>
    api<DepartmentRole[]>(
      "/api/v1/department-roles" +
        qs({
          department_id: params.department_id,
          user_id: params.user_id,
          q: params.q,
          include_inactive: params.include_inactive ? "true" : undefined,
        })
    ),
  get: (id: string) =>
    api<DepartmentRoleDetail>(`/api/v1/department-roles/${encodeURIComponent(id)}`),
  create: (body: { department_id: string; name: string; description?: string | null }) =>
    api<DepartmentRole>("/api/v1/department-roles", { method: "POST", body }),
  update: (
    id: string,
    body: { name?: string; description?: string | null; is_active?: boolean }
  ) =>
    api<DepartmentRole>(`/api/v1/department-roles/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),
  /** `repointTo` moves every gate naming this role onto another one first
   *  (as a new workflow version). Without it, a referenced role is a 409 —
   *  that refusal is the feature, not an obstacle to route around. */
  remove: (id: string, repointTo?: string) =>
    api(
      `/api/v1/department-roles/${encodeURIComponent(id)}` +
        qs({ repoint_to: repointTo }),
      { method: "DELETE" }
    ),
  addHolder: (id: string, userId: string) =>
    api<DepartmentRoleDetail>(
      `/api/v1/department-roles/${encodeURIComponent(id)}/members`,
      { method: "POST", body: { user_id: userId } }
    ),
  removeHolder: (id: string, userId: string) =>
    api(
      `/api/v1/department-roles/${encodeURIComponent(id)}/members/` +
        encodeURIComponent(userId),
      { method: "DELETE" }
    ),
};

export const usersApi = {
  list: (params: { department_id?: string; q?: string } = {}) =>
    api<AuthUser[]>("/api/v1/users" + qs(params)),
  create: (body: {
    email: string;
    name: string;
    password: string;
    role: Role;
    department_id?: string | null;
  }) => api<AuthUser>("/api/v1/users", { method: "POST", body }),
  update: (id: string, body: Partial<AuthUser> & { password?: string }) =>
    api<AuthUser>(`/api/v1/users/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  remove: (id: string) => api(`/api/v1/users/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

/**
 * O5b — `?organization_id=…` on the four endpoints that take it.
 *
 * Omitted, every one of them behaves exactly as it did before: the
 * organization the caller is acting as. Named, it can only NARROW — the
 * server refuses (403) any principal who is not a platform admin and
 * names an organization other than their own, and decides that before it
 * ever looks the organization up, so it is not an oracle for which
 * tenants exist. Never re-derived here; the client just passes it.
 */
function orgQuery(organizationId?: string | null): string {
  return organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : "";
}

export const providersApi = {
  /** `organizationId` narrows the listed keys to one tenant (O5b). */
  list: (organizationId?: string | null) =>
    api<Provider[]>("/api/v1/providers" + orgQuery(organizationId)),
  /** `is_platform` (super_admin only) creates a shared catalogue row instead
   *  of one owned by the caller's organization — the server enforces it.
   *  `organization_id` names the owning tenant explicitly (O5b). */
  create: (
    body: Partial<Provider> & { is_platform?: boolean; organization_id?: string | null }
  ) => api<Provider>("/api/v1/providers", { method: "POST", body }),
  update: (id: string, body: Partial<Provider>) =>
    api<Provider>(`/api/v1/providers/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  remove: (id: string) => api(`/api/v1/providers/${encodeURIComponent(id)}`, { method: "DELETE" }),
  /** `organizationId` stores the key under that tenant instead of the
   *  caller's active one (O5b) — how a platform admin provisions a key
   *  from the Organizations page without switching organization. */
  addKey: (
    providerId: string,
    body: { label: string; api_key: string },
    organizationId?: string | null
  ) =>
    api<ProviderKey>(
      `/api/v1/providers/${encodeURIComponent(providerId)}/keys` + orgQuery(organizationId),
      { method: "POST", body }
    ),
  rotateKey: (keyId: string, body: { label: string; api_key: string }) =>
    api<ProviderKey>(`/api/v1/providers/keys/${encodeURIComponent(keyId)}/rotate`, {
      method: "POST",
      body,
    }),
  /**
   * O5 — delete a key. The server refuses with 409 while the key is still
   * ELECTED as the organization's default (since M1 that is the only way a
   * key is in use); `force` is the informed-consent retry after the user
   * has seen the consequence.
   */
  deleteKey: (keyId: string, force = false) =>
    api(
      `/api/v1/providers/keys/${encodeURIComponent(keyId)}${force ? "?force=true" : ""}`,
      { method: "DELETE" },
    ),
  /** O5 — elect (or clear) this key as the organization's org-wide default.
   *  `organizationId` (O5b) ASSERTS which organization the caller believes
   *  owns the key; a stale list offering somebody else's key is then
   *  refused as unknown rather than electing the wrong tenant's credential. */
  setOrgDefault: (
    keyId: string,
    is_org_default: boolean,
    organizationId?: string | null
  ) =>
    api<ProviderKey>(
      `/api/v1/providers/keys/${encodeURIComponent(keyId)}/org-default` +
        orgQuery(organizationId),
      { method: "PUT", body: { is_org_default } }
    ),
  /** O5 — plaintext of a key this caller's own organization owns. */
  revealKey: (keyId: string) =>
    api<ProviderKeySecret>(`/api/v1/providers/keys/${encodeURIComponent(keyId)}/secret`),
};

export const agentsApi = {
  /** org_admin+ (and able to edit the agent) — the agent as a file: its
   *  settings, active prompts and tools, with every tool secret removed. */
  exportFile: (id: string) =>
    api<Record<string, unknown>>(`/api/v1/agents/${encodeURIComponent(id)}/export`),
  /** org_admin+ — a NEW agent from an exported file (never overwrites).
   *  409 when the name is taken; `warnings` lists what needs a person. */
  importFile: (body: { file: Record<string, unknown>; name?: string | null; parent_id?: string | null }) =>
    api<AgentImportResult>("/api/v1/agents/import", { method: "POST", body }),
  list: () => api<AgentNode[]>("/api/v1/agents"),
  tree: () => api<AgentNode[]>("/api/v1/agents/tree"),
  create: (body: Partial<AgentNode>) => api<AgentNode>("/api/v1/agents", { method: "POST", body }),
  update: (id: string, body: Partial<AgentNode>) =>
    api<AgentNode>(`/api/v1/agents/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  remove: (id: string) => api(`/api/v1/agents/${encodeURIComponent(id)}`, { method: "DELETE" }),
  setDepartments: (id: string, department_ids: string[]) =>
    api(`/api/v1/agents/${encodeURIComponent(id)}/departments`, {
      method: "PUT",
      body: { department_ids },
    }),
  listPrompts: (id: string, kind?: string) =>
    api<PromptVersion[]>(`/api/v1/agents/${encodeURIComponent(id)}/prompts` + qs({ kind })),
  createPrompt: (
    id: string,
    body: { kind: string; content: string; note?: string; activate?: boolean }
  ) => api<PromptVersion>(`/api/v1/agents/${encodeURIComponent(id)}/prompts`, { method: "POST", body }),
  activatePrompt: (versionId: string) =>
    api(`/api/v1/agents/prompts/${encodeURIComponent(versionId)}/activate`, { method: "POST" }),
  /**
   * M2 — what this agent will actually run on, and where that comes from.
   *
   * Server-computed on purpose: three of the four rungs of the precedence
   * chain (the organization's elected key, the provider it hangs off, the
   * environment default) are facts the browser does not hold. The
   * `explanation` is rendered verbatim.
   */
  modelResolution: (id: string) =>
    api<ModelResolution>(`/api/v1/agents/${encodeURIComponent(id)}/model-resolution`),
};

/**
 * M2 — the model catalogue (`llm_models`), one API for all three views.
 *
 * `scope` is what makes them one endpoint rather than three:
 *   - `platform`     — /providers: the shared rows super_admin maintains
 *   - `organization` — Organizations: only what THIS tenant added
 *   - `all`          — the agent picker, where provenance is irrelevant
 *
 * `capability` is the filter that keeps voice models out of the chat
 * picker (and, in M3, chat models out of the voice one).
 */
export const modelsApi = {
  list: (
    params: {
      organization_id?: string | null;
      provider_id?: string | null;
      capability?: ModelCapability | null;
      scope?: "all" | "platform" | "organization";
      /** Default true server-side; pass false to include retired rows. */
      active_only?: boolean;
    } = {}
  ) =>
    api<LlmModel[]>(
      "/api/v1/models" +
        qs({
          organization_id: params.organization_id,
          provider_id: params.provider_id,
          capability: params.capability,
          scope: params.scope,
          active_only: params.active_only === false ? "false" : undefined,
        })
    ),
  create: (
    body: Partial<LlmModel> & {
      provider_id: string;
      model_id: string;
      is_platform?: boolean;
      organization_id?: string | null;
    }
  ) => api<LlmModel>("/api/v1/models", { method: "POST", body }),
  update: (id: string, body: Partial<LlmModel>) =>
    api<LlmModel>(`/api/v1/models/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  remove: (id: string) => api(`/api/v1/models/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

// ── Voice (M3) ──────────────────────────────────────────────────────
//
// Configuration and BOOKKEEPING. No audio and no WebSocket in this
// phase — the relay is M4 — which is what makes every refusal, the
// concurrency cap and the cost arithmetic exercisable over plain HTTP.
//
// There is NO ephemeral-token endpoint and no session payload ever
// carries a credential. Tokens exist only for the browser-direct
// pattern the plan rejected in favour of a server relay; with a relay
// the browser holds nothing.

/** The tuning that rides in `tools_config['voice_config']`. */
export interface VoiceTuning {
  /** LOWERCASE on the wire — `eve`, `ara`, … or a custom cloned id. */
  voice?: string;
  /**
   * The language of the call (§4d): a BCP-47 code, or `auto` to let xAI
   * detect it. Becomes `audio.input.transcription.language_hint` in
   * `session.update` AND a directive in the spoken instructions, because
   * the model answers in whatever language the transcript came back in —
   * so a transcriber left to guess between two similar-sounding
   * languages decides the reply language too.
   */
  language?: string;
  /**
   * Names to bias the RECOGNISER toward — brands, towns, product lines.
   * Becomes `audio.input.transcription.keyterms`, beside the model and
   * the language hint, because that is the block that configures the
   * transcriber. Unlike `language` it does not change what language the
   * call is decoded as, so it is safe on a line in any language.
   * At most 100 terms of 50 characters; an over-long list is refused
   * rather than truncated.
   */
  keyterms?: string[];
  /**
   * `written → spoken`, for a name the voice mispronounces. A TOP-LEVEL
   * `replace` in `session.update`, NOT part of the audio input block:
   * it rewrites text on its way to the speaker, so the phonetic spelling
   * never reaches the transcript — which matters because the transcript
   * is quoted on a complaint form as the customer's own words.
   */
  replace?: Record<string, string>;
  vad_enabled?: boolean;
  /** xAI's documented range is 0.1–0.9 (default 0.85). */
  vad_threshold?: number;
  /** `turn_detection.silence_duration_ms`, 0–10000. */
  vad_silence_ms?: number;
  /** `turn_detection.prefix_padding_ms`, 0–10000 (default 333). */
  vad_prefix_padding_ms?: number;
  vad_idle_timeout_ms?: number | null;
  /** Appended to the agent's system prompt for spoken delivery. */
  instructions_extra?: string;
  /**
   * V8 §4b.4 — **the first key in this object that is OURS rather than
   * xAI's.** Every field above becomes part of `session.update`;
   * `record_calls` never can, because the vendor has never heard of it.
   * The backend keeps the two apart as `VOICE_WIRE_DEFAULTS` and
   * `VOICE_PLATFORM_KEYS` so its completeness check can still say "every
   * wire key reaches the wire" without lying about this one.
   *
   * Opt-in per agent, off by default, and switching it on has
   * preconditions: an announcement WAV and a configured store, or the
   * agent REFUSES to take calls rather than recording them silently
   * (`reason: "no_recording"`).
   */
  /** Which engine conducts the call: `realtime` (model on an open socket),
   *  `ivr` (recorded prompts + DTMF, no model) or `stt_tts` (a conversation
   *  run as speech-to-text → reply model → text-to-speech). A PLATFORM
   *  key — xAI has never heard of it. */
  engine?: "realtime" | "ivr" | "stt_tts";
  /** The reply model on `stt_tts` only. `openai` uses the organization's
   *  OpenAI key; an empty model means the engine's default. */
  reply_provider?: "xai" | "openai";
  reply_model?: string;
  record_calls?: boolean;
}

export interface VoiceAgentConfig {
  agent_id: string;
  voice_enabled: boolean;
  voice_model_id?: string | null;
  /** True when a session could start right now. */
  available: boolean;
  reason: "ok" | "disabled" | "no_model" | "no_credential" | "no_recording";
  /** One sentence, rendered verbatim — never composed here. */
  explanation: string;
  resolved_model?: {
    model_id: string;
    model_row_id?: string | null;
    display_name?: string | null;
    source: "agent" | "organization" | "platform" | "environment";
  } | null;
  credential?: {
    source: "organization" | "environment";
    available: boolean;
    key_label?: string | null;
    provider_name?: string | null;
    key_mismatch?: boolean;
  } | null;
  config: VoiceTuning;
  defaults: VoiceTuning;
  voices: { value: string; label: string }[];
  /**
   * The call-language picker. `documented: false` marks a language xAI
   * does not list as natively supported — the transcription hint may be
   * ignored for it and only the spoken directive is guaranteed to apply.
   */
  languages: { value: string; label: string; documented: boolean }[];
  /**
   * How many voice models this organization can reach. ZERO is what
   * makes the editor's voice block inert rather than merely empty.
   */
  catalogue_size: number;
  definition_hidden: boolean;
  max_concurrent_sessions: number;
  open_sessions: number;
}

export interface VoiceSession {
  id: string;
  organization_id: string;
  agent_id: string;
  conversation_id: string;
  user_id?: string | null;
  department_id?: string | null;
  model_used: string;
  voice?: string | null;
  status: "active" | "ended" | "expired" | "failed";
  started_at: string;
  ended_at?: string | null;
  /**
   * The wall clock this session HELD. For one the reaper had to close it
   * can be hours — a browser shut mid-call never sends its end — and that
   * gap is the evidence, so it is deliberately not capped.
   */
  duration_ms?: number | null;
  /**
   * What was BILLED: `duration_ms` for an ordinary call, and at most
   * `VOICE_SESSION_MAX_MINUTES` for one that was reaped. Two facts, both
   * needed — the first says clients are not closing their sessions, the
   * second is the money.
   */
  billed_ms?: number | null;
  message_count: number;
  cost_usd: number;
  end_reason?: string | null;
  error?: string | null;
  agent_run_id?: string | null;

  // ── The recording (V8 §4b.3) ────────────────────────────────────────
  // **There is no `recording_uri` here and there is not meant to be.** The
  // server never puts it in a payload: a URI in a list is a URI in browser
  // history, in a log, in a screenshot and in a support ticket. The bytes
  // come from `voiceApi.recording()`, which streams them through the API
  // under the same principal check as every other read of this row.
  /**
   * Why there is no audio, when there is none — five different states that
   * a missing player would collapse into one shrug.
   */
  recording_status?:
    | "none"        // nobody asked to record this call
    | "recording"   // still in progress
    | "stored"      // there is audio and it is playable
    | "failed"      // it was meant to be recorded and was not
    | "deleted";    // retention took it, or somebody exercised erasure
  /**
   * The duration of the AUDIO, which is deliberately not `duration_ms`.
   * The file is the two legs of the call in arrival order, so on any call
   * where anybody spoke the two numbers differ.
   */
  recording_ms?: number | null;
  recording_bytes?: number | null;
  /** When retention will take it. Shown so "gone on the 12th" is answerable. */
  recording_expires_at?: string | null;
}

export interface VoiceSessionStart {
  session: VoiceSession;
  realtime_url: string;
  model_id: string;
  voice: string;
  config: VoiceTuning;
  /** Exactly the `session.update` M4's relay must send. No credential. */
  session_update: Record<string, unknown>;
}

// ── Voice analytics (the /voice-analytics page) ─────────────────────
//
// Every duration is MILLISECONDS, exactly as on `WorkflowAnalytics`, so
// both pages go through the one shared `fmtDuration`.
//
// There is no `recording_uri` anywhere below and there is not meant to
// be — the aggregate does not even select the column.

/** Same shape and same nearest-rank percentiles as the workflow page. */
export interface VoiceDistribution {
  count: number;
  mean_ms: number | null;
  p50_ms: number | null;
  p90_ms: number | null;
  min_ms: number | null;
  max_ms: number | null;
  total_ms: number;
  samples_ms: number[];
}

export interface VoiceTotals {
  calls: number;
  open_calls: number;
  failed_calls: number;
  expired_calls: number;
  agents: number;
  callers: number;
  departments: number;
  /** Wall clock HELD by the calls in scope. */
  duration_ms: number;
  /**
   * Wall clock BILLED. Lower than `duration_ms` exactly when sessions had
   * to be reaped, and the difference is time nobody was talking for.
   */
  billed_ms: number;
  cost_usd: number;
  messages: number;
  first_call_at: string | null;
  last_call_at: string | null;
}

export interface VoiceBucket {
  /** ISO date of the bucket's first day. */
  bucket: string;
  calls: number;
  duration_ms: number;
  cost_usd: number;
  recorded: number;
  failed: number;
}

export interface VoiceVolume {
  /** day | week | month — chosen from the span the DATA covers. */
  unit: string;
  buckets: VoiceBucket[];
}

/**
 * How calls ended, grouped. This is the operational signal the page
 * exists for: `at_capacity` and `no_credential` are two completely
 * different problems with two different owners, and `status="failed"`
 * flattens both into one word.
 */
export interface VoiceEndingCategory {
  category: string;
  label: string;
  note: string;
  calls: number;
  pct: number;
}

export interface VoiceEndingReason {
  /** The raw `end_reason`, verbatim. Null when none was recorded. */
  reason: string | null;
  label: string;
  category: string;
  calls: number;
  duration_ms: number;
  cost_usd: number;
  pct: number;
}

export interface VoiceEndings {
  categories: VoiceEndingCategory[];
  reasons: VoiceEndingReason[];
  /** Rendered verbatim — `end_reason` is free text and the first closer wins. */
  caveat: string;
  scope_note: string;
}

export interface VoiceAgentStat {
  agent_id: string | null;
  label: string;
  deleted: boolean;
  calls: number;
  failed: number;
  recorded: number;
  callers: number;
  duration_ms: number;
  cost_usd: number;
  duration: VoiceDistribution;
}

export interface VoiceDepartmentStat {
  department_id: string | null;
  label: string;
  calls: number;
  recorded: number;
  duration_ms: number;
  cost_usd: number;
}

export interface VoiceCallerStat {
  user_id: string | null;
  label: string;
  calls: number;
  duration_ms: number;
  cost_usd: number;
}

export interface VoiceModelStat {
  model_used: string;
  calls: number;
  duration_ms: number;
  cost_usd: number;
}

export interface VoiceRecordingCoverage {
  counts: { status: string; calls: number; pct: number }[];
  total_calls: number;
  /** Calls somebody ASKED to record — the honest denominator. */
  asked: number;
  playable: number;
  coverage_pct: number;
  bytes_held: number;
  audio: VoiceDistribution;
  expiring_soon: number;
  expiring_soon_days: number;
  /** Past retention and still stored: the deleter has not reached them. */
  overdue: number;
  next_expiry_at: string | null;
  plays: number;
  listeners: number;
  deletions: number;
  last_played_at: string | null;
}

/**
 * The honest half of the page.
 *
 * `derivable` is always `false` today and it is a FIELD rather than a
 * comment so the page renders the absence as a stated absence. A call
 * flow's interpreter persists nothing — visited steps, collect outcomes
 * and the branch taken die with the worker task — so what is counted here
 * is only the flow's last durable act: the workflow run it started.
 */
export interface VoiceFlows {
  cases_opened: number;
  with_reference: number;
  by_status: { status: string; runs: number; pct: number }[];
  duration: VoiceDistribution;
  derivable: boolean;
  explanation: string;
  missing: { question: string; why: string; needs: string }[];
}

export interface VoiceAnalytics {
  date_from: string | null;
  date_to: string | null;
  agent_id: string | null;
  /** The call set hit the server's cap; these are the most recent N. */
  truncated: boolean;
  max_sessions: number;
  totals: VoiceTotals;
  duration: VoiceDistribution;
  volume: VoiceVolume;
  endings: VoiceEndings;
  agents: VoiceAgentStat[];
  agents_hidden: number;
  departments: VoiceDepartmentStat[];
  departments_hidden: number;
  callers: VoiceCallerStat[];
  callers_hidden: number;
  models: VoiceModelStat[];
  voices: { voice: string; calls: number }[];
  recordings: VoiceRecordingCoverage;
  flows: VoiceFlows;
}

export const voiceApi = {
  getConfig: (agentId: string) =>
    api<VoiceAgentConfig>(`/api/v1/voice/agents/${encodeURIComponent(agentId)}/config`),
  putConfig: (
    agentId: string,
    body: { voice_enabled?: boolean; voice_model_id?: string | null; config?: VoiceTuning }
  ) =>
    api<VoiceAgentConfig>(`/api/v1/voice/agents/${encodeURIComponent(agentId)}/config`, {
      method: "PUT",
      body,
    }),
  start: (agent_id: string, conversation_id?: string | null) =>
    api<VoiceSessionStart>("/api/v1/voice/sessions", {
      method: "POST",
      body: { agent_id, conversation_id },
    }),
  end: (
    sessionId: string,
    body: {
      message_count?: number;
      duration_ms?: number;
      end_reason?: string;
      error?: string;
      failed?: boolean;
    } = {}
  ) =>
    api<VoiceSession>(`/api/v1/voice/sessions/${encodeURIComponent(sessionId)}/end`, {
      method: "POST",
      body,
    }),
  get: (sessionId: string) =>
    api<VoiceSession>(`/api/v1/voice/sessions/${encodeURIComponent(sessionId)}`),
  list: (
    params: {
      agent_id?: string | null;
      /** V8 — the calls that belong beside ONE transcript thread. */
      conversation_id?: string | null;
      status?: string | null;
      /** ISO instants. Narrowing only — applied after the scope ladder. */
      date_from?: string | null;
      date_to?: string | null;
      /** true = only calls with playable audio. Omitted = both. */
      recorded?: boolean | null;
      limit?: number;
    } = {}
  ) => api<VoiceSession[]>("/api/v1/voice/sessions" + qs(params)),
  /**
   * The aggregate behind `/voice-analytics`. dept_admin and above at the
   * endpoint; which CALLS it counts is `scoping.run_scope_conditions`,
   * exactly the ladder `list` above applies — so the totals here and the
   * rows there describe the same set for the same person.
   */
  analytics: (
    params: {
      agent_id?: string | null;
      date_from?: string | null;
      date_to?: string | null;
    } = {}
  ) => api<VoiceAnalytics>("/api/v1/voice/analytics" + qs(params)),
  /**
   * The call audio, as a blob URL for an `<audio>` element.
   *
   * Fetched rather than pointed at, because the endpoint needs the bearer
   * token and `<audio src>` cannot send one — and because the alternative
   * (a signed URL in the markup) is the thing §4b.3 refuses. The caller
   * owns the returned URL and must `URL.revokeObjectURL` it.
   *
   * Every call to this writes an audit row naming who listened and when.
   */
  recording: async (sessionId: string): Promise<string> => {
    const res = await fetch(
      API_BASE_URL +
        `/api/v1/voice/sessions/${encodeURIComponent(sessionId)}/recording`,
      { headers: { Authorization: "Bearer " + (getAccessToken() || "") } }
    );
    if (!res.ok) {
      let detail = `Could not load the recording (${res.status}).`;
      try {
        const body = await res.json();
        if (typeof body?.detail === "string") detail = body.detail;
      } catch {
        /* the server said nothing readable; the status is the message */
      }
      throw new Error(detail);
    }
    return URL.createObjectURL(await res.blob());
  },
  /**
   * Destroy the audio — the object AND the row's pointer to it. org_admin
   * of the owning organization or super_admin only: somebody who may PLAY
   * a recording must not be able to destroy evidence.
   *
   * **This does not delete the transcript.** They are separate artefacts
   * with separate retention.
   */
  deleteRecording: (sessionId: string, reason: string) =>
    api<VoiceSession>(
      `/api/v1/voice/sessions/${encodeURIComponent(sessionId)}/recording` +
        qs({ reason }),
      { method: "DELETE" }
    ),
};

/**
 * The relay socket's URL (M5). `WS /api/v1/voice/stream`.
 *
 * THE JWT IS A QUERY PARAMETER, and that is not a shortcut. Browsers
 * cannot set headers on a WebSocket handshake — there is no header
 * argument on the constructor and no preflight to carry one — so a
 * credential has to travel in the URL. `backend/app/api/channels.py:104`
 * set the precedent and `voice_ws.py` implements the same thing; it also
 * accepts `Authorization` for non-browser callers, which is why this
 * function is the browser-only half of a two-sided scheme rather than the
 * scheme itself.
 *
 * The cost is real: a query string reaches access logs, proxies and
 * `Referer` in a way a header does not. It is mitigated the two ways
 * available to a client — this is a short-lived ACCESS token rather than a
 * refresh token or an API key, and the URL carries nothing else that
 * identifies the tenant.
 *
 * **Call this immediately after a successful `voiceApi.start`.** That
 * ordering is load-bearing: `api()` refreshes an expired access token on a
 * 401 and persists the new one, so a POST that has just succeeded
 * guarantees the token read here is live. A stale token produces an HTTP
 * 403 on the upgrade, which a browser surfaces as a bare `close` event
 * with no explanation attached — the one failure the console cannot narrate
 * from the socket alone.
 */
export function voiceStreamUrl(params: {
  session_id?: string | null;
  agent_id?: string | null;
  conversation_id?: string | null;
}): string {
  // API_BASE_URL empty = same origin (the dev server proxies /api to
  // :8000; `ws: true` in vite.config.ts is what makes it proxy the
  // upgrade as well as the requests).
  const base = API_BASE_URL || window.location.origin;
  const u = new URL(base, window.location.origin);
  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
  u.pathname = u.pathname.replace(/\/+$/, "") + "/api/v1/voice/stream";
  u.search = "";
  const token = getAccessToken();
  if (token) u.searchParams.set("token", token);
  if (params.session_id) u.searchParams.set("session_id", params.session_id);
  if (params.agent_id) u.searchParams.set("agent_id", params.agent_id);
  if (params.conversation_id)
    u.searchParams.set("conversation_id", params.conversation_id);
  return u.toString();
}

// ── Chat analytics (the Chat tab of the same page) ──────────────────
//
// `GET /api/v1/chat/analytics` is the SIBLING of `/voice/analytics`, not a
// second analytics idiom: the same `totals` / `buckets` / `by_*` /
// `truncated` / `scope_note` furniture, the same cap-rather-than-paginate
// bargain, and the same rule that a number which cannot be computed
// honestly arrives ABSENT WITH A REASON rather than as a zero. The types
// below are transcribed from `ChatAnalyticsOut` in
// `backend/app/api/chat_analytics.py` field for field, so a payload change
// there shows up here as a type error rather than as a blank panel.
//
// **NOTHING A CUSTOMER TYPED IS IN THIS PAYLOAD.** No message body, no
// transcript, no sender, and none of the parked state's `vars` — which on
// the ELFA graph holds a national identity number. That is a property of
// the columns the service selects, so there is no field below to be
// careful with; there is simply no field.

/**
 * The same distribution the workflow and voice pages carry, DELIBERATELY
 * the same TypeScript type: the server imports one `Distribution`
 * dataclass into all three, so a p50 means the same nearest-rank p50 over
 * the same thinned sample list on every one of them. A second interface
 * with the same members would be an invitation to let them drift.
 */
export type ChatDistribution = VoiceDistribution;

/** A question this page cannot answer, and what would close it. */
export interface ChatGap {
  question: string;
  why: string;
  needs: string;
}

export interface ChatTotals {
  conversations: number;
  channels: number;
  /** Of those, the ones with a chat workflow bound. The funnel's
   *  denominator — and the number that explains an empty funnel on a busy
   *  page: a channel with no graph has no questions to drop out of. */
  flow_channels: number;
  inbound_messages: number;
  voice_notes: number;
  /** Every message in the threads — the customer's and ours. */
  messages: number;
  /** Parked at a question, INSIDE that question's own timeout. */
  in_flight: number;
  /** Parked past it. See `funnel.abandoned_note`. */
  abandoned: number;
  left_the_script: number;
  cases_opened: number;
  cost_usd: number;
  agent_runs: number;
  first_conversation_at: string | null;
  last_conversation_at: string | null;
}

export interface ChatBucket {
  /** ISO date of the bucket's FIRST day (its left edge). */
  bucket: string;
  conversations: number;
  messages: number;
  voice_notes: number;
  cases_opened: number;
  cost_usd: number;
}

export interface ChatBuckets {
  /** day | week | month — from the span the DATA covers, not the window. */
  unit: string;
  buckets: ChatBucket[];
}

/** One `collect_reply` node — one QUESTION a customer was asked. */
export interface ChatStage {
  workflow_id: string;
  workflow_name: string;
  /** Position in the graph walk, 0-based. */
  index: number;
  /** The node id — "cnic", "fault", "confirm". What a reader opens on the
   *  canvas; the page shows the `title` and keeps this beside it. */
  node: string;
  title: string;
  /** The operator's own prompt copy, truncated. Authored text — no
   *  customer answer can reach it. */
  prompt: string;
  /** THIS NODE'S OWN reply_timeout_hours, because `abandoned` below is
   *  measured against it and means nothing without it. */
  timeout_hours: number;
  reached: number;
  waiting: number;
  abandoned: number;
  /** Parked here with an unreadable `parked_at`: neither answer can be
   *  given, and it is counted rather than assumed either way. */
  undatable: number;
  /** Got past this question and is no longer parked anywhere. */
  completed: number;
  abandoned_pct: number;
  pct_of_positioned: number;
}

export interface ChatFunnelWorkflow {
  workflow_id: string;
  workflow_name: string;
  version: number;
  stages: number;
}

/** One way a chat flow stopped, counted. */
export interface ChatFlowEnding {
  /** `end_chat` | `ran_out` | `handoff` | `error`, or null for a row
   *  written before the ending was recorded. */
  ending: string | null;
  label: string;
  note?: string;
  sessions?: number;
  conversations?: number;
  pct: number;
}

export interface ChatFunnel {
  workflows: ChatFunnelWorkflow[];
  stages: ChatStage[];
  /** Conversations that can be PLACED on a graph — the denominator of
   *  every `pct_of_positioned`, and a count of CONVERSATIONS rather than
   *  of placements. See `caveat`. */
  positioned: number;
  parked: number;
  completed: number;
  completed_endings: ChatFlowEnding[];
  waiting: number;
  abandoned: number;
  left_the_script: number;
  /** Parked at a node no loadable graph has: a conversation nobody can
   *  answer. */
  unknown_node: number;
  undatable: number;
  cases_opened: number;
  caveat: string;
  /** Rendered verbatim: abandonment is per-node, not a global threshold. */
  abandoned_note: string;
  missing: ChatGap[];
}

/** One correction, as a pair: what the case opened as, what it became. */
export interface ChatConfusion {
  from: string;
  to: string;
  cases: number;
  pct: number;
}

export interface ChatCategoryStat {
  category: string;
  cases: number;
  corrected: number;
  pct: number;
}

export interface ChatSourceStat {
  source: string;
  cases: number;
  corrected: number;
  pct: number;
}

export interface ChatClassifier {
  available: boolean;
  /** Why not, when not. Null on a working panel — and rendered verbatim,
   *  because "no case was opened from chat in this window" and "this
   *  tenant's form has no category field" are two different problems. */
  reason: string | null;
  cases: number;
  compared: number;
  unchanged: number;
  corrected: number;
  /** The AI recorded nothing and a human filled it in. NOT a correction,
   *  and kept out of `corrected_pct` for that reason. */
  filled_in: number;
  corrected_pct: number;
  /** `section.field` this was read from, resolved from the form SCHEMA. */
  field: string | null;
  /** True while `form_field_changes` retains field history. False would
   *  make the comparison an invention, and the panel has to say so. */
  history_retained: boolean;
  confusion: ChatConfusion[];
  most_corrected: ChatCategoryStat[];
  /** Which RUNG chose the category. Null — with `sources_missing` beside
   *  it — for a form with no `category_source` box, because a table of
   *  zeros there would read as "the model is never wrong". */
  sources: ChatSourceStat[] | null;
  sources_missing: ChatGap | null;
}

export interface ChatTranscriptionOutcome {
  outcome: string;
  label: string;
  note: string;
  messages: number;
  pct: number;
  /** Whether this outcome is in the success rate's denominator. A
   *  sensitive-node refusal and a sender-supplied transcript are
   *  deliberately outside it. */
  counts_toward_rate: boolean;
}

export interface ChatProviderStat {
  provider: string | null;
  model: string | null;
  messages: number;
  succeeded: number;
}

export interface ChatVoiceNotes {
  inbound_messages: number;
  voice: number;
  text: number;
  voice_pct: number;
  outcomes: ChatTranscriptionOutcome[];
  /** Messages somebody TRIED to transcribe — the honest denominator. */
  attempts: number;
  succeeded: number;
  success_pct: number;
  duration: ChatDistribution;
  /** Clips nothing could measure and no envelope described. Unknown, not
   *  zero: a page reporting 0 ms would be inventing a measurement. */
  duration_unknown: number;
  duration_measured: number;
  duration_from_envelope: number;
  duration_note: string;
  audio_bytes: number;
  providers: ChatProviderStat[];
  /** When the per-message record starts. A window beginning before this
   *  instant is only partly covered, and `coverage_note` says so. */
  recording_since: string | null;
  coverage_note: string;
}

/** Messages per conversation — deliberately NOT a `ChatDistribution`:
 *  every field on that type is named `*_ms`, and a `p50_ms` of 6 meaning
 *  "six messages" renders as six milliseconds to `fmtDuration`. */
export interface ChatPerConversation {
  conversations: number;
  messages: number;
  mean: number;
  p50: number;
  max: number;
  note: string;
}

export interface ChatCases {
  opened: number;
  with_case_file: number;
  by_status: { status: string; runs: number; pct: number }[];
  duration: ChatDistribution;
  /** Cases OPENED, and the note says why it is not a completion rate. */
  note: string;
}

export interface ChatSessionEnding {
  reason: string | null;
  label: string;
  sessions: number;
  pct: number;
}

export interface ChatHealth {
  conversations: number;
  in_flight: number;
  abandoned: number;
  left_the_script: number;
  finished_sessions: number;
  session_endings: ChatSessionEnding[];
  /**
   * `null` IS NOT `0` on these two, and the union is the whole reason the
   * page can tell the difference. A deployment whose `channel_sessions`
   * predates the `flow_ending` column cannot say how many conversations
   * were handed off; rendering that as "0 handed off" would report a
   * platform that never escalates.
   */
  handed_off: number | null;
  errored: number | null;
  ended_cleanly: number;
  ran_out: number;
  /** Idle session expiry force-finished a parked script
   *  (`flow_ending=timeout`). Not an engine ending. */
  timed_out: number;
  /** Finished before `flow_ending` existed. Never folded into
   *  `ended_cleanly`: back-filling would turn every historic handoff and
   *  every historic crash into a clean completion. */
  ending_unknown: number;
  flow_endings: ChatFlowEnding[];
  missing: ChatGap[];
}

export interface ChatChannelStat {
  channel_id: string;
  label: string;
  type: string;
  workflow_id: string | null;
  workflow_name: string | null;
  conversations: number;
  messages: number;
  voice_notes: number;
  waiting: number;
  abandoned: number;
  /** Parked, but neither: the node is in no loadable graph, or the park
   *  carries no readable timestamp. Its own column so that
   *  `waiting + abandoned + unplaced` is the parked total and none of the
   *  three is a guess. */
  unplaced: number;
  completed: number;
  cost_usd: number;
}

export interface ChatDepartmentStat {
  department_id: string | null;
  label: string;
  channels: number;
  conversations: number;
  messages: number;
  cost_usd: number;
}

export interface ChatWorkflowStat {
  workflow_id: string;
  label: string;
  version: number;
  channels: number;
  conversations: number;
  waiting: number;
  abandoned: number;
}

export interface ChatAnalytics {
  date_from: string | null;
  date_to: string | null;
  channel_id: string | null;
  /** The conversation set hit the service's cap; the numbers describe the
   *  most recent `max_conversations` in the window, not all of them. */
  truncated: boolean;
  max_conversations: number;
  /** Which rows this principal was shown, said out loud. */
  scope_note: string;
  generated_at: string | null;
  totals: ChatTotals;
  buckets: ChatBuckets;
  funnel: ChatFunnel;
  classifier: ChatClassifier;
  voice_notes: ChatVoiceNotes;
  messages_per_conversation: ChatPerConversation;
  cases: ChatCases;
  health: ChatHealth;
  by_channel: ChatChannelStat[];
  channels_hidden: number;
  by_department: ChatDepartmentStat[];
  departments_hidden: number;
  by_workflow: ChatWorkflowStat[];
  workflows_hidden: number;
}

export const chatAnalyticsApi = {
  /**
   * The aggregate behind the Chat tab of `/voice-analytics`.
   *
   * dept_admin and above at the endpoint — the SAME floor `voiceApi
   * .analytics` sits behind, under the same `voice_analytics` surface,
   * because a tab that 403s where its neighbour allows is a broken screen
   * rather than a policy. Which CONVERSATIONS it counts is the CHANNEL
   * ladder (the conditions `channelsApi.list` applies), decided per
   * request on the server.
   *
   * `channel_id` NARROWS the channel set; it cannot widen it, so the
   * broadest answer this can give is the answer without it.
   */
  analytics: (
    params: {
      channel_id?: string | null;
      date_from?: string | null;
      date_to?: string | null;
    } = {}
  ) => api<ChatAnalytics>("/api/v1/chat/analytics" + qs(params)),
};

export const agentToolsApi = {
  list: (agentId: string) =>
    api<AgentTool[]>(`/api/v1/agents/${encodeURIComponent(agentId)}/tools`),
  /** Super admin: decrypt and return the tool's stored auth secret. */
  revealSecret: (toolId: string) =>
    api<{ secret: string | null; hint?: string | null }>(
      `/api/v1/agents/tools/${encodeURIComponent(toolId)}/secret`
    ),
  create: (agentId: string, body: Partial<AgentTool>) =>
    api<AgentTool>(`/api/v1/agents/${encodeURIComponent(agentId)}/tools`, {
      method: "POST",
      body,
    }),
  /** `workflow` is a workflow tool's form (merged server-side: a seeded tool's
   *  lookup and follow-on steps are kept); `workflow_id` alone re-points it. */
  update: (toolId: string, body: Partial<AgentTool> & { workflow_id?: string; workflow?: Record<string, unknown> }) =>
    api<AgentTool>(`/api/v1/agents/tools/${encodeURIComponent(toolId)}`, {
      method: "PATCH",
      body,
    }),
  remove: (toolId: string) =>
    api(`/api/v1/agents/tools/${encodeURIComponent(toolId)}`, { method: "DELETE" }),
  /** Sub-agent per-intent allow-list of the PARENT's tools. Empty = all allowed. */
  setAllowedTools: (subAgentId: string, tool_ids: string[]) =>
    api<{ agent_id: string; tool_ids: string[] }>(
      `/api/v1/agents/${encodeURIComponent(subAgentId)}/allowed-tools`,
      { method: "PUT", body: { tool_ids } }
    ),
  runTest: (toolId: string, args: Record<string, unknown>) =>
    api<ToolRunTestResult>(`/api/v1/agents/tools/${encodeURIComponent(toolId)}/run-test`, {
      method: "POST",
      body: { args },
    }),
  importOpenapi: (
    agentId: string,
    body: {
      spec_url?: string;
      spec_text?: string;
      operation_ids?: string[];
      base_url_override?: string;
    }
  ) =>
    api<AgentTool[]>(`/api/v1/agents/${encodeURIComponent(agentId)}/tools/import-openapi`, {
      method: "POST",
      body,
    }),
  sandbox: (agentId: string, message: string, context?: Record<string, unknown>) =>
    api<SandboxResponse>(`/api/v1/agents/${encodeURIComponent(agentId)}/sandbox`, {
      method: "POST",
      body: context ? { message, context } : { message },
    }),
};

export const knowledgeApi = {
  listCollections: () => api<KnowledgeCollection[]>("/api/v1/knowledge/collections"),
  createCollection: (body: {
    name: string;
    description?: string;
    embedding_mode?: EmbeddingMode;
    language?: string;
  }) => api<KnowledgeCollection>("/api/v1/knowledge/collections", { method: "POST", body }),
  updateCollection: (id: string, body: { name?: string; description?: string; language?: string }) =>
    api<KnowledgeCollection>(`/api/v1/knowledge/collections/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),
  removeCollection: (id: string) =>
    api(`/api/v1/knowledge/collections/${encodeURIComponent(id)}`, { method: "DELETE" }),
  listDocuments: (collectionId: string) =>
    api<KnowledgeDocument[]>(
      `/api/v1/knowledge/collections/${encodeURIComponent(collectionId)}/documents`
    ),
  uploadDocument: (collectionId: string, file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return apiUpload<KnowledgeDocument>(
      `/api/v1/knowledge/collections/${encodeURIComponent(collectionId)}/documents`,
      form
    );
  },
  removeDocument: (docId: string) =>
    api(`/api/v1/knowledge/documents/${encodeURIComponent(docId)}`, { method: "DELETE" }),
  search: (collectionId: string, query: string, limit = 5) =>
    api<KnowledgeSearchResult[]>(
      `/api/v1/knowledge/collections/${encodeURIComponent(collectionId)}/search`,
      { method: "POST", body: { query, limit } }
    ),
  setAgentCollections: (agentId: string, collection_ids: string[]) =>
    api(`/api/v1/agents/${encodeURIComponent(agentId)}/knowledge`, {
      method: "PUT",
      body: { collection_ids },
    }),
};

// ── Public Agent API keys ───────────────────────────────────────────

export type ApiKeyStatus = "active" | "expired" | "revoked";

export interface ApiKey {
  id: string;
  name: string;
  /** Target: agent_id XOR workflow_id (W4). */
  agent_id?: string | null;
  agent_name?: string | null;
  workflow_id?: string | null;
  workflow_name?: string | null;
  department_id?: string | null;
  /** Set when the key is a PRINCIPAL rather than a public-API key: it
   *  authenticates the platform's own gated endpoints as this user. */
  acts_as_user_id?: string | null;
  acts_as_user_name?: string | null;
  acts_as_user_role?: string | null;
  /** Public key prefix, e.g. "xai_live_ab12" — never the full secret. */
  prefix?: string | null;
  expires_at?: string | null;
  is_active?: boolean;
  rate_limit_rpm?: number | null;
  monthly_cost_cap_usd?: number | null;
  last_used_at?: string | null;
  created_at?: string;
  spend_30d_usd?: number | null;
  status?: ApiKeyStatus;
}

/** POST response — `plaintext_key` is returned exactly once, at creation. */
export interface ApiKeyCreated extends ApiKey {
  plaintext_key?: string;
}

export interface ApiKeyCreateBody {
  /** Exactly one of agent_id / workflow_id / acts_as_user_id. */
  agent_id?: string;
  workflow_id?: string;
  /** A SERVICE key: it fronts no resource and instead carries this
   *  user's authority, so a workflow's api_call can reach the
   *  platform's own gated seams with a credential that can be revoked.
   *  Platform principals only — the server refuses it to anyone else. */
  acts_as_user_id?: string;
  name: string;
  department_id?: string | null;
  expires_at?: string | null; // ISO datetime
  rate_limit_rpm?: number | null;
  monthly_cost_cap_usd?: number | null;
}

export interface ApiKeyUpdateBody {
  name?: string;
  is_active?: boolean;
  expires_at?: string | null;
  rate_limit_rpm?: number | null;
  monthly_cost_cap_usd?: number | null;
}

export const apiKeysApi = {
  list: () => api<ApiKey[]>("/api/v1/api-keys"),
  create: (body: ApiKeyCreateBody) =>
    api<ApiKeyCreated>("/api/v1/api-keys", { method: "POST", body }),
  update: (id: string, body: ApiKeyUpdateBody) =>
    api<ApiKey>(`/api/v1/api-keys/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  remove: (id: string) => api(`/api/v1/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

// ── Usage explorer ──────────────────────────────────────────────────

/**
 * Mirrors `scoping.SCOPE_*`. `"organization"` is both an org_admin's own
 * rung AND what a platform admin's response downgrades to once they narrow
 * with `organization_id` — the chip must never say "Platform-wide" over one
 * tenant's numbers.
 */
export type UsageScope = "all" | "organization" | "department" | "self";

export interface UsagePoint {
  date: string; // YYYY-MM-DD
  cost_usd: number;
  requests: number;
}

/**
 * What kind of run a breakdown row is made of.
 *
 * `agent_runs` holds three sorts of row and they disagree about what
 * `latency_ms` and `tokens_used` mean — a chat turn's latency is how long
 * a user waited, a voice row's is the CALL DURATION, a workflow row's is
 * wall clock including however long a human sat on an approval gate. The
 * server now says which, per row, instead of averaging them together.
 */
export type RunKind = "chat" | "voice" | "workflow" | "mixed";

/** Fields every kind-aware breakdown row carries (backend `KindMixin`). */
export interface UsageKindFields {
  kind?: RunKind;
  /** Which kind `avg_latency_ms` describes — chat wherever chat exists. */
  latency_kind?: RunKind;
  /** How many rows are behind `avg_latency_ms` (≤ `requests`). */
  latency_requests?: number;
  /** Duration-priced rows: they carry cost but structurally zero tokens. */
  voice_requests?: number;
  /** Rows for which cost ÷ tokens is actually a price. */
  token_requests?: number;
  /**
   * TOTAL talk time behind the duration-priced rows, in seconds.
   * `avg_latency_ms` is a MEAN, and on a pure-voice row it is a mean call
   * duration — which reads exactly like a total. "1h 09m" across 29 calls
   * is 33 hours of audio, not 69 minutes, and at a per-minute tariff that
   * misreading is the difference between a $99.84 bill and a $5.52 one.
   */
  voice_seconds?: number;
  /** Rows whose cost is a fallback valuation, not a measurement. */
  fallback_requests?: number;
}

export interface UsageDimRow extends UsageKindFields {
  id: string;
  label: string;
  sublabel?: string | null;
  requests: number;
  total_cost_usd: number;
  avg_cost_usd: number;
  avg_latency_ms: number;
  total_tokens: number;
}

export interface UsageExplorerParams {
  date_from?: string;
  date_to?: string;
  department_id?: string;
  user_id?: string;
  /**
   * O6 — narrow to ONE tenant. A platform admin may name any organization;
   * anybody else may only name the one they are already acting as and gets
   * a 403 otherwise. Never a widening: the server ANDs it onto the
   * caller's own scope. Show the picker on `can_filter_organizations`
   * from the response, not on a locally-derived role.
   */
  organization_id?: string;
  provider_id?: string;
  agent_id?: string;
  model?: string;
}

export interface UsageExplorerResponse {
  date_from: string;
  date_to: string;
  scope: UsageScope;
  /** The organization the response was narrowed to, or null. */
  organization_id?: string | null;
  /** Server's verdict on whether this caller may use the org filter. */
  can_filter_organizations?: boolean;
  total_cost_usd: number;
  total_requests: number;
  avg_cost_per_request: number;
  /**
   * CHAT latency. Was `avg(latency_ms)` over every row, which on the live
   * data read three hours because four workflow rows carried up to 3.8
   * days of wall clock. The counters below say what it excludes so the
   * card can state its own denominator.
   */
  avg_latency_ms: number;
  latency_kind?: RunKind;
  latency_requests?: number;
  voice_requests?: number;
  voice_seconds?: number;
  workflow_requests?: number;
  /** Requests whose cost is a guess or a known absence, not a measurement. */
  unpriced_requests?: number;
  unpriced_cost_usd?: number;
  /**
   * Requests whose cost is a FALLBACK VALUATION — the meter the model is
   * sold by could not be read. Distinct from `unpriced_*`: a voice
   * session that lost its clock has a token count and a cost that is
   * still not the bill.
   */
  fallback_requests?: number;
  fallback_cost_usd?: number;
  total_tokens: number;
  usage_over_time: UsagePoint[];
  by_department: UsageDimRow[];
  by_user: UsageDimRow[];
  by_provider: UsageDimRow[];
  by_model: UsageDimRow[];
  by_agent: UsageDimRow[];
}

export const usageApi = {
  summary: (dateFrom: Date, dateTo: Date, organizationId?: string | null) =>
    api(
      "/api/v1/usage/summary" +
        qs({
          date_from: dateFrom.toISOString(),
          date_to: dateTo.toISOString(),
          organization_id: organizationId || undefined,
        })
    ),
  explorer: (params: UsageExplorerParams = {}) =>
    api<UsageExplorerResponse>("/api/v1/usage/explorer" + qs({ ...params })),
  /** Path (with query string) for the CSV export — fetch via apiBlob(). */
  exportCsvPath: (params: UsageExplorerParams = {}) =>
    "/api/v1/usage/export.csv" + qs({ ...params }),
};

/** ``GET /api/health`` — liveness + live build identity (no auth). */
export interface PlatformBuildInfo {
  build_id?: string;
  source?: string;
  version?: string;
  git_sha?: string | null;
  started_at?: string;
  newest_source_mtime?: string | null;
  error?: string;
}

export interface PlatformHealth {
  status?: string;
  version?: string;
  build?: PlatformBuildInfo;
  schema?: Record<string, unknown>;
  agents_loaded?: number;
}

/*
 * chatApi keeps the method names the pre-existing Chat/Dashboard views
 * were written against, so those views only had to change their import.
 */
export const chatApi = {
  health: () => api<PlatformHealth>("/api/health", { noAuth: true }),
  listAgents: () => api<AgentNode[]>("/api/v1/agents"),
  send: (body: Record<string, unknown>) => api("/api/v1/chat", { method: "POST", body }),
  listConversations: (params: { limit?: number; offset?: number; date_from?: string; date_to?: string } = {}) =>
    api("/api/v1/conversations" + qs(params)),
  getConversation: (id: string) => api("/api/v1/conversations/" + encodeURIComponent(id)),
  usageSummary: (dateFrom: Date, dateTo: Date, organizationId?: string | null) =>
    usageApi.summary(dateFrom, dateTo, organizationId),
  feedback: (body: Record<string, unknown>) => api("/api/feedback/", { method: "POST", body }),
};

// ── In-app notifications (derived alerts, stateless backend) ────────

export type NotificationSeverity = "info" | "warn" | "error";

export interface AppNotification {
  /** Stable deterministic id — read-state is tracked client-side against it. */
  id: string;
  severity: NotificationSeverity;
  title: string;
  detail: string;
  /** Frontend route to navigate to when clicked, e.g. "/usage". */
  link: string;
  /** ISO timestamp of when the alert was derived (server "now"). */
  created_hint?: string;
}

export const notificationsApi = {
  list: () => api<AppNotification[]>("/api/v1/notifications"),
};

// ── Pipeline traces (waterfall observability) ───────────────────────

export type SpanKind = "router" | "intent" | "kb" | "llm" | "tool";
export type SpanStatus = "ok" | "error";

export interface Span {
  id?: string;
  seq: number;
  /** v2 tracing: seq of the parent span (root "run" span has null). Absent in v1 data. */
  parent_seq?: number | null;
  /** Known kinds get colored badges; unknown values fall back to a muted chip. */
  kind: SpanKind | string;
  name: string;
  /** Null offset = approximate placement (rendered right-aligned + hatched). */
  offset_ms?: number | null;
  duration_ms?: number | null;
  status?: SpanStatus;
  tokens?: number | null;
  cost_usd?: number | null;
  meta?: Record<string, unknown>;
}

export interface TraceRun {
  id: string;
  agent_id?: string | null;
  agent_name?: string | null;
  model_used?: string | null;
  cost_usd?: number | null;
  tokens_used?: number | null;
  latency_ms?: number | null;
  created_at?: string | null;
  conversation_id?: string | null;
}

/** A voice call behind a run, with its transcript (GET /traces/{id}). */
export interface TraceVoiceCall {
  session_id: string;
  status?: string | null;
  end_reason?: string | null;
  duration_ms?: number | null;
  cost_usd?: number | null;
  started_at?: string | null;
  transcript: { role: string; text: string; at_ms: number; script_converted?: string }[];
}

export interface TraceResponse {
  run: TraceRun;
  spans: Span[];
  /** Present only for a voice call's run. */
  voice?: TraceVoiceCall | null;
}

/** Where a run originated: internal chat console vs. the Public Agent API. */
export type TraceSource = "operations" | "public_api";

/** One row in the trace explorer list (GET /api/v1/traces). */
export interface TraceRunListItem {
  run_id: string;
  created_at?: string | null;
  agent_id?: string | null;
  agent_name?: string | null;
  user_id?: string | null;
  user_label?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  conversation_id?: string | null;
  conversation_title?: string | null;
  /** Set on workflow-engine rows: the workflow this run executed. */
  workflow_name?: string | null;
  /** Defensive: backend may add more sources later. */
  source?: TraceSource | string | null;
  model_used?: string | null;
  cost_usd?: number | null;
  tokens_used?: number | null;
  latency_ms?: number | null;
  span_count?: number | null;
  has_error?: boolean;
}

export interface TraceListParams {
  date_from?: string;
  date_to?: string;
  agent_id?: string;
  user_id?: string;
  department_id?: string;
  conversation_id?: string;
  source?: string;
  /** "workflow" = workflow-engine runs; "agent" = chat/agent turns. */
  run_type?: "agent" | "workflow";
  errors_only?: boolean;
  /** Exclude runs without a conversation (workflow/sandbox pseudo-rows). */
  conversations_only?: boolean;
  /**
   * Substring match on the conversation title, the workflow's name and the
   * run's submitted case data (form-instance values + input envelope).
   * Span payloads are deliberately not searched — see the server docstring.
   */
  q?: string;
  /** group=conversation only: which aggregate to order on. */
  sort_by?: TraceSortKey;
  sort_dir?: "asc" | "desc";
  limit?: number;
  offset?: number;
}

/** Server-side sort keys for the grouped trace list (allow-listed there). */
export type TraceSortKey = "last_activity" | "duration" | "cost" | "turns";

export interface TraceListResponse {
  total: number;
  runs: TraceRunListItem[];
}

/** One turn summary from GET /api/v1/conversations/{id}/traces (oldest first). */
export interface ConversationTurnSummary {
  agent_run_id: string;
  created_at?: string | null;
  cost_usd?: number | null;
  latency_ms?: number | null;
  span_count?: number | null;
  has_error?: boolean;
}

/** One row in the grouped trace explorer (GET /api/v1/traces?group=conversation). */
export interface TraceConversationItem {
  /** Null for pseudo-conversations (runs recorded without a conversation). */
  conversation_id: string | null;
  /**
   * A conversation's title, or — for a workflow run, which has no title of
   * its own — a case-identifying label derived server-side from the run's
   * form instance / input / output. Falls back to "Workflow: <name>".
   */
  conversation_title?: string | null;
  /** Set on workflow-engine rows: the workflow this run executed. */
  workflow_name?: string | null;
  /** Set only on pseudo-rows: the single run behind this row. */
  run_id?: string | null;
  source?: TraceSource | string | null;
  agent_ids?: string[];
  agent_names?: string[];
  /** Attribution of the conversation's latest run. */
  user_label?: string | null;
  department_name?: string | null;
  turns?: number;
  total_cost_usd?: number | null;
  total_tokens?: number | null;
  avg_latency_ms?: number | null;
  first_created_at?: string | null;
  last_created_at?: string | null;
  has_error?: boolean;
}

export interface TraceConversationListResponse {
  total: number;
  conversations: TraceConversationItem[];
}

export const tracesApi = {
  get: (runId: string) => api<TraceResponse>(`/api/v1/traces/${encodeURIComponent(runId)}`),
  /** Turn summaries for one conversation, oldest first (RBAC-scoped server-side). */
  byConversation: (convId: string) =>
    api<ConversationTurnSummary[]>(
      `/api/v1/conversations/${encodeURIComponent(convId)}/traces`
    ),
  /** Paginated run list, newest first. RBAC is applied server-side. */
  list: (params: TraceListParams = {}) =>
    api<TraceListResponse>(
      "/api/v1/traces" +
        qs({
          date_from: params.date_from,
          date_to: params.date_to,
          agent_id: params.agent_id,
          user_id: params.user_id,
          department_id: params.department_id,
          conversation_id: params.conversation_id,
          source: params.source,
          run_type: params.run_type,
          errors_only: params.errors_only ? "true" : undefined,
          q: params.q,
          limit: params.limit,
          offset: params.offset,
        })
    ),
  /** Paginated conversation list (one aggregated row per conversation),
   *  last activity first. Same filters + RBAC as list(). */
  listConversations: (params: TraceListParams = {}) =>
    api<TraceConversationListResponse>(
      "/api/v1/traces" +
        qs({
          date_from: params.date_from,
          date_to: params.date_to,
          agent_id: params.agent_id,
          user_id: params.user_id,
          department_id: params.department_id,
          source: params.source,
          run_type: params.run_type,
          errors_only: params.errors_only ? "true" : undefined,
          conversations_only: params.conversations_only ? "true" : undefined,
          q: params.q,
          group: "conversation",
          sort_by: params.sort_by,
          sort_dir: params.sort_dir,
          limit: params.limit,
          offset: params.offset,
        })
    ),
  /** RBAC-scoped aggregate over runs in a window (member dashboard KPIs). */
  stats: (dateFrom?: string, dateTo?: string) =>
    api<TraceStats>(
      "/api/v1/traces/stats" + qs({ date_from: dateFrom, date_to: dateTo })
    ),
};

/** GET /api/v1/traces/stats — window aggregate, RBAC-scoped like list(). */
export interface TraceStats {
  date_from?: string | null;
  date_to?: string | null;
  runs: number;
  /** Runs recorded by the workflow executor (model_used = "workflow"). */
  workflow_runs: number;
  chat_runs: number;
  conversations: number;
  cost_usd: number;
  tokens: number;
  avg_latency_ms?: number | null;
  error_runs: number;
}

// ── Agent Scheduler ─────────────────────────────────────────────────

export type ScheduleType = "cron" | "once";
export type SchedulerRunStatus = "success" | "error";

export interface SchedulerRun {
  id: string;
  status?: string | null; // "success" | "error" (defensive: backend may add more)
  reply?: string | null;
  error?: string | null;
  model?: string | null;
  cost_usd?: number | null;
  latency_ms?: number | null;
  started_at?: string | null;
  finished_at?: string | null;
}

export interface SchedulerJob {
  id: string;
  name: string;
  /** Target: agent_id XOR workflow_id (W4). */
  agent_id?: string | null;
  agent_name?: string | null;
  workflow_id?: string | null;
  workflow_name?: string | null;
  /** Structured input for workflow-target jobs. */
  workflow_input?: Record<string, unknown> | null;
  message?: string | null;
  context?: Record<string, unknown> | null;
  schedule_type: ScheduleType;
  cron_expr?: string | null;
  run_at?: string | null;
  enabled: boolean;
  department_id?: string | null;
  next_run_at?: string | null;
  last_run_at?: string | null;
  last_status?: SchedulerRunStatus | null;
  run_count?: number;
  last_run?: { status?: string | null; finished_at?: string | null; cost_usd?: number | null } | null;
  created_at?: string;
}

export interface SchedulerJobCreateBody {
  name: string;
  /** Exactly one of agent_id / workflow_id. */
  agent_id?: string | null;
  workflow_id?: string | null;
  workflow_input?: Record<string, unknown> | null;
  message?: string | null;
  context?: Record<string, unknown>;
  schedule_type: ScheduleType;
  cron_expr?: string | null;
  run_at?: string | null;
  department_id?: string | null;
  enabled?: boolean;
}

export type SchedulerJobUpdateBody = Partial<SchedulerJobCreateBody>;

export const schedulerApi = {
  listJobs: () => api<SchedulerJob[]>("/api/v1/scheduler/jobs"),
  createJob: (body: SchedulerJobCreateBody) =>
    api<SchedulerJob>("/api/v1/scheduler/jobs", { method: "POST", body }),
  updateJob: (id: string, body: SchedulerJobUpdateBody) =>
    api<SchedulerJob>(`/api/v1/scheduler/jobs/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  removeJob: (id: string) =>
    api(`/api/v1/scheduler/jobs/${encodeURIComponent(id)}`, { method: "DELETE" }),
  runNow: (id: string) =>
    api<SchedulerRun>(`/api/v1/scheduler/jobs/${encodeURIComponent(id)}/run-now`, { method: "POST" }),
  listRuns: (id: string, limit = 20) =>
    api<SchedulerRun[]>(`/api/v1/scheduler/jobs/${encodeURIComponent(id)}/runs` + qs({ limit })),
};

// ── Workflows (visual multi-step orchestration) ─────────────────────

export type WorkflowStepType =
  | "agent"
  | "tool"
  | "condition"
  | "transform"
  | "output"
  | "parallel"
  | "join"
  | "loop"
  | "approval"
  | "form_edit"
  | "subworkflow"
  | "set_vars"
  | "api_call"
  | "sql_query"
  // SMTP, which `api_call` cannot speak. A step type rather than a REST
  // tool because there is no HTTP endpoint to point one at, and because
  // a graph is an immutable snapshot that gets exported and diffed —
  // so the credentials live in the server settings and this node has no
  // field to hold one.
  | "notify_email"
  // V4 (specs/006 §3d.7) — the CALL-ONLY verbs. They are members of this
  // union because they are step types, but `allowed_step_types_for` on
  // the server refuses them in any workflow that does not declare a
  // `voice_call` source, and the palette hides them for the same reason.
  | "say"
  | "collect_digits"
  | "handoff"
  | "end_call"
  // THE CHAT-ONLY VERBS, absent from this union until somebody had to
  // FIX a chat graph rather than seed one. They were never authorable
  // here: no member of this type, so no palette entry, no node
  // renderer and no config editor — which is why a `collect_reply` that
  // Ask AI produced drew as UNKNOWN STEP with no way to set the
  // `collected` branch the validator was asking for.
  | "collect_reply"
  | "collect_speech"
  | "end_chat"
  // V5 (specs/006 §3e.3) — the SEAM. A call graph ends by starting the
  // run that opens the case; it never becomes one.
  | "start_workflow"
  // A caller given to a PERSON through the PBX Portal (not to the model,
  // which is what `handoff` means). Call-only.
  | "transfer_to_person";

/** V4 — the call-only step types, in one place, so the palette filter and
 *  the "is this authorable here" test cannot drift apart. */
/** The chat-only verbs — the mirror of CALL_ONLY_STEP_TYPES. Hidden
 *  from the executor's palette for the same reason: offering a step the
 *  server refuses is a 422 the developer earns by doing what the UI
 *  invited. (`say`, `handoff` and `start_workflow` are shared with the
 *  call engine and live in that list.) */
export const CHAT_ONLY_STEP_TYPES: readonly WorkflowStepType[] = [
  "collect_reply",
  "end_chat",
] as const;

export const CALL_ONLY_STEP_TYPES: readonly WorkflowStepType[] = [
  "say",
  "collect_speech",
  "collect_digits",
  "handoff",
  "end_call",
  "start_workflow",
  "transfer_to_person",
] as const;

/** What a CHAT graph may contain — the chat engine's vocabulary, mirroring
 *  `allowed_step_types_for` on the server.
 *
 *  TWO OF THEM ARE NOT AUTHORABLE HERE YET. `collect_reply` and `end_chat`
 *  have no entry in `STEP_KINDS`, no node renderer and no config editor,
 *  because chat graphs have only ever been produced by seed scripts and by
 *  Ask AI. They are listed anyway, and deliberately: this constant is what
 *  the palette filters BY, so as soon as either gains an editor it appears
 *  without anybody having to remember this file. */
export const CHAT_STEP_TYPES: readonly string[] = [
  "say",
  "collect_reply",
  "condition",
  "end_chat",
  "handoff",
  "set_vars",
  "start_workflow",
  "tool",
  /** The chat engine runs these too — with a timeout the executor's
   *  versions do not have, because a customer is watching a typing
   *  indicator while they run. `tool` is still the better choice for
   *  most REST work (endpoint and credentials in a Tool + Connection
   *  rather than in a versioned graph), but that was never a reason to
   *  refuse these. */
  "api_call",
  "sql_query",
] as const;

/** V4 — what a CALL graph may never contain (§3e.4, decided not deferred):
 *  a call cannot wait for a human, cannot fan out, and cannot nest another
 *  workflow. Refused by the server at publish time; hidden here so the
 *  canvas says it first. */
export const CALL_FORBIDDEN_STEP_TYPES: readonly WorkflowStepType[] = [
  "approval",
  "form_edit",
  "parallel",
  "join",
  "loop",
  "subworkflow",
] as const;

export type WorkflowConditionOp =
  | "contains"
  | "equals"
  | "not_equals"
  | "gt"
  | "lt"
  | "is_empty"
  | "not_empty"
  | "regex";

export interface WorkflowInputField {
  name: string;
  type?: string; // "string" | "number" | "boolean" (defensive: free-form)
  required?: boolean;
  description?: string;
}

export interface WorkflowInputSchema {
  fields: WorkflowInputField[];
}

/**
 * Step config is type-dependent:
 *  - agent     {agent_id, message}
 *  - tool      {tool_id, args: {name: template}}
 *  - condition {left, op, right}
 *  - transform {template}
 *  - output    {template}
 *  - parallel  {} (fan-out comes from the edges)
 *  - join      {mode: "all" | "any"}
 *  - loop      {mode: "for_each" | "until", items?, condition?: {left, op, right}, max_iterations?}
 *  - approval  {assignee_type: "user"|"role"|"department_role"|"requester_dept_admin"
 *                              |"organization_department_role"|"department_role_id",
 *               user_id?, role?, department_id?, organization_id?,
 *               role_id? (R1 — a NAMED ROLE such as "CFO", by id, so a
 *               rename cannot silently unhook the gate),
 *               instructions?, timeout_hours?, on_timeout?}
 *  - form_edit {assignee_type (same options as approval), user_id?, role?,
 *               department_id?, sections: [section_key,...], instructions?,
 *               timeout_hours?, on_timeout: "wait"} — F3: pauses the run
 *               until the assignee completes those form sections.
 *  - subworkflow {workflow_id, input: {childField: template},
 *               inherit_vars?: bool} — runs a published child workflow
 *               (no approval/form_edit inside; max depth 3).
 *  - set_vars  {assignments: [{key, value, parse_json?}]} — writes {{vars.<key>}}
 *  - api_call  {method, url, headers?, query?, body_template?, timeout_s?,
 *               auth_tool_id?, assign?: [{var, path}], fail_on_error?}
 *  - sql_query {connection_id, query (SELECT/WITH only), max_rows?,
 *               assign?: [{var, mode: "scalar"|"rows"|"count", column?}]}
 *
 * V4 — CALL-ONLY (a voice_call-sourced workflow, interpreted by the voice
 * worker; the server refuses these anywhere else):
 *  - say       {audio_key?, text?, interruptible?} — plays an operator's
 *              24 kHz/16-bit/mono WAV through the same loader as the
 *              refusal announcement. `interruptible: false` is what a legal
 *              announcement needs.
 *  - collect_digits {assign, max_digits?, min_digits?, terminator?,
 *              submit_on: "max"|"terminator"|"either", sensitive?,
 *              on_star: "clear"|"backspace"|"ignore", max_retries?,
 *              first_digit_ms?, inter_digit_ms?, attempt_ms?,
 *              prompt?/reprompt?/reprompt_no_input?/reprompt_invalid?:
 *              {audio_key?, text?, interruptible?}} — three outgoing edges,
 *              "collected" (required), "no_input" and "invalid". Retries
 *              are NOT edges. On the digit prompt, `interruptible` governs
 *              keypress dial-ahead only; speech never cuts it.
 *  - collect_speech {assign, silence_ms?, max_ms?, min_chars?, max_retries?,
 *              sensitive?, prompt?/reprompt_no_input?/reprompt_short?:
 *              {audio_key?, text?, interruptible?}} — same three edges;
 *              `prompt.interruptible` defaults false so noise cannot cut
 *              the question (words spoken over it are still captured).
 *  - handoff   {note?} — stop scripting, give the conversation to the
 *              model with what was learned. One-way; nothing runs after it.
 *  - end_call  {reason?} — hang up. Nothing runs after it.
 * The builder additionally persists canvas positions as config._pos {x,y}
 * (a harmless extra key the backend ignores).
 */
export interface WorkflowStep {
  id: string;
  name: string;
  type: WorkflowStepType;
  config: Record<string, any>;
}

/** Edge branch labels: condition → "true"/"false"; loop → "body"/"next";
 *  approval → "approved"/"rejected"; V4 collect_digits →
 *  "collected"/"no_input"/"invalid". */
export type WorkflowEdgeBranch =
  | "true"
  | "false"
  | "body"
  | "next"
  | "approved"
  | "rejected"
  | "collected"
  | "no_input"
  | "invalid"
  // transfer_to_person's outcomes ("accepted" has no edge).
  | "not_accepted"
  | "timeout"
  | "failed";

export interface WorkflowGraphEdge {
  source: string;
  target: string;
  /** Present only on edges leaving a condition or loop step. */
  branch?: WorkflowEdgeBranch | null;
}

/** V2 (specs/006 §3b) — where runs of this workflow come from. */
export type WorkflowSourceKind =
  | "voice_call"
  /** A WhatsApp conversation, run by the resumable chat engine. Declared,
   *  like `voice_call` — the backend has had it in
   *  DECLARABLE_SOURCE_KINDS since V2, but it was missing from this union
   *  and from the Sources drawer, so the only way to make a chat flow was
   *  a seed script. */
  | "chat"
  | "schedule"
  | "public_api"
  | "form"
  | "manual"
  | "subflow";

/**
 * One DECLARED source, as it appears in the graph JSON.
 *
 * NOTE WHAT IS NOT HERE: no `channel_id`, no cron, no key id. Fork D =
 * D2 — the graph declares the KIND, the binding lives on the channel /
 * the scheduled job / the API key, and the node card resolves it at
 * render time via `workflowsApi.sources`. Putting a binding in the
 * versioned graph would make publishing a draft a routing change and a
 * version rollback a DID rollback.
 *
 * Only `voice_call` is ever declared today; the other five are derived.
 */
export interface WorkflowGraphSource {
  kind: WorkflowSourceKind;
  label?: string | null;
}

/** One resolved binding behind a source (a channel, a job, a key, …). */
export interface WorkflowSourceBinding {
  id?: string | null;
  label?: string | null;
  detail?: string | null;
  enabled?: boolean;
  warning?: string | null;
}

/** `GET /workflows/{id}/sources` — the union of declared and derived. */
export interface WorkflowSourceResolved {
  kind: WorkflowSourceKind | string;
  /** true = written in graph.sources; false = derived from a binding. */
  declared?: boolean;
  label?: string | null;
  /** "worker" for a voice_call source, "executor" for everything else. */
  engine?: string;
  editable?: boolean;
  bindings?: WorkflowSourceBinding[];
  note?: string | null;
}

export interface WorkflowGraph {
  input_schema: WorkflowInputSchema;
  /** V2 — a SIBLING of input_schema, not a step type. Absent on every
   *  graph written before V2, which is the implied "manual" source; it is
   *  deliberately never backfilled. */
  sources?: WorkflowGraphSource[];
  steps: WorkflowStep[];
  edges: WorkflowGraphEdge[];
}

export interface WorkflowValidationIssue {
  node_id?: string | null;
  code: string;
  message: string;
}

export interface WorkflowStepRun {
  step_id: string;
  step_name?: string | null;
  step_type?: string | null;
  status?: string | null; // "success" | "error" | "skipped" … (defensive)
  /** The step's RESOLVED inputs after template interpolation — for a
   *  `tool` or `api_call` step, the request that actually went on the
   *  wire. The executor has always written this; it was simply never
   *  rendered, which hid the commonest tool bug of all ("the template
   *  resolved to an empty string and we POSTed it"). */
  input?: any;
  output?: any;
  error?: string | null;
  seq: number;
}

export interface WorkflowRun {
  id: string;
  /** "success" | "error" | "running" | "cancelled" | "waiting_approval" | "rejected" … (defensive) */
  status?: string | null;
  input?: Record<string, any> | null;
  output?: any;
  error?: string | null;
  cost_usd?: number | null;
  latency_ms?: number | null;
  created_at?: string | null;
  /** W3: AgentRun id carrying this run's trace spans (Tracing page / tracesApi.get). */
  trace_run_id?: string | null;
  /** F2: form instance the run was started from (form-bound workflows). */
  form_instance_id?: string | null;
  /** Subworkflow child runs carry the parent run's id (top-level = null). */
  parent_run_id?: string | null;
  /**
   * How the run started. "manual" | "schedule" | "public_api" | "subflow" |
   * "form" | "voice" | "chat" | "voice_call" | "chat_session".
   *
   * R1 made the last two possible, and they are why this is rendered: a
   * channel-bound graph's runs now sit in the same list as the ones
   * somebody started with the Run button, and without the trigger there
   * is nothing on a row to tell a 3am telephone call from a manual test.
   * Note "voice"/"chat" mean the case a call/chat OPENED, while
   * "voice_call"/"chat_session" mean the channel-bound graph itself ran.
   */
  trigger?: string | null;
  step_runs?: WorkflowStepRun[];
}

/** One SSE event from POST /workflows/{id}/run/stream. */
export interface WorkflowStreamEvent {
  type: "run_started" | "step_started" | "step_finished" | "run_finished" | "run_error" | string;
  run_id?: string;
  step_id?: string;
  name?: string;
  step_type?: string;
  status?: string;
  content?: string;
  error?: string | null;
  /** Present on run_finished: same payload as POST /run. */
  run?: WorkflowRun;
}

/** The variable state at one paused step. Streamed, never stored. */
export interface WorkflowDebugState {
  /** A sensitive value arrives as the string "[redacted:13]" — a length. */
  vars: Record<string, unknown>;
  /** Finished step name → its clipped output. */
  steps: Record<string, string>;
  steps_done: string[];
  previous: string;
  input: Record<string, unknown>;
  /** `section.field` → value, the way a step template addresses it. */
  form: Record<string, unknown>;
  has_form: boolean;
}

/** One step the debugger has stopped on. */
export interface WorkflowDebugPause {
  step_id: string;
  name: string;
  step_type: string;
  seq: number;
  offset_ms: number;
  /** The cursor cannot follow into a child run — debug it as its own session. */
  is_subworkflow: boolean;
  /** An approval / form_edit gate: continuing ENDS the run at a human task. */
  is_gate: boolean;
  state: WorkflowDebugState;
  /** Filled in by the page: which step_run this pause belongs to. */
  step_run_id?: string;
}

/** What one Ask AI edit produced. Every field is a claim the UI must show. */
/** One build instruction, as proposed and possibly reworded. */
export interface WorkflowAiPlanStep {
  title: string;
  instruction: string;
}

/** What `aiPlan` returns: the steps to review, and nothing built. */
export interface WorkflowAiPlan {
  steps: WorkflowAiPlanStep[];
  notes: string[];
  model: string;
  duration_ms: number;
}

export interface WorkflowAiEditResult {
  /** The inactive version row the proposal was parked as. */
  version: WorkflowVersion;
  graph: WorkflowGraph;
  diff: {
    steps_added: { id: string; name?: string; type?: string }[];
    steps_removed: { id: string; name?: string; type?: string }[];
    steps_changed: { id: string; name?: string; type?: string; type_changed?: boolean }[];
    edges_added: { source: string; target: string; branch?: string | null }[];
    edges_removed: { source: string; target: string; branch?: string | null }[];
    step_count_before: number;
    step_count_after: number;
    unchanged: boolean;
  };
  id_churn: { kept: string[]; removed: string[]; added: string[] };
  issues: WorkflowValidationIssue[];
  warnings: WorkflowValidationIssue[];
  valid: boolean;
  /** The model's account of its own work — shown BESIDE the diff, never instead. */
  summary: string;
  notes: string[];
  /** Gaps the attached requirements did not answer. The graph is ALREADY
   *  built — each entry names the decision that is in it, so a reviewer
   *  can see what was chosen rather than reading the JSON to find out.
   *  Empty when nothing was attached, or when the document said enough. */
  questions?: {
    question: string;
    assumed: string;
    why_it_matters?: string;
  }[];
  /** True when the flow was too large for one reply and was planned and
   *  built part by part. Worth surfacing rather than hiding: a staged
   *  graph did NOT go through the validator repair loop, so its issues
   *  are what the first assembly produced. */
  staged?: boolean;
  /** The plan a staged build followed, in the order it was built. */
  parts?: { title: string; covers?: string }[];
  /** True when the model tried to change `sources` and was overruled. */
  sources_restored: boolean;
  stalled: boolean;
  iterations: number;
  model: string;
  duration_ms: number;
}

export interface WorkflowVersion {
  id: string;
  version?: number;
  note?: string | null;
  is_active?: boolean;
  created_at?: string | null;
  /** `{}` on the list unless `includeGraph` was passed — "not asked
   *  for", never "empty graph". */
  graph?: WorkflowGraph | null;
  /** Server-side count, always present on the list, so a panel never
   *  needs the graph to show how big a version is. */
  step_count?: number;
  created_by?: string | null;
  /** Resolved by GET /workflows/{id}/versions only; null elsewhere. */
  created_by_name?: string | null;
}

export interface Workflow extends ResourceOrigin {
  id: string;
  slug?: string;
  name: string;
  description?: string | null;
  is_published?: boolean;
  department_ids?: string[];
  /** Defensive: may be a full version object (with graph), a bare number, or null. */
  active_version?: WorkflowVersion | number | null;
  last_run?: WorkflowRun | null;
  version_count?: number;
  created_at?: string | null;
  /** F2: form bound to this workflow — runs start from a form instance. */
  form_id?: string | null;
  form_name?: string | null;
}

export const workflowsApi = {
  list: () => api<Workflow[] | { workflows: Workflow[] }>("/api/v1/workflows"),
  create: (body: { name: string; description?: string | null }) =>
    api<Workflow>("/api/v1/workflows", { method: "POST", body }),
  get: (id: string) => api<Workflow>(`/api/v1/workflows/${encodeURIComponent(id)}`),
  update: (id: string, body: Partial<Workflow>) =>
    api<Workflow>(`/api/v1/workflows/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  remove: (id: string) => api(`/api/v1/workflows/${encodeURIComponent(id)}`, { method: "DELETE" }),
  setDepartments: (id: string, department_ids: string[]) =>
    api(`/api/v1/workflows/${encodeURIComponent(id)}/departments`, {
      method: "PUT",
      body: { department_ids },
    }),
  createVersion: (id: string, body: { graph: WorkflowGraph; note?: string; activate: boolean }) =>
    api<WorkflowVersion>(`/api/v1/workflows/${encodeURIComponent(id)}/versions`, {
      method: "POST",
      body,
    }),
  activateVersion: (versionId: string) =>
    api(`/api/v1/workflows/versions/${encodeURIComponent(versionId)}/activate`, { method: "POST" }),
  /**
   * Version history. **Graph-free and bounded by default** (19 Oct 2026).
   *
   * The route used to return every version WITH its full graph: on
   * ELFA's 78-step call flow that is 45.7 KB each, so forty versions
   * was 1.7 MB in one response — to render a step count beside a date.
   * `step_count` is now always populated, so the history panel needs
   * no graph at all.
   *
   * Pass `includeGraph` only when you actually read `.graph`. The
   * ACTIVE version is always in the response whatever `limit` says, so
   * a caller that wants one graph can ask for `limit: 1` and still get
   * the published one.
   */
  listVersions: (
    id: string,
    opts: { limit?: number; includeGraph?: boolean } = {}
  ) =>
    api<WorkflowVersion[]>(
      `/api/v1/workflows/${encodeURIComponent(id)}/versions` +
        qs({ limit: opts.limit, include_graph: opts.includeGraph })
    ),
  /**
   * O4b — organizations this workflow may route an approval gate into,
   * with their departments. The answer IS the permission: every entry is
   * an org this workflow is already shared with, so the builder's picker
   * cannot offer something the validator would refuse. Empty = share the
   * workflow with another organization first. 403 at `use` access.
   */
  routingTargets: (id: string) =>
    api<WorkflowRoutingTarget[]>(
      `/api/v1/workflows/${encodeURIComponent(id)}/routing-targets`
    ),
  /** The names an assignee picker needs, on the builder's own surface.
   *  The management endpoints (/departments, /users, /department-roles)
   *  sit on ORG_MANAGEMENT, which excludes platform_developer on
   *  purpose — so the builder used to take three 403s and render empty
   *  pickers to somebody who was simply never allowed to ask. */
  directory: () => api<BuilderDirectory>("/api/v1/workflows/directory"),
  /**
   * Ask AI, pointed at the graph currently on the canvas.
   *
   * Sends what the developer is LOOKING AT, not what is stored — unsaved
   * canvas edits are part of what they mean by "this workflow".
   *
   * The server saves the proposal as an INACTIVE version and returns it.
   * Nothing this call does can reach a customer; making it live is a
   * separate, human click in the version history.
   */
  aiEdit: (id: string, body: { instruction: string; graph: WorkflowGraph }) =>
    api<WorkflowAiEditResult>(
      `/api/v1/workflows/${encodeURIComponent(id)}/ai-edit`,
      { method: "POST", body }
    ),
  /** Ask AI with a requirements document attached.
   *
   *  MULTIPART, NOT JSON, and that is the reason for a second route: a
   *  3 MB diagram base64'd into a JSON body is a 4 MB string the server
   *  must parse before it can reject it. The graph rides along as a
   *  JSON string in a form field. */
  aiEditWithFile: (
    id: string,
    body: { instruction: string; graph: WorkflowGraph },
    file: File,
  ) => {
    const form = new FormData();
    form.append("instruction", body.instruction);
    form.append("graph", JSON.stringify(body.graph));
    form.append("file", file);
    return apiUpload<WorkflowAiEditResult>(
      `/api/v1/workflows/${encodeURIComponent(id)}/ai-edit/attach`,
      form,
    );
  },
  /** Ask AI, reporting each phase as it happens.
   *
   *  Both routes stream, including the multipart one — the call with a
   *  requirements document is the one that plans and then makes a model
   *  call per part, so it is the one that can run for minutes, and
   *  leaving it un-streamed would have meant the only silent call was
   *  the only slow one.
   *
   *  Resolves with the finished result so the caller reads exactly like
   *  the blocking version; `onPhase` is the only addition. */
  aiEditStream: (
    id: string,
    body: { instruction: string; graph: WorkflowGraph },
    onPhase: (message: string, event: any) => void,
    file?: File | null,
  ): Promise<WorkflowAiEditResult> => {
    let payload: unknown;
    let path: string;
    if (file) {
      const form = new FormData();
      form.append("instruction", body.instruction);
      form.append("graph", JSON.stringify(body.graph));
      form.append("file", file);
      payload = form;
      path = `/api/v1/workflows/${encodeURIComponent(id)}/ai-edit/attach/stream`;
    } else {
      payload = body;
      path = `/api/v1/workflows/${encodeURIComponent(id)}/ai-edit/stream`;
    }
    return new Promise<WorkflowAiEditResult>((resolve, reject) => {
      let result: WorkflowAiEditResult | null = null;
      let failure: ApiError | null = null;
      apiSse(path, payload, (ev: any) => {
        if (ev?.type === "phase") onPhase(String(ev.message || ""), ev);
        else if (ev?.type === "finished") result = ev.result;
        else if (ev?.type === "error")
          // WRAPPED IN `{detail}` TO MATCH AN HTTP BODY. `ApiError`
          // reads `body.detail || body.error`, and every caller's error
          // renderer reads `e.detail?.issues` — so passing the already
          // unwrapped detail here made a 422 whose `issues` held a full
          // explanatory sentence surface as the bare word
          // "reply_truncated". The frame must look like the body the
          // non-streaming route returns, or the two paths show the
          // developer different things for the same failure.
          failure = new ApiError(ev.status || 500, { detail: ev.detail });
      })
        .then(() => {
          if (failure) reject(failure);
          // A STREAM THAT ENDED WITHOUT A RESULT IS A FAILURE, not an
          // empty success. Resolving with null here would hand the
          // drawer `undefined` to render as a proposal, which looks
          // exactly like the model having produced nothing.
          else if (result) resolve(result);
          else
            reject(
              new ApiError(500, {
                issues: [
                  "The connection closed before the proposal arrived. Nothing was saved — ask again.",
                ],
              })
            );
        })
        .catch(reject);
    });
  },
  /** Read the requirements and propose the steps — builds nothing.
   *
   *  The review gate: the developer rewords, deletes or adds to these
   *  before pressing Build, so a bad plan costs one cheap call instead
   *  of one per step. */
  aiPlan: (
    id: string,
    body: { instruction: string; graph: WorkflowGraph },
    file: File
  ) => {
    const form = new FormData();
    form.append("instruction", body.instruction);
    form.append("graph", JSON.stringify(body.graph));
    form.append("file", file);
    return apiUpload<WorkflowAiPlan>(
      `/api/v1/workflows/${encodeURIComponent(id)}/ai-plan/attach`,
      form
    );
  },
  /** Carry out the APPROVED steps, saving each as an inactive version. */
  aiBuild: (
    id: string,
    body: { instruction: string; graph: WorkflowGraph },
    steps: WorkflowAiPlanStep[],
    file: File,
    onPhase: (message: string, event: any) => void
  ): Promise<WorkflowAiEditResult> => {
    const form = new FormData();
    form.append("instruction", body.instruction);
    form.append("graph", JSON.stringify(body.graph));
    form.append("steps", JSON.stringify(steps));
    form.append("file", file);
    return new Promise<WorkflowAiEditResult>((resolve, reject) => {
      let result: WorkflowAiEditResult | null = null;
      let failure: ApiError | null = null;
      apiSse(
        `/api/v1/workflows/${encodeURIComponent(id)}/ai-build/attach/stream`,
        form,
        (ev: any) => {
          if (ev?.type === "phase") onPhase(String(ev.message || ""), ev);
          else if (ev?.type === "finished") result = ev.result;
          else if (ev?.type === "error")
            failure = new ApiError(ev.status || 500, { detail: ev.detail });
        }
      )
        .then(() => {
          if (failure) reject(failure);
          else if (result) resolve(result);
          else
            reject(
              new ApiError(500, {
                detail: {
                  issues: [
                    "The connection closed before the build finished. Any steps that completed were saved as versions — check version history.",
                  ],
                },
              })
            );
        })
        .catch(reject);
    });
  },
  validate: (id: string, graph: WorkflowGraph) =>
    api<{ issues: WorkflowValidationIssue[] }>(
      `/api/v1/workflows/${encodeURIComponent(id)}/validate`,
      { method: "POST", body: { graph } }
    ),
  /**
   * V2 — every source this workflow is reachable from, RESOLVED.
   *
   * The graph declares (`graph.sources`), the binding lives on the
   * channel / scheduled job / API key / attached form / calling graph,
   * and this endpoint reads them at render time so the card can show a
   * DID or a cron without the graph ever holding a copy. That is Fork
   * D2's whole mitigation for "two screens could disagree": neither
   * screen caches, so there is nothing to go stale but the render.
   */
  sources: (id: string) =>
    api<WorkflowSourceResolved[]>(
      `/api/v1/workflows/${encodeURIComponent(id)}/sources`
    ),
  run: (id: string, input: Record<string, any>, formInstanceId?: string | null) =>
    api<WorkflowRun>(`/api/v1/workflows/${encodeURIComponent(id)}/run`, {
      method: "POST",
      body: formInstanceId ? { input, form_instance_id: formInstanceId } : { input },
    }),
  /** SSE live run: onEvent receives run_started / step_started /
   *  step_finished / run_finished events. Abort via the signal. */
  runStream: (
    id: string,
    input: Record<string, any>,
    onEvent: (event: WorkflowStreamEvent) => void,
    signal?: AbortSignal,
    formInstanceId?: string | null
  ) =>
    apiSse(
      `/api/v1/workflows/${encodeURIComponent(id)}/run/stream`,
      formInstanceId ? { input, form_instance_id: formInstanceId } : { input },
      onEvent,
      signal
    ),
  /**
   * The run, pausing before every step.
   *
   * Same shape as `runStream` because it is the same endpoint body — the
   * server shares one implementation so the two cannot drift. It adds
   * `debug_ready` and `step_resumed` events.
   *
   * THIS IS A REAL RUN: agents answer, tools fire, tickets get filed.
   */
  debugStream: (
    id: string,
    input: Record<string, any>,
    onEvent: (event: WorkflowStreamEvent) => void,
    signal?: AbortSignal,
    formInstanceId?: string | null
  ) =>
    apiSse(
      `/api/v1/workflows/${encodeURIComponent(id)}/debug/stream`,
      formInstanceId ? { input, form_instance_id: formInstanceId } : { input },
      onEvent,
      signal
    ),
  /** Continue one paused step, all of them, or stop the run. */
  debugControl: (
    runId: string,
    action: "continue" | "run_to_end" | "abort",
    stepRunId?: string | null
  ) =>
    api<{ run_id: string; action: string; released: number }>(
      `/api/v1/workflows/debug/${encodeURIComponent(runId)}/control`,
      { method: "POST", body: { action, step_run_id: stepRunId || null } }
    ),
  /**
   * Settle the gate this run is parked at and keep stepping.
   *
   * A REAL DECISION — the same functions the Tasks page calls. Streams
   * the resumed leg with the debugger still attached, so stepping
   * carries on into the steps after the gate.
   */
  debugResumeGate: (
    runId: string,
    action: "complete_task" | "approve" | "reject",
    onEvent: (event: WorkflowStreamEvent) => void,
    signal?: AbortSignal,
    comment?: string | null
  ) =>
    apiSse(
      `/api/v1/workflows/debug/${encodeURIComponent(runId)}/resume-gate`,
      { action, comment: comment || null },
      onEvent,
      signal
    ),
  /** Where the run is, for a client that reconnected or lost a frame. */
  debugState: (runId: string) =>
    api<{
      run_id: string;
      attached: boolean;
      released?: boolean;
      aborted?: boolean;
      paused: WorkflowDebugPause[];
    }>(`/api/v1/workflows/debug/${encodeURIComponent(runId)}`),
  listRuns: (id: string, limit = 20) =>
    api<WorkflowRun[] | { runs: WorkflowRun[] }>(
      `/api/v1/workflows/${encodeURIComponent(id)}/runs` + qs({ limit })
    ),
  getRun: (runId: string) => api<WorkflowRun>(`/api/v1/workflows/runs/${encodeURIComponent(runId)}`),
  /** Every run that is the caller's (success/error/running/paused), newest
   *  first — includes runs that never reached an approval gate.
   *  Subworkflow CHILD runs are excluded unless includeChildren is true.
   *
   *  "Mine" is two things and each row says which in `involvement`:
   *  runs I STARTED, and runs somebody else started where I personally
   *  decided/completed/was assigned a gate. `involvement` narrows to one
   *  of them; the two partition the default list. */
  myRuns: (
    limit = 20,
    dateFrom?: string,
    includeChildren = false,
    involvement?: MyRunInvolvement | "any"
  ) =>
    api<MyWorkflowRun[]>(
      "/api/v1/workflows/runs/mine" +
        qs({
          limit,
          date_from: dateFrom,
          include_children: includeChildren ? "true" : undefined,
          involvement: involvement && involvement !== "any" ? involvement : undefined,
        })
    ),
  /** F3: mark the caller's pending form_edit task on this run complete.
   *  422 → {detail:{issues:["section.field", …]}} lists incomplete fields. */
  completeTask: (runId: string, comment?: string) =>
    api<WorkflowRun>(`/api/v1/workflows/runs/${encodeURIComponent(runId)}/complete-task`, {
      method: "POST",
      body: comment?.trim() ? { comment: comment.trim() } : {},
    }),
  listTemplates: () => api<WorkflowTemplate[]>("/api/v1/workflows/templates"),
  instantiateTemplate: (key: string, name: string) =>
    api<Workflow>(`/api/v1/workflows/templates/${encodeURIComponent(key)}/instantiate`, {
      method: "POST",
      body: { name },
    }),
  /**
   * Timings, outcomes and the bound form's answers for one workflow.
   *
   * Authorization is entirely the server's: dept_admin and above, scoped
   * by `scoping.is_workflow_visible`, 404 for a workflow the caller
   * cannot see and 403 for a cross-org consumer holding only `use`. The
   * client renders whichever of those it is told, and never guesses.
   */
  analytics: (id: string, params: WorkflowAnalyticsParams = {}) =>
    api<WorkflowAnalytics>(
      `/api/v1/workflows/${encodeURIComponent(id)}/analytics` +
        qs({ date_from: params.date_from, date_to: params.date_to })
    ),
};

// ── Workflow analytics ──────────────────────────────────────────────
//
// Every duration is MILLISECONDS so the whole page can go through the
// one shared `fmtDuration` — the formatter that exists because a 56.9
// hour gate used to render as `204900.11 s`.

export interface WorkflowAnalyticsParams {
  date_from?: string;
  date_to?: string;
}

/** A duration distribution. `samples_ms` is the sorted population itself
 *  (thinned past 200 points), which is what the strip plot draws — with
 *  four cases the individual waits ARE the distribution. */
export interface Distribution {
  count: number;
  mean_ms: number | null;
  p50_ms: number | null;
  p90_ms: number | null;
  min_ms: number | null;
  max_ms: number | null;
  total_ms: number;
  samples_ms: number[];
}

export interface GateStat {
  step_id: string | null;
  step_name: string;
  /** "approval" | "form_edit" — the two human task kinds. */
  kind: string;
  decisions: number;
  approved: number;
  rejected: number;
  pending: number;
  wait: Distribution;
}

export interface StepStat {
  step_name: string;
  step_type: string;
  executions: number;
  failed: number;
  duration: Distribution;
}

export interface DeciderStat {
  user_id: string | null;
  label: string;
  department_id: string | null;
  department_label: string | null;
  decisions: number;
  rejections: number;
  wait: Distribution;
}

export interface DepartmentStat {
  department_id: string | null;
  label: string;
  decisions: number;
  deciders: number;
  wait: Distribution;
}

export interface OutcomeStat {
  step_id: string | null;
  label: string;
  runs: number;
  pct: number;
  /** False = the run ended without reaching an output (error, cancelled)
   *  or has not ended yet. */
  reached_output: boolean;
  /** This output declares `form_outcome: "reopened"` — ending here leaves
   *  the bound document unlocked. */
  reopens_document: boolean;
}

export interface FormOptionStat {
  value: string;
  label: string;
  count: number;
  pct: number;
}

export interface FormFieldStat {
  section_key: string;
  section_title: string;
  field_key: string;
  field_type: string;
  label: string;
  answered: number;
  unanswered: number;
  options: FormOptionStat[];
}

export interface FormStats {
  form_id: string;
  form_name: string;
  instances: number;
  fields: FormFieldStat[];
}

export interface WorkflowAnalyticsTotals {
  runs: number;
  completed_runs: number;
  in_flight_runs: number;
  gate_decisions: number;
  gates_pending: number;
  distinct_gates: number;
  deciders: number;
  first_run_at: string | null;
  last_run_at: string | null;
}

export interface WorkflowAnalytics {
  workflow_id: string;
  workflow_name: string;
  workflow_slug: string;
  organization_id: string | null;
  date_from: string | null;
  date_to: string | null;
  /** The run set hit the server's cap — the numbers describe the most
   *  recent `max_runs` runs in the window, not all of them. */
  truncated: boolean;
  max_runs: number;
  totals: WorkflowAnalyticsTotals;
  cycle_time: Distribution;
  /** Share of total cycle time spent waiting at a human gate. Can exceed
   *  100 when gates run in parallel branches. */
  waiting_share_pct: number | null;
  gates: GateStat[];
  steps: StepStat[];
  deciders: DeciderStat[];
  departments: DepartmentStat[];
  outcomes: OutcomeStat[];
  /** Null when no form is bound — not an empty object, so the page can
   *  say "no form bound" rather than "no answers". */
  form: FormStats | null;
}

/** How a run from GET /workflows/runs/mine is the caller's. Server-derived
 *  — the client never sees the run's `user_id` and must not re-derive it. */
export type MyRunInvolvement = "started" | "participated";

/** Slim row from GET /workflows/runs/mine (member dashboard). */
export interface MyWorkflowRun {
  id: string;
  workflow_id: string;
  workflow_name?: string | null;
  trigger: string;
  status: string;
  /** "started" = I raised this run; "participated" = somebody else raised
   *  it and I decided, completed or was assigned a gate on it. */
  involvement?: MyRunInvolvement;
  error?: string | null;
  cost_usd?: number | null;
  latency_ms?: number | null;
  trace_run_id?: string | null;
  created_at?: string | null;
  finished_at?: string | null;
  /** F2: form instance the run was started from (form-bound workflows). */
  form_instance_id?: string | null;
  /** Subworkflow child runs carry the parent run's id (top-level = null). */
  parent_run_id?: string | null;
}

// ── Workflow templates (starter gallery) ────────────────────────────

export interface WorkflowTemplate {
  key: string;
  name: string;
  description?: string | null;
  node_count?: number | null;
  uses?: string[];
}

/**
 * O4b — one organization this workflow may route an approval into. Backed
 * by a `resource_shares` row on the workflow itself; `access` is the level
 * of that share (`use` is enough, and is what the builder recommends).
 */
export interface WorkflowRoutingTarget {
  organization_id: string;
  organization_name: string;
  organization_slug?: string | null;
  access?: string | null;
  departments: { id: string; name: string }[];
}

// ── Approvals (human-in-the-loop workflow gates, W4) ────────────────

export interface ApprovalContext {
  input?: Record<string, any> | null;
  previous_content?: string | null;
}

export interface ApprovalItem {
  id: string;
  run_id: string;
  /** F3: "approval" (decide) vs "form_edit" (complete form sections).
   *  Absent/null on older payloads — treat as "approval". */
  kind?: "approval" | "form_edit" | string | null;
  /** F3: section keys the assignee must complete (form_edit only). */
  sections?: string[] | null;
  workflow_id?: string | null;
  workflow_name?: string | null;
  step_name?: string | null;
  instructions_rendered?: string | null;
  requested_by_label?: string | null;
  created_at?: string | null;
  timeout_at?: string | null;
  context?: ApprovalContext | null;
  // History rows additionally carry the decision (defensive field names):
  status?: string | null; // "approved" | "rejected" | "timeout" … (defensive)
  decision?: string | null;
  decided_by_label?: string | null;
  decided_by?: string | null;
  decided_at?: string | null;
  comment?: string | null;
  decided_comment?: string | null;
  /** F2: form instance attached to the gated run (nullable). */
  form_instance_id?: string | null;
  form_id?: string | null;
  form_name?: string | null;

  // ── O4b: cross-organization routing (all defaults on a normal task) ──
  /** True when this task was routed across an organization boundary. */
  is_cross_org?: boolean;
  /** The organization the task was routed INTO. */
  target_organization_name?: string | null;
  /** The organization that owns the workflow and the run — where the cost
   *  stays, and who can recover the task if the routing is revoked. */
  owner_organization_name?: string | null;
  /** False once the share that authorised the routing has been revoked.
   *  Such a row disappears from the foreign approver's inbox and appears
   *  in the originating org's, flagged `routing_blocked`. */
  routing_permitted?: boolean;
  routing_blocked?: boolean;
  /** True when the server withheld the run context because the reader is
   *  outside the run's organization. The UI says so rather than showing an
   *  unexplained empty context panel. */
  context_withheld?: boolean;
}

export interface ApprovalAuditStep {
  step_name: string;
  step_type: string;
  status: string;
  error?: string | null;
  started_at?: string | null;
  finished_at?: string | null;
  duration_ms?: number | null;
  output_snippet?: string | null;
  seq?: number;
}

export interface ApprovalAuditGate {
  step_name: string;
  /** F3: "approval" | "form_edit" (form tasks resolve with status "completed"). */
  kind?: "approval" | "form_edit" | string | null;
  /** F3: assigned section keys (form_edit only). */
  sections?: string[] | null;
  status: string;
  requested_by_label?: string | null;
  assignee_labels?: string[];
  instructions_rendered?: string | null;
  created_at?: string | null;
  decided_by_label?: string | null;
  decided_at?: string | null;
  decided_comment?: string | null;
  wait_ms?: number | null;
}

export interface ApprovalAudit {
  workflow_id: string;
  workflow_name: string;
  run_id: string;
  run_status: string;
  trigger?: string;
  requested_by_label?: string | null;
  input?: Record<string, any> | null;
  output?: Record<string, any> | null;
  error?: string | null;
  cost_usd?: number;
  tokens?: number;
  latency_ms?: number;
  trace_run_id?: string | null;
  created_at?: string | null;
  finished_at?: string | null;
  /** F2: form instance attached to the audited run (nullable). */
  form_instance_id?: string | null;
  form_id?: string | null;
  form_name?: string | null;
  steps: ApprovalAuditStep[];
  approvals: ApprovalAuditGate[];
}

// ── Forms (F1 form builder — versioned schemas + instances) ─────────

export type FormAccessLevel = "hidden" | "view" | "edit";

export type FormFieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "select"
  | "checkbox"
  | "readonly"
  | "attachment"
  | "computed";

/** Client-side mirror of the backend upload cap (10 MB). */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** Value stored for an "attachment" field (and upload response). */
export interface FormAttachmentMeta {
  attachment_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

/** One section-level access rule. null department/role = "any". */
export interface SectionPermission {
  department_id: string | null;
  role: Role | null;
  access: FormAccessLevel;
}

export interface FormField {
  key: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  default?: any;
  help?: string;
  placeholder?: string;
  /** Choice list — required (non-empty) when type === "select". */
  options?: string[];
  /** F4: server-evaluated expression — required when type === "computed".
   *  References other fields as field_key or section_key.field_key;
   *  supported: + - * / // % and round(). */
  formula?: string;
}

export interface FormSection {
  key: string;
  title: string;
  description?: string;
  /** F3: agents may write into this section. */
  agent_writable?: boolean;
  /** Floor access for the instance creator (default "edit"). */
  requester_access?: FormAccessLevel;
  fields: FormField[];
  permissions?: SectionPermission[];
}

export interface FormSchema {
  sections: FormSection[];
}

/** Row from GET /forms. */
export interface FormSummary extends ResourceOrigin {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  is_published: boolean;
  active_version?: number | null;
  updated_at: string;
  instance_count?: number;
}

/** GET /forms/{id} — includes the ACTIVE version's schema. */
export interface FormDetail extends ResourceOrigin {
  id: string;
  slug: string;
  name: string;
  description?: string | null;
  is_published: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  active_version?: number | null;
  schema?: FormSchema | null;
}

export interface FormVersion {
  id: string;
  form_id: string;
  version: number;
  schema: FormSchema;
  note?: string | null;
  is_active: boolean;
  created_by?: string | null;
  created_at: string;
}

/** values live as {section_key: {field_key: value}}. */
export type FormValues = Record<string, Record<string, any>>;

export interface FormInstanceInfo {
  id: string;
  form_id: string;
  form_version_id: string;
  form_name?: string | null;
  form_slug?: string | null;
  run_id?: string | null;
  /** V10 — the short case number a caller was read out on the telephone,
   *  and the only identifier on this object a human can say. `id` is a
   *  UUID; this is eight digits. Null on every case a person created
   *  rather than a call, and on every one opened before the column
   *  existed — so it is rendered when present and never asserted. */
  reference?: string | null;
  /** F5 lifecycle: "draft" (not started) | "in_progress" (a bound run is
   *  driving it) | "submitted" (manual submit) | "closed" (the run ended
   *  — READ-ONLY) | "reopened" (ended on a Reopened outcome — editable
   *  again). Typed as string, defensively, like every other status. */
  status: string;
  /** F5: the document lock, decided by the server. True → every section
   *  is view-only for everyone (super_admin included) and every write
   *  path 403s with `document_locked`. */
  locked?: boolean;
  created_by?: string | null;
  created_department_id?: string | null;
  created_at: string;
  updated_at: string;
}

/** GET /forms/instances/{id} — schema/values are pre-filtered per
 *  caller; `access` maps each VISIBLE section key to its level. */
export interface FormInstancePayload {
  instance: FormInstanceInfo;
  schema: FormSchema;
  access: Record<string, "edit" | "view">;
  values: FormValues;
}

/** Slim row from GET /forms/instances/mine. */
export interface MyFormInstance {
  id: string;
  form_id: string;
  form_name?: string | null;
  form_slug?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

/** One audit row from GET /forms/instances/{id}/history. */
export interface FormFieldChange {
  id: string;
  section_key: string;
  field_key: string;
  old_value?: any;
  new_value?: any;
  changed_by?: string | null;
  changed_by_label?: string | null;
  changed_at: string;
}

/** One row of the form-instance grid. `values` is already permission-filtered. */
export interface FormInstanceRow {
  id: string;
  reference?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  run_id?: string | null;
  /** The run's trigger — "voice" | "chat" | "form" | "manual" | … */
  channel?: string | null;
  /** `{section: {field: value}}`, sections this caller may see only. */
  values: Record<string, Record<string, unknown>>;
}

export interface FormInstanceList {
  total: number;
  limit: number;
  offset: number;
  /** True when a value filter hit the scan cap: `total` is a floor. */
  truncated: boolean;
  rows: FormInstanceRow[];
}

export interface FormInstanceQuery {
  limit?: number;
  offset?: number;
  status?: string | null;
  channel?: string | null;
  reference?: string | null;
  /** EXACT match. Finds a person's cases; never comes back in a row. */
  cnic?: string | null;
  q?: string | null;
  field?: string | null;
  value?: string | null;
  date_from?: string | null;
  date_to?: string | null;
}

export const formsApi = {
  list: () => api<FormSummary[]>("/api/v1/forms"),
  /**
   * The cases raised against one form — filtered, paged, and already
   * cut to the sections this caller may read.
   */
  instances: (formId: string, query: FormInstanceQuery = {}) =>
    api<FormInstanceList>(
      `/api/v1/forms/${encodeURIComponent(formId)}/instances` + qs(query as any)
    ),
  create: (body: { name: string; description?: string | null }) =>
    api<FormDetail>("/api/v1/forms", { method: "POST", body }),
  get: (id: string) => api<FormDetail>(`/api/v1/forms/${encodeURIComponent(id)}`),
  update: (id: string, body: { name?: string; description?: string | null; is_published?: boolean }) =>
    api<FormDetail>(`/api/v1/forms/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  remove: (id: string) => api(`/api/v1/forms/${encodeURIComponent(id)}`, { method: "DELETE" }),
  listVersions: (id: string) =>
    api<FormVersion[]>(`/api/v1/forms/${encodeURIComponent(id)}/versions`),
  /** GET /forms/{id}/export — the ACTIVE version as elfa-workflow's
   *  create-a-type payload. 422 with `issues` when the form cannot be
   *  represented over there; see `app.services.form_export`. */
  exportForElfa: (id: string) =>
    api<Record<string, any>>(`/api/v1/forms/${encodeURIComponent(id)}/export`),
  createVersion: (id: string, body: { schema: FormSchema; note?: string | null; activate?: boolean }) =>
    api<FormVersion>(`/api/v1/forms/${encodeURIComponent(id)}/versions`, { method: "POST", body }),
  activateVersion: (versionId: string) =>
    api<FormVersion>(`/api/v1/forms/versions/${encodeURIComponent(versionId)}/activate`, {
      method: "POST",
    }),
  createInstance: (formId: string, values: FormValues = {}) =>
    api<FormInstancePayload>(`/api/v1/forms/${encodeURIComponent(formId)}/instances`, {
      method: "POST",
      body: { values },
    }),
  myInstances: (limit = 50) =>
    api<MyFormInstance[]>("/api/v1/forms/instances/mine" + qs({ limit })),
  getInstance: (id: string) =>
    api<FormInstancePayload>(`/api/v1/forms/instances/${encodeURIComponent(id)}`),
  /** PATCH only the CHANGED fields: {section: {field: value}}. */
  patchInstance: (id: string, values: FormValues) =>
    api<FormInstancePayload>(`/api/v1/forms/instances/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { values },
    }),
  submitInstance: (id: string) =>
    api<FormInstancePayload>(`/api/v1/forms/instances/${encodeURIComponent(id)}/submit`, {
      method: "POST",
    }),
  instanceHistory: (id: string, limit = 100) =>
    api<FormFieldChange[]>(
      `/api/v1/forms/instances/${encodeURIComponent(id)}/history` + qs({ limit })
    ),
  /** Upload (or replace) the file behind one "attachment" field.
   *  Multipart — the browser sets the Content-Type boundary itself. */
  uploadAttachment: (instanceId: string, sectionKey: string, fieldKey: string, file: File) => {
    const form = new FormData();
    form.append("section_key", sectionKey);
    form.append("field_key", fieldKey);
    form.append("file", file);
    return apiUpload<FormAttachmentMeta>(
      `/api/v1/forms/instances/${encodeURIComponent(instanceId)}/attachments`,
      form
    );
  },
  deleteAttachment: (attachmentId: string) =>
    api(`/api/v1/forms/attachments/${encodeURIComponent(attachmentId)}`, { method: "DELETE" }),
  /** Authenticated fetch → blob object URL → temporary <a download>. */
  downloadAttachment: async (attachmentId: string, filename: string): Promise<void> => {
    const blob = await apiBlob(`/api/v1/forms/attachments/${encodeURIComponent(attachmentId)}`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "attachment";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  /** Server-rendered PDF of the instance (sections visible to the
   *  caller only) — same blob-download pattern as attachments.
   *  `includeHistory` appends the field-change trail (F6): off by
   *  default, because the case file people file with a claim is the
   *  signatures, not the keystrokes. */
  exportPdf: async (
    instanceId: string,
    filename?: string,
    includeHistory = false
  ): Promise<void> => {
    const blob = await apiBlob(
      `/api/v1/forms/instances/${encodeURIComponent(instanceId)}/pdf` +
        (includeHistory ? "?history=1" : "")
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

// ── Database connections (W5 sql_query data nodes) ──────────────────

/** Row from GET /api/v1/db-connections. The connection URL itself is
 *  write-only — the backend only ever returns a masked url_hint. */
export interface DbConnection {
  id: string;
  name: string;
  url_hint?: string | null;
  is_active?: boolean;
  created_at?: string | null;
}

export interface DbConnectionTestResult {
  ok: boolean;
  error?: string | null;
  dialect?: string | null;
}

export const dbConnectionsApi = {
  /** dept_admin+ — list rows (never includes the URL). */
  list: () => api<DbConnection[]>("/api/v1/db-connections"),
  /** super_admin only. */
  create: (body: { name: string; url: string }) =>
    api<DbConnection>("/api/v1/db-connections", { method: "POST", body }),
  /** super_admin only — omit url to keep the stored one. */
  update: (id: string, body: { name?: string; url?: string; is_active?: boolean }) =>
    api<DbConnection>(`/api/v1/db-connections/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body,
    }),
  /** super_admin only. */
  remove: (id: string) =>
    api(`/api/v1/db-connections/${encodeURIComponent(id)}`, { method: "DELETE" }),
  test: (id: string) =>
    api<DbConnectionTestResult>(`/api/v1/db-connections/${encodeURIComponent(id)}/test`, {
      method: "POST",
    }),
};

// ── Service connections (how to reach an external system) ───────────
//
// The tenant-scoped replacement for per-integration environment
// variables. An AgentTool row covers ONE HTTP call; a connection covers
// an integration that takes several and therefore lives in Python —
// `elfa_workflow_bridge` (log in → find the form by slug → file the
// case) is the first.
//
// THE SECRET IS WRITE-ONLY. It is never returned by any endpoint, in any
// form. `secret_set` says whether one is stored and `secret_hint` is a
// LENGTH — deliberately not a prefix, since four characters of a live
// credential in every list response is four characters too many. So the
// edit form cannot pre-fill it: leaving the field blank keeps the stored
// credential, and that has to be said on the form.

/** Row from GET /api/v1/service-connections. Carries no credential. */
export interface ServiceConnection {
  id: string;
  name: string;
  label?: string;
  base_url?: string;
  auth_mode?: string;
  username?: string;
  /** Whether a credential is stored — never the credential itself. */
  secret_set?: boolean;
  /** A length, e.g. "set (24 characters)". Never a prefix. */
  secret_hint?: string;
  settings?: Record<string, string>;
  is_active?: boolean;
  note?: string;
  /** Which tenant owns the row; null means every tenant can use it.
   *  A service resolves a connection by NAME, matching the caller's own
   *  organization first and falling back to an org-less row — so one
   *  created under the wrong tenant is invisible to the tool that wants
   *  it, and looks identical here to one that is fine. Hence the badge. */
  organization_id?: string | null;
  organization_name?: string;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface ServiceConnectionWrite {
  name?: string;
  label?: string;
  base_url?: string;
  auth_mode?: string;
  username?: string;
  /** CREATE ONLY, and omitted vs null are different requests: omitted
   *  means the caller's own organization, explicit null asks for the
   *  row every tenant shares (platform principals only). Collapsing
   *  the two would make a platform-wide act reachable by saying
   *  nothing. */
  organization_id?: string | null;
  /** Omit to keep the stored credential; "" clears it. */
  secret?: string;
  settings?: Record<string, string>;
  is_active?: boolean;
  note?: string;
}

/** What the PBX Portal can transfer a call to (`transfer_to_person`'s pick list).
 *  `value` is what the node stores: a desk team's number ("500"), an extension ("101"),
 *  or a target name. */
export interface PbxTransferTarget {
  value: string;
  label: string;
  kind: string;
  detail: string;
}

export const pbxPortalApi = {
  /** Never an error status: `error` says why the list is empty. */
  /** `workflowId`: the list belongs to the workflow's organization, not the user's. */
  targets: (workflowId?: string, connection?: string, agentId?: string) => {
    const q = new URLSearchParams();
    if (workflowId) q.set("workflow_id", workflowId);
    if (agentId) q.set("agent_id", agentId);
    if (connection) q.set("connection", connection);
    const qs = q.toString();
    return api<{ configured: boolean; targets: PbxTransferTarget[]; error: string | null }>(
      `/api/v1/pbx-portal/targets${qs ? `?${qs}` : ""}`);
  },
};

export const serviceConnectionsApi = {
  /** dept_admin+ — a builder needs to see which connections exist. */
  list: () => api<ServiceConnection[]>("/api/v1/service-connections"),
  /** org_admin+. */
  create: (body: ServiceConnectionWrite) =>
    api<ServiceConnection>("/api/v1/service-connections", {
      method: "POST",
      body,
    }),
  /** org_admin+ — omit `secret` to keep the stored one. */
  update: (id: string, body: ServiceConnectionWrite) =>
    api<ServiceConnection>(
      `/api/v1/service-connections/${encodeURIComponent(id)}`,
      { method: "PATCH", body },
    ),
  /** org_admin+. Prefer is_active=false to turn one off — it keeps the
   *  credential, which matters when the person reaching for this is
   *  debugging a live incident. */
  remove: (id: string) =>
    api(`/api/v1/service-connections/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
};

// ── Channels (external messaging → agents) ──────────────────────────
//
// TWO TYPES:
//   "whatsapp" — WE are the integration: Meta calls our webhook and we
//                call the Graph API to deliver (CH1/CH2).
//   "gateway"  — the customer runs its OWN WhatsApp gateway; it POSTs
//                inbound messages to us and reads the assistant reply
//                out of the SYNCHRONOUS response body (CH3). Inbound
//                only — we never call Meta, and the Meta fields
//                (phone_number_id / access_token / app_secret /
//                verify_token) are unused for this type.

//   "livekit"  — a PHONE NUMBER (L1). Calls arrive through LiveKit's
//                SIP service, so there is no inbound HTTP surface at
//                all: no webhook, no verify token, no inbound token.
//                The DIALLED NUMBER is the entire routing key — a call
//                carries no JWT, no user and no organization — so
//                `phone_number` is E.164-normalised and GLOBALLY unique
//                in the database. Unlike `slug`, a global constraint is
//                not a leak here: E.164 is one worldwide namespace and
//                a number cannot belong to two tenants.
export type ChannelType = "whatsapp" | "gateway" | "livekit";

/** How the gateway frames the inbound key inside `auth_header` (CH3b):
 *  "bearer" → `<auth_header>: Bearer <token>`, "raw" → the value IS the
 *  token. Irrelevant when no `auth_header` is configured — the built-in
 *  mechanisms are always accepted on top of it. */
export type ChannelAuthScheme = "bearer" | "raw";

/** What the gateway endpoint does with a request that carries NO per-user
 *  identifier (CH3b). "shared" answers using ONE conversation shared by the
 *  whole channel and flags the reply (`sender_resolved: false` + `warning`);
 *  "reject" answers 422 naming every path that was tried. */
export type ChannelSenderFallback = "shared" | "reject";

/** Row from GET /api/v1/channels. Secrets (access token / app secret /
 *  callback token) are write-only — the backend only ever returns a
 *  masked token_hint and has_* flags. The verify_token IS returned in
 *  full: it is not a secret in Meta's model (it only proves webhook
 *  ownership). The gateway inbound_token IS a live credential: full
 *  value for super_admins, `null` + inbound_token_hint for dept_admins. */
export interface Channel {
  id: string;
  type: ChannelType | string;
  name: string;
  agent_id: string;
  agent_name?: string | null;
  department_id?: string | null;
  enabled: boolean;
  phone_number_id?: string | null;
  /** Masked hint of the stored access token, e.g. "…x4F2". */
  token_hint?: string | null;
  has_app_secret?: boolean;
  verify_token?: string | null;
  /** Backend-relative webhook path, e.g. "/api/channels/whatsapp/{id}/webhook".
   *  NULL for "livekit" — a telephony channel has no inbound HTTP surface. */
  webhook_path?: string | null;
  /** L1, "livekit" channels only — the dialled number in E.164 form
   *  ("+441707123456"). NULL for every other type. This is the routing
   *  key: it maps to exactly one agent in exactly one organization. */
  phone_number?: string | null;
  /** V2 (Fork D2) — the workflow a call on this number STARTS once
   *  intake has what it needs. livekit channels only; NULL is normal
   *  ("answers with the agent, starts nothing"). The graph declares that
   *  it starts from a call; THIS says which call. */
  workflow_id?: string | null;
  /** Resolved for display only — the row holds the id, exactly as an
   *  approval gate holds a role id, so a rename cannot unhook it. */
  workflow_name?: string | null;
  // ── Gateway channels (CH3) — null/absent on whatsapp channels ──
  /** Backend-relative path the GATEWAY posts to:
   *  "/api/channels/gateway/{id}/message". Prefix with the public host. */
  endpoint_path?: string | null;
  /** CH3b — the SAME endpoint under a readable slug:
   *  "/api/channels/{slug}/chat". Identical behaviour to endpoint_path;
   *  hand the gateway team either. null when the channel has no slug. */
  endpoint_alias?: string | null;
  /** CH3d — the SESSION-KEYED chat endpoint:
   *  "/api/channels/gateway/{slug or id}/chat". A DIFFERENT contract from
   *  the two above, not another alias: a fixed
   *  `{session_id, response, model}` body, `reply_field` does not apply,
   *  and a request with no `session_id` mints one. This is the URL to
   *  hand a WhatsApp middleware. */
  chat_endpoint_path?: string | null;
  /** URL-friendly alias segment, auto-derived from the name on create
   *  ("Production Assistant" → "production_assistant"). Unique across
   *  channels; "whatsapp" / "gateway" are reserved. */
  slug?: string | null;
  /** CH3b — EXTRA header name the gateway sends the key in, e.g.
   *  "CHAT_API_KEY". null = only the built-in mechanisms are looked at. */
  auth_header?: string | null;
  /** How the key is framed inside `auth_header` (default "bearer"). */
  auth_scheme?: ChannelAuthScheme | string;
  /** CH3b — "shared" (default) or "reject" for requests with no per-user id. */
  sender_fallback?: ChannelSenderFallback | string;
  /** BCP-47-ish code the channel's voice notes are decoded as.
   *  `""`/null = use the deployment default (auto-detect). */
  transcription_language?: string | null;
  /** The key the gateway presents. super_admin ONLY — dept_admins get null
   *  here and the masked hint below. Server-generated, or pasted by an
   *  admin on create/update (CH3b). */
  inbound_token?: string | null;
  /** Masked form ("••••abcd") — safe to show to dept_admins. */
  inbound_token_hint?: string | null;
  /** Dotted path to the message text in the gateway's payload
   *  (e.g. "data.text", "a.b[0].c"). null = auto-detect. */
  message_path?: string | null;
  /** Dotted path to the sender id. null = auto-detect. */
  sender_path?: string | null;
  /** Dotted path to the gateway's session id. null = auto-detect. */
  session_path?: string | null;
  /** JSON key the reply is mirrored under (default "reply"). */
  reply_field?: string;
  /** Optional async push target — the reply is ALSO POSTed here.
   *  SUPPRESSED while `deliver_via_graph` is on: one reply, one route. */
  callback_url?: string | null;
  /** True when a bearer token is stored for the callback (never echoed). */
  has_callback_token?: boolean;
  /** CH3g — WHO delivers the reply to the customer. False (the default,
   *  and every channel that existed before it): the caller reads the reply
   *  out of our response body and sends it. True: we POST it to the
   *  WhatsApp Cloud API ourselves with this channel's access token, using
   *  the `phone_number_id` the inbound message names (falling back to the
   *  stored one) — for a middleware that ignores our response. */
  deliver_via_graph?: boolean;
  // ── Keyword-gated sessions (CH2) ──
  /** true = shared number: silent until the sender texts start_keyword.
   *  Defaults to true for "whatsapp" and FALSE for "gateway" (the gateway
   *  already owns the ai / aiexit session before it forwards to us). */
  require_keyword?: boolean;
  /** Word that opens an AI session (default "ai"). */
  start_keyword?: string;
  /** Word that ends it (default "aiexit"); must differ from start_keyword. */
  exit_keyword?: string;
  /** Idle minutes before a session expires; 0 = never. */
  session_timeout_minutes?: number;
  /** null → server default text (which names the keywords); custom text is sent verbatim. */
  greeting_text?: string | null;
  exit_text?: string | null;
  /** Read-only — senders currently inside an active, non-expired session. */
  active_sessions?: number;
  last_inbound_at?: string | null;
  last_outbound_at?: string | null;
  last_error?: string | null;
  created_at?: string | null;
}

export interface ChannelCreateBody {
  type: ChannelType;
  name: string;
  agent_id: string;
  department_id?: string | null;
  /** V2 — livekit channels only (422 otherwise); must be a workflow of
   *  the same organization. */
  workflow_id?: string | null;
  /** REQUIRED for type "whatsapp" (422 without it), unused for "gateway". */
  access_token?: string;
  /** REQUIRED for type "whatsapp" (422 without it), unused for "gateway". */
  phone_number_id?: string;
  /** Optional — enables webhook signature validation when set (whatsapp). */
  app_secret?: string;
  // ── Keyword-gated sessions (CH2); omit for the per-type default ──
  require_keyword?: boolean;
  /** Max 32 chars, non-empty; must differ from exit_keyword (422 otherwise). */
  start_keyword?: string;
  exit_keyword?: string;
  /** 0…1440; 0 = never expire. */
  session_timeout_minutes?: number;
  /** Omit/null for the templated default; custom text is sent verbatim (≤ 1000 chars). */
  greeting_text?: string | null;
  exit_text?: string | null;
  // ── Gateway field mapping (CH3) — omit/null = auto-detect ──
  /** Dotted path to the message text, e.g. "data.text" / "a.b[0].c". */
  message_path?: string | null;
  sender_path?: string | null;
  session_path?: string | null;
  /** Simple JSON key (letters/digits/_/-), default "reply". */
  reply_field?: string;
  /** http:// or https:// — optional async push of the reply. */
  callback_url?: string | null;
  /** Write-only bearer token for the callback POST — never echoed back. */
  callback_token?: string | null;
  /** CH3g — deliver the reply to WhatsApp ourselves. Omitted = false. */
  deliver_via_graph?: boolean;
  // ── Real-gateway adaptation (CH3b) — gateway type only ──
  /** WRITE field: paste the key the gateway is ALREADY configured with
   *  (16…128 chars, letters/digits and `. _ ~ + / = : -` → 422 otherwise).
   *  Omit/blank and the server generates one. */
  inbound_token?: string | null;
  /** Header name the key arrives in, e.g. "CHAT_API_KEY" (letters, digits,
   *  `_` and `-`; ≤ 60 chars). Omit for the built-in mechanisms only. */
  auth_header?: string | null;
  /** How that header frames the key — default "bearer". */
  auth_scheme?: ChannelAuthScheme;
  /** Alias segment; omit and it is derived from the name (collision-suffixed).
   *  An EXPLICIT slug that clashes → 409; a reserved one → 422. */
  slug?: string | null;
  /** Requests with no per-user id: "shared" (default) or "reject". */
  sender_fallback?: ChannelSenderFallback;
  /** BCP-47-ish code the channel's voice notes are decoded as.
   *  `""`/null = use the deployment default (auto-detect). */
  transcription_language?: string | null;
  // ── Telephony (L1) — "livekit" type only ──
  /** REQUIRED for type "livekit" (422 without it) and REFUSED for every
   *  other type (422 — a number parked on a WhatsApp channel would consume
   *  the global uniqueness constraint while routing nothing).
   *
   *  Must be INTERNATIONAL: "+441707123456" or "00441707123456". Spaces,
   *  dashes and parentheses are stripped. A national number ("01707
   *  123456") or a bare digit string is refused rather than expanded —
   *  expanding one needs a default country, and a guess routes the call to
   *  the wrong organization. Already in use → 409. */
  phone_number?: string | null;
}

/** PATCH body — omit access_token/app_secret/callback_token (or send
 *  blank) to keep the stored values; explicit null clears them. */
export interface ChannelUpdateBody {
  /** Switching to "whatsapp" needs Meta credentials on file (422 otherwise);
   *  switching to "gateway" mints an inbound_token when there isn't one. */
  type?: ChannelType;
  name?: string;
  agent_id?: string;
  department_id?: string | null;
  enabled?: boolean;
  /** V2 (Fork D2) — bind/unbind the workflow this number starts. An
   *  explicit `null` UNBINDS: the number keeps answering with its agent
   *  and simply starts nothing afterwards, which is what "unbound (test
   *  only)" looks like from the channel's side. */
  workflow_id?: string | null;
  phone_number_id?: string;
  access_token?: string;
  app_secret?: string;
  // ── Keyword-gated sessions (CH2) — omitted fields keep the stored values.
  // The keyword-collision check runs against the MERGED values server-side.
  require_keyword?: boolean;
  start_keyword?: string;
  exit_keyword?: string;
  session_timeout_minutes?: number;
  /** null (or "") clears the override → back to the templated default. */
  greeting_text?: string | null;
  exit_text?: string | null;
  // ── Gateway field mapping (CH3) — explicit null clears → auto-detect ──
  message_path?: string | null;
  sender_path?: string | null;
  session_path?: string | null;
  reply_field?: string;
  /** null clears the callback (and drops the stored callback token). */
  callback_url?: string | null;
  /** Write-only; blank keeps the stored token, explicit null clears it. */
  callback_token?: string | null;
  /** CH3g — omitted keeps the stored value; send it explicitly to turn
   *  delivery off again. */
  deliver_via_graph?: boolean;
  // ── Real-gateway adaptation (CH3b) — omitted fields keep stored values ──
  /** WRITE field: a value REPLACES the stored key (that is how you adopt a
   *  key the gateway already has); blank/omitted keeps it. Use
   *  `regenerateInboundToken` to mint a fresh one instead. */
  inbound_token?: string | null;
  /** Explicit null clears the custom header (built-in mechanisms only). */
  auth_header?: string | null;
  auth_scheme?: ChannelAuthScheme;
  /** Clash → 409, reserved ("whatsapp" / "gateway") → 422; explicit null
   *  drops the alias route. */
  slug?: string | null;
  sender_fallback?: ChannelSenderFallback;
  /** BCP-47-ish code the channel's voice notes are decoded as.
   *  `""`/null = use the deployment default (auto-detect). */
  transcription_language?: string | null;
  // ── Telephony (L1) ──
  /** Re-provisioning a number. Already in use → 409; on a non-livekit
   *  channel → 422. Switching a channel AWAY from "livekit" releases its
   *  number automatically, which is what frees a rented DID for the next
   *  tenant. Switching a channel TO "livekit" requires one (422). */
  phone_number?: string | null;
}

export interface ChannelTestSendResult {
  ok: boolean;
  error?: string | null;
  message_id?: string | null;
  /** Gateway channels only — the callback_url the test payload went to. */
  target?: string | null;
  /** Gateway channels only — HTTP status returned by the callback. */
  status_code?: number | null;
}

/** V2b — one workflow the phone-number dialog may bind a call to.
 *
 *  Every entry is already published, already in the channel's
 *  organization and already declares a `voice_call` source in its ACTIVE
 *  version, because the SERVER applied all three. Nothing here is
 *  re-filtered in the browser: a picker that offered a workflow the save
 *  would refuse — or, worse, one the save would accept and the call would
 *  then silently not run — is the same defect as having no picker. */
export interface CallFlowOption {
  id: string;
  name: string;
  description?: string | null;
  /** The ACTIVE version whose graph declares the source — the exact
   *  version the voice worker would interpret when a call arrives. */
  version?: number | null;
  /** The author's label on the source node ("Complaint line"), if any. */
  source_label?: string | null;
}

export interface CallFlowOptions {
  options: CallFlowOption[];
  /** Workflows the organization owns AT ALL, whatever their source. What
   *  separates "you have no workflows" from "none of them starts from a
   *  call" — two different problems with two different fixes. */
  workflows_in_scope: number;
  /** Server-owned copy for the empty case; null when there is something
   *  to offer. Rendered verbatim, never re-derived from the count. */
  empty_note?: string | null;
  /** WHICH TENANT WAS SEARCHED. Present on every answer, not only the
   *  empty ones. On a multi-tenant page "no results" and "you are looking
   *  at the wrong organization" are the same picture — and the Agent
   *  picker beside this one (`GET /agents/tree`) is NOT narrowed the same
   *  way for a platform principal, so the two controls in one dialog can
   *  legitimately answer for different scopes. `empty_note` already names
   *  it; these are for a client that wants to compare. */
  organization_id?: string | null;
  organization_name?: string | null;
}

export const channelsApi = {
  /** dept_admin+ — list channels visible to the caller. */
  list: () => api<Channel[]>("/api/v1/channels"),
  /** super_admin only — workflows a channel may BIND (V2b; V2-chat).
   *
   *  Pass the channel id when EDITING, so the list is scoped to that
   *  channel's organization; omit it when creating, and it is scoped to
   *  the organization the new channel would land in. Either way it is the
   *  same org the create/update handler validates the binding against.
   *
   *  `organizationId` (V9) names a tenant explicitly — for the Voice Test
   *  Call page, which picks its own organization rather than inheriting
   *  the one the caller is acting as. It NARROWS only
   *  (`scoping.organization_filter_allows`) and is ignored when a
   *  channel is named, because a channel already names its own tenant.
   *
   *  `channelType` (V2-chat) says which KIND of flow to list: "livekit" →
   *  voice call flows, "whatsapp" / "gateway" → chat flows. It is LAST and
   *  optional because the server defaults it to "livekit" — which is what
   *  every caller that predates chat flows means — and it is ignored when
   *  a channel is named, because the channel already says what it is. */
  callFlowOptions: (
    channelId?: string | null,
    organizationId?: string | null,
    channelType?: string | null
  ) =>
    api<CallFlowOptions>(
      "/api/v1/channels/call-flow-options" +
        qs({
          channel_id: channelId,
          organization_id: organizationId,
          channel_type: channelType,
        })
    ),
  /** super_admin only. Agent must exist (404) and be published (422); duplicate name → 409. */
  create: (body: ChannelCreateBody) => api<Channel>("/api/v1/channels", { method: "POST", body }),
  /** super_admin only. */
  update: (id: string, body: ChannelUpdateBody) =>
    api<Channel>(`/api/v1/channels/${encodeURIComponent(id)}`, { method: "PATCH", body }),
  /** super_admin only. */
  remove: (id: string) => api(`/api/v1/channels/${encodeURIComponent(id)}`, { method: "DELETE" }),
  /** super_admin only. "whatsapp": sends a real WhatsApp message through
   *  the Graph API. "gateway": POSTs a test payload to callback_url
   *  instead (422 when the channel has no callback configured). */
  testSend: (id: string, body: { to: string; text: string }) =>
    api<ChannelTestSendResult>(`/api/v1/channels/${encodeURIComponent(id)}/test-send`, {
      method: "POST",
      body,
    }),
  /** super_admin only — invalidates the old verify token (update Meta afterwards). */
  regenerateVerifyToken: (id: string) =>
    api<Channel>(`/api/v1/channels/${encodeURIComponent(id)}/regenerate-verify-token`, {
      method: "POST",
    }),
  /** super_admin only, gateway channels only (422 otherwise) — mints a new
   *  inbound_token. The old one starts returning 401 immediately, so the
   *  gateway team must be given the new value straight away. */
  regenerateInboundToken: (id: string) =>
    api<Channel>(`/api/v1/channels/${encodeURIComponent(id)}/regenerate-inbound-token`, {
      method: "POST",
    }),
};

// ── Telephony — LiveKit numbers, projects and room tokens (L1) ─────────
//
// A phone call arrives with no JWT, no user and no organization: only the
// number that was dialled. So the number IS the routing key, and these
// endpoints are the admin surface over it plus the per-organization
// LiveKit Cloud project it runs on.
//
// AUTHORIZATION COMES FROM THE SERVER, always. `ready` / `reason` /
// `explanation` on a number are the server's verdict, rendered verbatim —
// never re-derived from the other fields, and never used to decide what a
// client may do.

/** Why a mapping is not ready. `ok` when it is. The routing-key values
 *  come from `app.services.telephony`; the voice ones are M3's own
 *  `VoiceAvailability.reason`, so a phone number reports the same reason
 *  the agent editor does. */
export type TelephonyReason =
  | "ok"
  | "invalid_number"
  | "unmapped_number"
  | "ambiguous_number"
  | "channel_disabled"
  | "no_organization"
  | "agent_unavailable"
  | "disabled"
  | "no_model"
  | "no_credential"
  | "no_livekit_project"
  | "error";

/** Which rung of `organization → environment` answered. */
export type LiveKitSource = "organization" | "environment";

export interface PhoneNumber {
  channel_id: string;
  name: string;
  /** E.164, e.g. "+441707123456". */
  phone_number: string;
  agent_id: string;
  agent_name?: string | null;
  organization_id?: string | null;
  department_id?: string | null;
  enabled: boolean;
  /** Would a call to this number be answered right now? Read-only probe —
   *  it opens no session and consumes no concurrency slot. */
  ready: boolean;
  reason?: TelephonyReason | string | null;
  /** One sentence, rendered verbatim (the server owns the explanation). */
  explanation?: string | null;
  livekit_source?: LiveKitSource | string | null;
}

export interface TelephonyResolveResult {
  phone_number: string;
  channel_id: string;
  channel_name: string;
  organization_id: string;
  agent_id: string;
  agent_name?: string | null;
  department_id?: string | null;
  ready: boolean;
  reason?: TelephonyReason | string | null;
  explanation?: string | null;
  livekit_source?: LiveKitSource | string | null;
}

/**
 * The routing key a TEST CALL should carry (V0, specs/006 §3.3).
 *
 * An INTENT, not a payload: the server authorises it against your own
 * tenancy and then writes the job metadata itself. Exactly one key —
 * `dialled_number` routes through the same six refusals a telephone call
 * meets, `agent_id` is the no-DID case and short-circuits the number
 * entirely (inventing a `+E.164` for test calls would put un-dialable
 * numbers in the channels table and in every cost report).
 */
export interface TestCallDispatch {
  dialled_number?: string | null;
  agent_id?: string | null;
  /** V9 — the CALL FLOW this test call should run. NOT a routing key: a
   *  workflow names no agent (Fork D2 — the graph declares, the channel
   *  binds), so `agent_id` above is still required and the operator picks
   *  both. Omit it and the behaviour is exactly V0's.
   *
   *  Refused at the mint with a sentence when the workflow is not yours
   *  (404, same as a missing one), not published (409
   *  `workflow_unpublished`) or does not start from a call (409
   *  `workflow_not_call_sourced`) — because neither of the last two can
   *  be heard on the call. */
  workflow_id?: string | null;
  label?: string | null;
}

/** What the server says the token will tell the worker. Echoed back so a
 *  page can show the exact key it is about to test rather than guess. */
export interface TestCallDispatchEcho {
  source: string;
  agent_id?: string | null;
  agent_name?: string | null;
  dialled_number?: string | null;
  channel_id?: string | null;
  channel_name?: string | null;
  organization_id?: string | null;
  /** V9 — the call flow the worker will run, resolved and named. */
  workflow_id?: string | null;
  workflow_name?: string | null;
  workflow_version?: number | null;
  /** The workflow the DIALLED CHANNEL is bound to, when the routing key
   *  is a number. It OUTRANKS `workflow_id` in the worker (a channel
   *  binding is what a real call to this number runs), so a page can say
   *  which one will actually win before the call is placed. */
  channel_workflow_id?: string | null;
  /** The worker name in the token's `roomConfig` — `LIVEKIT_AGENT_NAME`. */
  agent_worker?: string | null;
}

export interface LiveKitRoomToken {
  /** Bearer credential for ONE room, short-lived. Never stored. */
  token: string;
  url: string;
  room: string;
  identity: string;
  expires_at: string;
  ttl_seconds: number;
  source: LiveKitSource | string;
  organization_id?: string | null;
  /** Non-null when the token carries a `roomConfig` dispatch claim. */
  agent_name?: string | null;
  dispatch?: TestCallDispatchEcho | null;
}

/** One organization's LiveKit Cloud project. The SIGNING SECRET is never
 *  returned in any shape — only `secret_hint` (its last 4 characters) and
 *  `can_decrypt`. The API KEY is returned in clear on purpose: it is the
 *  `iss` claim of every token we mint and therefore travels to every
 *  client anyway. */
export interface LiveKitCredential {
  organization_id: string;
  configured: boolean;
  /** FALSE when the stored ciphertext no longer opens under the current
   *  XELLERATE_SECRET_KEY. The organization keeps a project that LOOKS
   *  configured while every call quietly runs on the platform's — show it
   *  as ⚠ unreadable rather than healthy. */
  can_decrypt: boolean;
  is_active: boolean;
  url?: string | null;
  api_key?: string | null;
  secret_hint?: string | null;
  /** Where a call would ACTUALLY get its project — a different question
   *  from `configured`. An unreadable row is configured and still
   *  resolves to "environment". `null` = nothing resolves at all. */
  effective_source?: LiveKitSource | string | null;
  rotated_at?: string | null;
  updated_at?: string | null;
  /** True when the platform's own env project could answer for this org. */
  environment_available: boolean;
}

export interface LiveKitCredentialBody {
  /** wss:// or https:// form of the Cloud project URL — both accepted,
   *  stored as wss://. A non-host → 422. */
  url: string;
  api_key: string;
  /** Blank/omitted KEEPS the stored secret (so a URL typo can be fixed
   *  without re-pasting it). Required on a first save. */
  api_secret?: string;
  is_active?: boolean;
}

/** One recorded prompt, as the API reports it. (W3) */
export interface PromptAudio {
  key: string;
  exists: boolean;
  /** "ok" | "silent" | "wrong_format" | "unreadable" | "missing" */
  status: string;
  detail: string;
  sample_rate?: number | null;
  channels?: number | null;
  bits?: number | null;
  seconds?: number | null;
  /** 0–32767. The server calls anything under ~300 silent. */
  peak?: number | null;
  bytes?: number | null;
}

/**
 * The WAVs a voice call plays, named by a step's `audio_key`. (W3)
 *
 * Worth knowing before using these: **`audio_key` WINS over `text`.**
 * Wherever a usable file exists the worker plays it and never reaches
 * the prompt's words — so a recording is not decoration beside the
 * text, it REPLACES it. `status` is what says whether that is currently
 * happening, and `"silent"` is the one to act on: it plays, so the text
 * is suppressed and the caller hears nothing at all.
 */
export const promptAudioApi = {
  /** dept_admin+ — every file in the directory, or just the keys named.
   *  Pass the keys a graph references to learn about the MISSING ones
   *  too; a directory listing cannot report a file that is not there. */
  list: (keys?: string[]) =>
    api<PromptAudio[]>(
      "/api/v1/prompt-audio" + (keys?.length ? qs({ keys: keys.join(",") }) : "")
    ),
  /**
   * The bytes as a blob URL an `<audio>` element can play.
   *
   * **NOT A PLAIN URL, AND THE FIRST VERSION WAS.** The endpoint is
   * `require_role(ROLE_DEPT_ADMIN)`, and an `<audio src="...">` is a
   * bare browser request: it carries no `Authorization` header and
   * cannot be made to. So the listing worked — that goes through
   * `api()` with the bearer token — while pressing play produced
   * `401 Unauthorized`, which reads as a broken file rather than a
   * missing credential.
   *
   * `apiBlob` is the existing authenticated binary GET (same header,
   * same one-shot 401-refresh) used for CSV exports and the PDF
   * preview. The caller must `URL.revokeObjectURL` what it gets back;
   * a blob URL is a reference the page holds until it does.
   */
  blobUrl: async (key: string): Promise<string> => {
    const blob = await apiBlob(
      `/api/v1/prompt-audio/${encodeURIComponent(key)}`
    );
    return URL.createObjectURL(blob);
  },
  /** dept_admin+ — replace one recording. Send a 24 kHz/16-bit/mono WAV;
   *  `lib/promptAudio.toPromptWav` converts anything the browser can
   *  decode into one. The server re-checks regardless. */
  put: (key: string, wav: Blob) => {
    const form = new FormData();
    form.append("file", wav, `${key}.wav`);
    return apiUpload<PromptAudio>(
      `/api/v1/prompt-audio/${encodeURIComponent(key)}`,
      form,
      { method: "PUT" }
    );
  },
  /** dept_admin+ — remove it, so the prompt's TEXT is spoken again.
   *  The one-second fix for a prompt that is wrong. */
  remove: (key: string) =>
    api<PromptAudio>(`/api/v1/prompt-audio/${encodeURIComponent(key)}`, {
      method: "DELETE",
    }),
};

export const telephonyApi = {
  /** dept_admin+ — provisioned numbers the caller may see, org-scoped
   *  exactly as /channels is, each with its routing health. */
  numbers: () => api<PhoneNumber[]>("/api/v1/telephony/numbers"),
  /** dept_admin+ — "if somebody dialled this right now, who would
   *  answer?". A number in another organization returns the SAME 404 an
   *  unprovisioned number does, so this is not an oracle for other
   *  tenants' DIDs. */
  resolve: (dialled_number: string) =>
    api<TelephonyResolveResult>("/api/v1/telephony/resolve", {
      method: "POST",
      body: { dialled_number },
    }),
  /** org_admin+ — a LiveKit access token for ONE room, short-lived
   *  (5 minutes by default). Grants roomJoin and nothing administrative.
   *
   *  `dispatch` (V0) adds a `roomConfig` claim naming the voice worker
   *  plus that worker's job metadata. It adds NO video grant — there is
   *  still no roomCreate/roomAdmin/roomList/roomRecord — and it only
   *  takes effect if this join is the call that CREATES the room, so the
   *  room name must be one nobody has used. */
  token: (body: {
    room: string;
    identity?: string;
    name?: string;
    organization_id?: string;
    ttl_seconds?: number;
    can_publish?: boolean;
    can_subscribe?: boolean;
    dispatch?: TestCallDispatch;
  }) => api<LiveKitRoomToken>("/api/v1/telephony/token", { method: "POST", body }),
  /** org_admin+ — one organization's project. Omit `organizationId` for
   *  the one you are acting as; naming another → 403 (super_admin may). */
  credentials: (organizationId?: string) =>
    api<LiveKitCredential>("/api/v1/telephony/credentials" + qs({ organization_id: organizationId })),
  saveCredentials: (body: LiveKitCredentialBody, organizationId?: string) =>
    api<LiveKitCredential>(
      "/api/v1/telephony/credentials" + qs({ organization_id: organizationId }),
      { method: "PUT", body },
    ),
  /** 204 whether or not a row was there — the org falls back to the
   *  platform's environment project. */
  removeCredentials: (organizationId?: string) =>
    api("/api/v1/telephony/credentials" + qs({ organization_id: organizationId }), {
      method: "DELETE",
    }),
};

// ── Chat flows — the operator's test conversation ──────────────────────
//
// The Voice Test Call page's twin for the OTHER engine. `telephonyApi`
// above mints a token so a call flow can be heard without a DID; this
// walks a CHAT flow one message at a time without WhatsApp, without a
// channel and without a customer.
//
// THE STATE LIVES HERE, IN THE BROWSER. The server keeps nothing: no
// conversation, no ChannelSession, no `flow_state` column. Whatever
// `state` a response carries is handed back UNCHANGED on the next call,
// and that is the only thing continuing the conversation. It is opaque —
// the interpreter refuses a mutated blob — so it must not be read,
// trimmed or prettified on the way past. `vars` is the readable copy, and
// it is where the masking happens.

/** One message the operator is pretending a customer sent. */
export interface ChatFlowTestMessage {
  text: string;
  /** "text" | "voice". The engine treats them differently — see the view. */
  kind?: string;
  /** The channel's media handle. Only meaningful for `kind: "voice"`, and
   *  nothing downloads it here; it travels so a graph that branches on
   *  its presence sees what it would see in production. */
  media_id?: string;
  /** The second path a real voice note takes: the channel tried to
   *  transcribe it and could not. */
  transcription_failed?: boolean;
}

/** WHICH question the flow is parked on, derived server-side from the
 *  state blob plus that node's config — so the page never parses a graph. */
export interface ChatFlowAwaiting {
  node_id: string;
  node_name: string;
  /** "text" | "choice" | "digits". */
  expect: string;
  /** The var this question lands in. */
  assign: string;
  attempt: number;
  max_retries: number;
  /** True when the node is marked sensitive: a voice note will be REFUSED
   *  here, and whatever it collects comes back masked in `vars`. */
  sensitive?: boolean;
}

export interface ChatFlowTestResult {
  workflow: { id: string; name: string; version: number };
  /** What the flow said this turn, in order — ONE ENTRY PER NODE that
   *  said something. This is the record of who said what, not what the
   *  customer receives; see `delivered`. */
  messages: string[];
  /** **WHAT WHATSAPP ACTUALLY RECEIVES: one message.** `channels.py`
   *  joins a turn's lines with a blank line and sends a single body,
   *  because two pings for one turn of a conversation is worse than two
   *  paragraphs. Joined SERVER-SIDE by the channel's own function, so
   *  this page cannot drift from the delivery rule. */
  delivered: string;
  /** OPAQUE. Hand it back verbatim; `null` means the conversation ended. */
  state: Record<string, unknown> | null;
  /** R1 — OPAQUE, like `state`. The `workflow_runs` row this rehearsal is
   *  being recorded into, so the whole test conversation lands on ONE run
   *  in Recent runs instead of one per message. Hand it back on the next
   *  call. `null` means the run is closed (the flow finished) or none
   *  could be opened — never a reason to stop the conversation. */
  run_id: string | null;
  finished: boolean;
  /** "awaiting" | "end_chat" | "ran_out" | "handoff" | "error". */
  ending: string;
  /** The handoff note, rendered, when `ending === "handoff"`. */
  note: string;
  /** Non-empty only for `ending === "error"`, and it says what went wrong. */
  error: string;
  /** The collected variables, with SENSITIVE ONES MASKED by the server.
   *  A debugging surface is not a reason to put a CNIC in a browser's
   *  memory; `<assign>_source` and `<assign>_label` survive, because
   *  metadata about the answer is not the answer. */
  vars: Record<string, unknown>;
  /** The trace of the walk, node by node — the debugging half. */
  spans: Record<string, unknown>[];
  awaiting: ChatFlowAwaiting | null;
  /** What the BACKEND heard, when a clip was attached to this turn and
   *  the interpreter actually asked for it. A report, never a gate: it is
   *  filled in after the turn has already been decided, so the page shows
   *  it and cannot act on it — which is the point, because a customer
   *  cannot inspect a transcript before their message is answered.
   *
   *  `null` on a text turn, on a voice turn sent without audio, and — the
   *  interesting one — on a voice note sent to a `sensitive` node, where
   *  the engine refuses the audio BEFORE the transcriber is reached and
   *  there is nothing to report but the refusal. */
  heard: ChatFlowHeard | null;
}

/** One browser recording, run through the REAL speech provider.
 *
 *  Empty `text` with a populated `detail` is a NORMAL answer and not an
 *  error: no provider key elected for this organization, an elected
 *  provider with no speech implementation, a provider that failed. The
 *  page prints `detail` where the transcript would have gone, because
 *  "it silently did nothing" is the failure this page exists to prevent. */
export interface ChatFlowAgentTurn {
  reply: string;
  conversation_id: string | null;
  agent_id: string;
  agent_name: string;
  model: string | null;
  latency_ms: number;
  /** Empty on success; a whole sentence, safe to show, when there is no
   *  reply. Each cause has a different fix, so they are not collapsed. */
  detail: string;
}

export interface ChatAgentOption {
  id: string;
  name: string;
  /** Whether a channel tells this agent the sender's WhatsApp number. */
  shares_sender_number: boolean;
}

export interface ChatFlowTranscription {
  text: string;
  /** The provider type that answered ("openai"), or "" when none did. */
  provider: string;
  model: string;
  /** How long the PROVIDER CALL took. Not the length of the audio — the
   *  page already timed that itself while recording. */
  duration_ms: number;
  bytes: number;
  mime: string;
  /** Empty on success; a whole sentence explaining the silence otherwise. */
  detail: string;
}

/** What the backend heard INSIDE a turn — structurally the same thing
 *  `/chat-flows/transcribe` reports, and an alias rather than a second
 *  interface on purpose: one type means the page has one formatter, so
 *  the debug line under a recorded turn and the line under the review
 *  mode's preview read identically and an operator comparing the two is
 *  comparing the numbers rather than the labels. */
export type ChatFlowHeard = ChatFlowTranscription;

export const chatFlowsApi = {
  /** org_admin+ (surface `chat_flow_test`) — walk ONE turn of a chat
   *  workflow. Omit `state` (or `message`) to START.
   *
   *  PASS `audio` AND THE TURN CARRIES THE VOICE NOTE ITSELF, which is
   *  what WhatsApp does: the body goes up in a `payload` part beside an
   *  `audio` part, and the INTERPRETER transcribes the clip inside the
   *  turn. That is the only shape in which the flow's own transcription
   *  step runs — a `message.text` filled in beforehand makes the engine
   *  return before it consults its transcriber, so a page that
   *  transcribed first and posted the words was testing a path that does
   *  not exist in production. `message.text` is IGNORED by the server
   *  when a clip is attached, for exactly that reason.
   *
   *  413 when the clip is over `VOICE_NOTE_MAX_BYTES` — the same cap and
   *  the same sentence `transcribe` gives.
   *
   *  422 when the workflow has no active version or is not chat-sourced —
   *  the `detail.reason` says which; 404 when there is no such workflow in
   *  the named organization, which is also what another tenant's workflow
   *  answers. */
  test: (
    body: {
      workflow_id: string;
      organization_id?: string | null;
      /** WHO THE PAGE IS PRETENDING TO BE, as a WhatsApp number —
       *  `messages[0].from` off the webhook, e.g. "923121228830". Lands
       *  in `vars.sender`. Empty falls back to the caller's own user id,
       *  which is what this always sent and is fine for a graph that
       *  only renders the sender; it is NOT fine for one that branches
       *  on it, like ELFA's intake, which asks whether the number is on
       *  file and skips the CNIC question when it is. */
      sender?: string | null;
      state?: Record<string, unknown> | null;
      /** R1 — echo `run_id` from the last response, verbatim, so every
       *  turn of one test conversation is recorded on one run. Checked
       *  server-side against the workflow and the organization, so a
       *  stale tab starts a fresh run rather than failing. */
      run_id?: string | null;
      message?: ChatFlowTestMessage | null;
      /** BCP-47-ish code a voice note on this turn is decoded as. Empty
       *  falls back to `TRANSCRIPTION_LANGUAGE`, and both empty is
       *  auto-detect. The page is channel-less, so this is the only way
       *  to reproduce a channel configured for one language. */
      transcription_language?: string | null;
    },
    audio?: Blob | null
  ) => {
    if (!audio) return api<ChatFlowTestResult>("/api/v1/chat-flows/test", { method: "POST", body });
    const form = new FormData();
    // ONE part for the whole body rather than flat form fields: `state`
    // is a nested blob that has to round-trip byte for byte, so it would
    // have to be JSON-in-a-string in a flat form anyway, and one part
    // keeps one schema validating both content types on the server.
    form.append("payload", JSON.stringify(body));
    form.append("audio", audio, `recording.${audioExtension(audio)}`);
    return apiUpload<ChatFlowTestResult>("/api/v1/chat-flows/test", form);
  },
  /** org_admin+ — transcribe one recording ON ITS OWN, with no turn and
   *  no graph. Multipart, and the audio is NOT stored server-side even
   *  when `VOICE_NOTE_AUDIO_DIR` is set.
   *
   *  This is NOT how the test page sends a voice note. It answers the
   *  narrower question "what does this organization's speech provider
   *  hear when I say this?", and backs the page's demoted "record, then
   *  review" mode — where the operator corrects a mis-heard word and
   *  sends the corrected TEXT, which does not exercise the flow's own
   *  transcription step. Use `test(body, blob)` for the production path.
   *
   *  200 for every provider outcome (see `ChatFlowTranscription.detail`);
   *  413 when the blob is over `VOICE_NOTE_MAX_BYTES`; 403 from the same
   *  role and tenant gates `test` has. */
  /** dept_admin+ — ONE TURN AFTER THE FLOW HANDED OFF, run by the agent
   *  the workflow's channel names.
   *
   *  This spends model tokens and records an AgentRun, exactly as a
   *  production turn does — the page already billed LLM calls through the
   *  graph's classifier tool, so what is new is the agent, not the cost.
   *
   *  200 for every outcome; `detail` carries the sentence when there is no
   *  reply (no channel binds this workflow, its channels disagree about
   *  the agent, the agent is unpublished, the run returned nothing). */
  agentTurn: (body: {
    workflow_id: string;
    organization_id?: string | null;
    message: string;
    handoff_note?: string;
    conversation_id?: string | null;
  }) => api<ChatFlowAgentTurn>("/api/v1/chat-flows/agent-turn", { method: "POST", body }),
  /** dept_admin+ — the organization's published agents that could answer a
   *  chat channel with no flow, for the "AI agent" tester. */
  agents: (organizationId?: string | null) =>
    api<ChatAgentOption[]>(
      `/api/v1/chat-flows/agents${organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ""}`),
  /** dept_admin+ — ONE TURN WITH AN AI AGENT AND NO FLOW, as a channel with
   *  no workflow runs it. `sender` is the pretend WhatsApp number; the agent
   *  is told it only if its tools_config sets `share_sender_number`, exactly
   *  as on a real channel. Spends model tokens and records an AgentRun; the
   *  agent's tools run for real. */
  agentTest: (body: {
    agent_id: string;
    organization_id?: string | null;
    message: string;
    sender?: string;
    conversation_id?: string | null;
  }) => api<ChatFlowAgentTurn>("/api/v1/chat-flows/agent-test", { method: "POST", body }),
  transcribe: (blob: Blob, organizationId?: string | null, language?: string | null) => {
    const form = new FormData();
    form.append("audio", blob, `recording.${audioExtension(blob)}`);
    if (organizationId) form.append("organization_id", organizationId);
    if (language) form.append("transcription_language", language);
    return apiUpload<ChatFlowTranscription>("/api/v1/chat-flows/transcribe", form);
  },
};

/** The browser's container as a file extension. The filename matters:
 *  the OpenAI audio API sniffs the container from the extension, so a
 *  constant "voice.ogg" would mislabel a webm recording — and both voice
 *  paths above have to label it the same way or they are testing two
 *  different uploads. */
function audioExtension(blob: Blob): string {
  const type = blob.type || "";
  if (type.includes("ogg")) return "ogg";
  if (type.includes("mp4")) return "mp4";
  return "webm";
}

// ── Admin backend console (§2.1) ────────────────────────────────────
//
// super_admin only. Everything under `process`, `registry`, `catalogue`,
// `scheduler`, `routers` and `log_buffer` is ONE WORKER's memory —
// `scope` says which, and `boot_id` says whose. A client that polls must
// compare `boot_id` between responses: if it changes, it was answered by
// a different worker and `seq` cursors from the old one are meaningless.

export type ConsoleScope = "worker" | "deployment";

export interface ConsoleProcess {
  scope: ConsoleScope;
  boot_id: string;
  pid: number;
  started_at: string;
  uptime_seconds: number;
  note: string;
}

export interface ConsoleSchema {
  scope: ConsoleScope;
  /** Head revision in backend/alembic/versions. */
  script_head: string | null;
  /** What `alembic_version` in the live database says. */
  database_revision: string | null;
  /** null = the database has never been stamped (not the same as "in sync"). */
  revision_in_sync: boolean | null;
  missing_tables: string[];
  missing_columns: string[];
  extra_tables: string[];
  drift_checked: boolean;
  has_drift?: boolean;
  error?: string | null;
}

export interface ConsoleRegistry {
  scope: ConsoleScope;
  agents: number;
  published: number;
  masters: number;
  voice_enabled: number;
  with_tools: number;
}

export interface ConsoleScheduler {
  scope: ConsoleScope;
  enabled: boolean;
  running: boolean | null;
  task_present: boolean;
  note: string;
}

export interface ConsoleProviderType {
  type: string;
  providers: number;
  active_providers: number;
  keys: number;
  unreadable_keys: number;
  env_fallback: boolean;
  env_var?: string | null;
}

export interface ConsoleHealth {
  version: string;
  app_name: string;
  debug: boolean;
  database: string;
  note: string;
  process: ConsoleProcess;
  schema: ConsoleSchema;
  registry: ConsoleRegistry;
  catalogue: { scope: ConsoleScope; prices_loaded: number };
  scheduler: ConsoleScheduler;
  providers: {
    scope: ConsoleScope;
    types: ConsoleProviderType[];
    unreadable_keys: number;
    unreadable_key_ids: string[];
  };
  routers: {
    scope: ConsoleScope;
    available: boolean;
    total?: number;
    mounted: string[];
    failed: { label: string; error: string }[];
  };
  log_buffer: {
    scope: ConsoleScope;
    installed: boolean;
    capacity?: number;
    size?: number;
    dropped?: number;
    total_captured?: number;
    newest_seq?: number;
    min_level?: string;
    by_level?: Record<string, number>;
  };
}

export interface ConsoleLogRecord {
  seq: number;
  ts: string;
  level: string;
  levelno: number;
  logger: string;
  message: string;
  module: string;
  line: number;
  thread: string;
  traceback: string | null;
}

export interface ConsoleLogPage {
  boot_id: string;
  installed: boolean;
  records: ConsoleLogRecord[];
  stats: Record<string, any>;
}

/** Every refresh action answers with WHAT IT DID, in `summary`. */
export interface ConsoleRefreshResult {
  action: string;
  summary: string;
  duration_ms?: number;
  [k: string]: any;
}

// ── Request/response body capture ────────────────────────────────────
//
// Opt-in, auto-expiring, in-memory and bounded — every one of those is a
// property of `app/services/request_capture.py`, not of this file. The
// only thing the client contributes is the countdown, and even that is
// display: the server re-checks its own deadline on every poll, so a tab
// left open cannot keep a window alive and a tab that is closed cannot
// leave one running.

/** One header or query parameter. `value` is a length when `redacted`. */
export interface CaptureField {
  name: string;
  value: string;
  redacted: boolean;
}

/** One side of an exchange. `text` is raw unless `binary` or `truncated`. */
export interface CaptureBody {
  text: string;
  bytes: number;
  truncated: boolean;
  binary: boolean;
  content_type: string;
  headers: CaptureField[];
}

export interface CaptureRecord {
  seq: number;
  ts: string;
  method: string;
  path: string;
  query: CaptureField[];
  status: number;
  duration_ms: number;
  client: string;
  request: CaptureBody;
  response: CaptureBody;
  note: string | null;
  /** `"outbound"` for a call the PLATFORM made — today that is the POST
   *  to graph.facebook.com that delivers a reply. Absent on an inbound
   *  request, which is every other row. Without it the two legs look
   *  identical in the table and "did the message reach the handset" is
   *  still unanswerable, which is the whole reason the outbound half
   *  was added. */
  direction?: string;
}

/** The window itself — armed or not, and for how much longer. */
export interface CaptureState {
  armed: boolean;
  generation: number;
  scope: string;
  scope_label: string;
  scope_note: string;
  scope_wide: boolean;
  window_minutes: number;
  armed_at: string | null;
  expires_at: string | null;
  remaining_seconds: number;
  never_captured: string[];
  worker_note: string;
  boot_id: string;
}

export interface CaptureScopeOption {
  value: string;
  label: string;
  note: string;
  wide: boolean;
}

export interface ConsoleCapturePage {
  boot_id: string;
  capture: CaptureState;
  records: CaptureRecord[];
  stats: Record<string, any>;
  durations: number[];
  scopes: CaptureScopeOption[];
}

export const adminConsoleApi = {
  health: () => api<ConsoleHealth>("/api/v1/admin/console/health"),
  logs: (params: {
    after_seq?: number;
    level?: string;
    logger_prefix?: string;
    limit?: number;
  } = {}) => api<ConsoleLogPage>("/api/v1/admin/console/logs" + qs(params)),
  clearLogs: () =>
    api<{ cleared: number; boot_id: string }>("/api/v1/admin/console/logs", {
      method: "DELETE",
    }),
  captures: (params: { after_seq?: number; limit?: number } = {}) =>
    api<ConsoleCapturePage>("/api/v1/admin/console/captures" + qs(params)),
  armCaptures: (body: { minutes: number; scope: string; confirm_wide?: boolean }) =>
    api<ConsoleCapturePage>("/api/v1/admin/console/captures/arm", {
      method: "POST",
      body,
    }),
  disarmCaptures: () =>
    api<ConsoleCapturePage>("/api/v1/admin/console/captures/disarm", {
      method: "POST",
    }),
  clearCaptures: () =>
    api<{ cleared: number; boot_id: string; capture: CaptureState }>(
      "/api/v1/admin/console/captures",
      { method: "DELETE" }
    ),
  refreshRegistry: () =>
    api<ConsoleRefreshResult>("/api/v1/admin/console/refresh/registry", {
      method: "POST",
    }),
  refreshCatalogue: () =>
    api<ConsoleRefreshResult>("/api/v1/admin/console/refresh/catalogue", {
      method: "POST",
    }),
  refreshSettings: () =>
    api<ConsoleRefreshResult>("/api/v1/admin/console/refresh/settings", {
      method: "POST",
    }),
};

// ── Role Mapping — the surface/role matrix, edited in the product ────
//
// The server's `app/services/surface_policy.py` is still the only table.
// This is CRUD over its overrides; nothing here re-decides anything, and
// in particular `gate` below is DERIVED on the server by walking
// FastAPI's mounted routes. It exists because ticking a cell grants
// REACH and not capability: opening `agents` to dept_admin lets them
// open the page, and `require_role()` in agents_admin.py still refuses
// them the create button. A matrix that did not say so would be worse
// than no matrix.

/** One endpoint's own gate, read out of its dependency tree. */
export interface RoleMappingRouteGate {
  method: string;
  path: string;
  /** `null` = no role gate at all; the endpoint only wants a signed-in caller. */
  floor: Role | null;
  /** The raw `require_role(...)` tuples, so `floor` can be checked, not trusted. */
  requires: string[][];
  checks: string[];
  undetermined: string[];
}

/**
 * R4 — would a page load for somebody at this access level?
 *
 * `loads` every index read answers · `partial` some do · `dead` none do,
 * so the page 403s the moment it opens · `no_api` the surface has no GET
 * of its own, so there is no load to fail.
 */
export type PageLoadVerdict = "loads" | "partial" | "dead" | "no_api";

export interface PageLoadOutcome {
  verdict: PageLoadVerdict;
  reads_total: number;
  reads_refused: number;
  refused: string[];
  blocking_floor: Role | null;
}

export interface RoleMappingGate {
  summary: string;
  route_count: number;
  floor_counts: Record<string, number>;
  strictest_floor: Role | null;
  weakest_floor: Role | null;
  check_counts: Record<string, number>;
  /** Endpoints the server could NOT read automatically. Shown, not guessed. */
  undetermined: string[];
  routes: RoleMappingRouteGate[];
  /**
   * R4 — the other half of "still requires", per access level. R3 was
   * told to leave this column alone and recommended one clause be added
   * to it; two dead pages later it says the whole thing. Ticking `member`
   * against a row whose reads are `require_role(dept_admin)` opens a page
   * that 403s on load, and until now nothing said so anywhere.
   */
  page_loads: Record<string, PageLoadOutcome>;
  load_note: string;
  /** How many of `route_count` actually run when the page loads. */
  read_count: number;
}

/** R4 — a grant judged against ONE role's current holders. */
export type GrantVerdict = "all" | "some" | "none" | "no_holders";

export interface RoleMappingSurfaceOutcome {
  verdict: GrantVerdict;
  /** Access levels the page loads for… */
  loads_for: Role[];
  /** …and the ones it is a dead page for. */
  dead_for: Role[];
  partial_for: Role[];
  /** The strictest floor among the reads a page load makes. */
  read_floor: Role | null;
  reads_total: number;
  sentence: string;
  /** Already open to every access level, so the tick adds no reach. */
  adds_nothing: boolean;
}

export interface RoleMappingSurface {
  surface: string;
  roles: Role[];
  default_roles: Role[];
  is_override: boolean;
  differs_from_default: boolean;
  /** Pinned surfaces cannot be edited in either direction — see reason. */
  pinned: boolean;
  pinned_reason: string | null;
  /**
   * R2b — this ACCESS-LEVEL row is still editable; what it cannot be is
   * given to a department role, because every endpoint behind it
   * requires super_admin. Derived, never hand-maintained.
   */
  undelegatable: boolean;
  undelegatable_reason: string | null;
  /** Not a refusal — see `grantable_surfaces[].caveat`. */
  delegation_caveat: string | null;
  is_page: boolean;
  page_path: string | null;
  updated_at: string | null;
  gate: RoleMappingGate;
}

export interface RoleMappingMatrix {
  roles: Role[];
  surfaces: RoleMappingSurface[];
  reach_note: string;
  /** The SECOND axis — not expressible as a tick in the matrix. */
  usage_self_only: {
    roles: Role[];
    default_roles: Role[];
    is_override: boolean;
    note: string;
  };
  cache: {
    generation: number;
    loaded: boolean;
    age_seconds: number | null;
    ttl_seconds: number;
    overrides: Record<string, string[]>;
    usage_self_scoped_roles: string[];
    usage_self_scoped_is_override: boolean;
  };
  /** R2 — one row per DEPARTMENT ROLE, each mapped individually. */
  department_roles: RoleMappingDepartmentRole[];
  role_grant_note: string;
  /** What the /usage second axis means for a role. (Nothing — see note.) */
  role_usage_note: string;
  /** R4 — the rule nothing stated: a grant works only where the HOLDERS'
   *  access levels already clear the endpoint floors. */
  holder_fit_note: string;
  grantable_surfaces: {
    surface: string;
    page_path: string | null;
    is_page: boolean;
    /** Already open to every access level, so granting it adds nothing. */
    open_to_every_access_level: boolean;
    /**
     * R2b — not a refusal. The two cases the server's derivation cannot
     * settle either way: a surface with no API of its own, and one with
     * endpoints it could not read. Shown next to the option rather than
     * swallowed.
     */
    caveat: string | null;
  }[];
  /**
   * R2b — every surface a DEPARTMENT ROLE may not be given, with why.
   * `pinned` is the pre-existing four; `undelegatable` is a surface
   * every endpoint of which requires super_admin, so the tick could only
   * ever open a page that 403s on load. Rendered as DISABLED options,
   * not omitted: an operator who cannot find `backend_console` in the
   * list concludes the list is broken and learns nothing.
   */
  ungrantable_surfaces: {
    surface: string;
    kind: "pinned" | "undelegatable";
    reason: string | null;
    page_path: string | null;
    is_page: boolean;
  }[];
  undelegatable_note: string;
  process: Record<string, any>;
  worker_scope_note: string;
  multi_worker_note: string;
}

/**
 * One department role and what holding it ADDS.
 *
 * The `grant_summary` and the per-surface `refused_for_weakest_holder`
 * counts are DERIVED on the server from FastAPI's dependency trees, the
 * same walk that feeds `RoleMappingGate`. They are the honest half of a
 * role grant and they matter more here than on an access-level row: a
 * grant does not change anybody's access level, so a member holding a
 * role mapped to `organizations` opens the page and is refused by every
 * write on it. The page must say so, per role.
 */
export interface RoleMappingDepartmentRole {
  role_id: string;
  name: string;
  slug: string;
  /** "Executive / CFO" — department first, because the pair is the identity. */
  label: string;
  department_id: string;
  department_name: string | null;
  organization_id: string;
  is_active: boolean;
  /** The EFFECTIVE grant: what the policy will actually honour. */
  surfaces: string[];
  /** What the row literally holds… */
  stored_surfaces: string[];
  /**
   * …minus what the policy dropped (pinned, undelegatable or unknown).
   * NOT normally empty any more: `backend_console` was tickable here
   * until R2b, so a database seeded before it can hold a row that has
   * since stopped granting, and this is where the page says so.
   */
  ignored_surfaces: string[];
  /** …and why each one is ignored, so it reads as a decision not a bug. */
  ignored_surface_reasons: {
    surface: string;
    kind: "pinned" | "undelegatable" | "unknown";
    reason: string;
  }[];
  is_mapped: boolean;
  updated_at: string | null;
  holder_count: number;
  holders: {
    user_id: string;
    name: string;
    email: string;
    access_level: Role;
    is_active: boolean;
  }[];
  weakest_holder_access_level: Role | null;
  grant_summary: string;
  grants: {
    surface: string;
    page_path: string | null;
    route_count: number;
    strictest_floor: Role | null;
    refused_for_weakest_holder: number;
    summary: string;
    /** R4 — does the page LOAD for these holders, and for which of them? */
    outcome: GrantVerdict;
    loads_for: Role[];
    dead_for: Role[];
    read_floor: Role | null;
    outcome_sentence: string;
  }[];
  /**
   * R4 — every GRANTABLE surface judged against THIS role's holders, so
   * the picker can label an option before it is ticked. `grantable_surfaces`
   * on the matrix is global (delegability is a property of the surface);
   * whether a grant will WORK is a property of the holders, so it can only
   * live on the role.
   */
  surface_outcomes: Record<string, RoleMappingSurfaceOutcome>;
  /** Granted surfaces that are a dead page for EVERY current holder. */
  dead_grants: string[];
  /** …and ones that work for some holders and break when they leave. */
  fragile_grants: string[];
}

export interface RoleMappingRoleChangeResult {
  role_id: string;
  role_label: string;
  action: string;
  surfaces: string[];
  is_mapped: boolean;
  holder_count: number;
  reloaded: Record<string, any>;
  audit_id: string | null;
}

export interface RoleMappingChangeResult {
  surface: string;
  action: string;
  roles: Role[];
  is_override: boolean;
  self_scoped_roles: Role[] | null;
  reloaded: Record<string, any>;
  audit_id: string | null;
}

export interface RoleMappingAuditEntry {
  id: string;
  action: string;
  /** NULL on a department-role event — it is about a role, not a surface. */
  surface: string | null;
  old_roles: string[] | null;
  new_roles: string[] | null;
  old_self_scoped: string[] | null;
  new_self_scoped: string[] | null;
  /** R2 — set on role_create / role_map / role_assign / … */
  department_role_id: string | null;
  /** "Executive / CFO", frozen at the time: roles get renamed. */
  department_role_label: string | null;
  old_surfaces: string[] | null;
  new_surfaces: string[] | null;
  /** Who a grant landed on (role_assign / role_unassign only). */
  subject_user_id: string | null;
  subject_user_email: string | null;
  changed_by: string | null;
  changed_by_email: string | null;
  changed_at: string | null;
}

export const roleMappingApi = {
  matrix: () => api<RoleMappingMatrix>("/api/v1/admin/role-mapping"),
  /**
   * Replace one surface's role set. `confirm` is REQUIRED by the server
   * when the change takes a role away — a 409 comes back naming who
   * would lose the page, and the caller is expected to ask a human
   * before re-sending. Widening needs no confirmation.
   */
  set: (
    surface: string,
    body: { roles: string[]; self_scoped_roles?: string[] | null; confirm?: boolean }
  ) =>
    api<RoleMappingChangeResult>(
      `/api/v1/admin/role-mapping/${encodeURIComponent(surface)}`,
      { method: "PUT", body }
    ),
  reset: (surface: string, confirm = false) =>
    api<RoleMappingChangeResult>(
      `/api/v1/admin/role-mapping/${encodeURIComponent(surface)}/reset` +
        qs({ confirm: String(confirm) }),
      { method: "POST" }
    ),
  resetAll: (confirm = false) =>
    api<{ reset: string[]; reloaded: Record<string, any> }>(
      "/api/v1/admin/role-mapping/reset" + qs({ confirm: String(confirm) }),
      { method: "POST" }
    ),
  /**
   * R2 — replace one DEPARTMENT ROLE's surface set. Grant-only: adding
   * needs no confirmation because nobody is locked out by being given
   * something. Removing comes back 409 naming the holders who lose the
   * page, exactly like the access-level half.
   */
  setRole: (roleId: string, body: { surfaces: string[]; confirm?: boolean }) =>
    api<RoleMappingRoleChangeResult>(
      `/api/v1/admin/role-mapping/roles/${encodeURIComponent(roleId)}`,
      { method: "PUT", body }
    ),
  /** Delete the mapping row — the role goes back to granting nothing. */
  resetRole: (roleId: string, confirm = false) =>
    api<RoleMappingRoleChangeResult>(
      `/api/v1/admin/role-mapping/roles/${encodeURIComponent(roleId)}/reset` +
        qs({ confirm: String(confirm) }),
      { method: "POST" }
    ),
  audit: (
    params: {
      limit?: number;
      offset?: number;
      surface?: string;
      department_role_id?: string;
      /** "How did THIS person get here" — one query. */
      subject_user_id?: string;
    } = {}
  ) =>
    api<{ total: number; entries: RoleMappingAuditEntry[] }>(
      "/api/v1/admin/role-mapping/audit" + qs(params)
    ),
  /** Force THIS worker to re-read the override table. See multi_worker_note. */
  reload: () =>
    api<Record<string, any>>("/api/v1/admin/role-mapping/reload", {
      method: "POST",
    }),
};

// ── Ask AI — form & workflow builder (§2.3 phase a) ──────────────────
//
// One extraction job = one uploaded PDF + the draft schema a human is
// correcting. `draft` is a real FormSchema with a `meta` sidecar on every
// section and field, so phase (b) inherits a schema rather than a
// translation problem.

/** Where an extracted item was read from. `match: "none"` means the
 *  model's quote is nowhere in the PDF — i.e. it may have invented it. */
export interface ExtractionProvenance {
  page: number | null;
  bbox: number[] | null;
  page_width: number | null;
  page_height: number | null;
  snippet: string;
  match: "exact" | "fuzzy" | "none";
  score: number;
}

export interface ExtractionFlag {
  code: string;
  message: string;
}

export type ExtractionReviewState = "proposed" | "confirmed" | "edited" | "added";

/** How many independent readings of the PDF found this item, and what
 *  they disagreed about. Written by the (b) reconciler. */
export interface ExtractionAgreement {
  passes_seen: number;
  passes_total: number;
  disagreed: string[];
}

export interface ExtractionMeta {
  confidence: number;
  provenance: ExtractionProvenance | null;
  flags: ExtractionFlag[];
  review_state: ExtractionReviewState;
  anchor: string;
  model_note: string;
  agreement?: ExtractionAgreement;
  /** Set when the interview split this section out of another one. */
  split_from?: string;
}

export interface ExtractedField {
  key: string;
  label: string;
  type: string;
  required: boolean;
  default: any;
  help: string;
  placeholder: string;
  options: string[];
  formula?: string;
  meta: ExtractionMeta;
}

export interface ExtractedSection {
  key: string;
  title: string;
  description: string;
  agent_writable: boolean;
  requester_access: "hidden" | "view" | "edit";
  /** Empty until phase (b)'s interview answers the section's matrix. */
  permissions: FormPermissionRule[];
  fields: ExtractedField[];
  meta: ExtractionMeta;
}

export interface FormPermissionRule {
  department_id: string | null;
  role: string | null;
  access: "hidden" | "view" | "edit";
}

/** A repeating structure the LAYOUT found — tick-box groups, numbered
 *  tables, signature blocks, and lines the document prints twice. */
export interface ExtractionRepeatBlock {
  kind: "box_group" | "enumerated_rows" | "signature_block" | "repeated_line";
  page: number;
  line: number;
  bbox: number[];
  text: string;
  count: number;
  labels?: string[];
  rows?: string[];
  party?: string;
  column?: string;
  prefix?: string;
  in_table?: boolean;
  printed?: number;
  covered?: number;
  collapsed?: boolean;
  matched_fields?: { section: string; field: string; label: string; type: string }[];
}

export interface ExtractionDraft {
  version: number;
  form: { name: string; description: string };
  sections: ExtractedSection[];
  warnings: ExtractionFlag[];
  schema_issues: string[];
  stats: {
    sections: number;
    fields: number;
    field_types: Record<string, number>;
    mean_confidence: number;
    low_confidence: number;
    unverified: number;
    needs_review: number;
    reviewed: number;
    flagged: number;
    passes?: number;
    single_pass?: number;
    disagreements?: number;
  };
  source?: {
    filename: string;
    page_count: number;
    char_count: number;
    passes?: number;
  };
  repeat_blocks?: ExtractionRepeatBlock[];
  splits?: { from: string; into: string[] }[];
}

// ── Phase (b): the interview ────────────────────────────────────────

export type InterviewQuestionKind =
  | "repeated_block"
  | "section_split"
  | "permissions"
  | "required"
  | "computed_field"
  | "open_question"
  | "use_case"
  | "process";

export interface InterviewPermissionAnswer {
  editors: string[];
  editor_role?: string | null;
  viewers?: string[] | "__all_other_departments__";
  hidden_from?: string[];
  requester_access?: "hidden" | "view" | "edit";
  _why?: string;
}

export interface InterviewSplitPart {
  key: string;
  title: string;
  description?: string;
  fields: string[];
  anchor?: { page: number | null; line: number | null };
  requester_access?: "hidden" | "view" | "edit";
  permissions?: InterviewPermissionAnswer | null;
  party?: string;
  column?: string;
}

export interface InterviewQuestion {
  id: string;
  kind: InterviewQuestionKind;
  blocking: boolean;
  title: string;
  prompt: string;
  why?: string;
  section_key?: string;
  provenance?: ExtractionProvenance | null;
  choices?: { id: string; name: string }[];
  roles?: string[];
  access_levels?: string[];
  options?: string[];
  labels?: string[];
  targets?: { section: string; field: string; label: string }[];
  fields?: {
    section?: string;
    field?: string;
    key?: string;
    label: string;
    section_title?: string;
    required?: boolean;
    uncertain?: boolean;
  }[];
  /** (c) a permission question doubles as the place to say "this section
   *  has two writers" when the page prints no signature block to anchor
   *  a split on. */
  splittable?: boolean;
  /** (c) computed_field: the fields a formula may reference. */
  sections?: {
    key: string;
    title: string;
    fields: { key: string; label: string; type: string }[];
  }[];
  grammar?: string;
  suggested?:
    | InterviewPermissionAnswer
    | { split: boolean; parts: InterviewSplitPart[] }
    | { fields: InterviewComputedField[] }
    | null;
}

export interface InterviewComputedField {
  section: string;
  key: string;
  label: string;
  formula: string;
  help?: string;
  _why?: string;
}

export interface InterviewReadiness {
  ready: boolean;
  blocking: { id: string; kind: string; title: string; prompt: string }[];
  assumptions: { id: string; kind: string; title: string; prompt: string }[];
  answered: number;
  total: number;
}

export interface InterviewDepartment {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  staffing: Record<string, number>;
}

export interface InterviewState {
  version: number;
  status: string;
  agenda: InterviewQuestion[];
  answers: Record<string, any>;
  readiness: InterviewReadiness;
  departments: InterviewDepartment[];
  repeat_blocks: ExtractionRepeatBlock[];
  splits: { from: string; into: string[] }[];
}

// ── Phase (b): the generated graph, held as a draft ─────────────────

export interface GeneratedGraphStep {
  id: string;
  name: string;
  type: string;
  config: Record<string, any>;
}

export interface GeneratedGraphEdge {
  source: string;
  target: string;
  branch?: string | null;
}

export interface GraphDraft {
  version: number;
  graph: {
    input_schema: { fields: any[] };
    steps: GeneratedGraphStep[];
    edges: GeneratedGraphEdge[];
  };
  issues: { code: string; message: string; node_id?: string; severity?: string }[];
  valid: boolean;
  /** (c) advisory findings — a section nobody is asked to fill. */
  warnings?: { code: string; message: string; severity?: string }[];
  /** (c) a condition's `right` snapped back onto the printed option. */
  snapped?: { step: string; field: string; from: string; to: string }[];
  stalled?: boolean;
  best_attempt?: number;
  /** (c) present once the job has been published. */
  publish?: PublishRecord | null;
  iterations: number;
  attempts: {
    iteration: number;
    steps: number;
    issues: { code: string; message: string; node_id?: string }[];
  }[];
  summary: {
    steps: number;
    edges: number;
    by_type: Record<string, number>;
    gates: {
      id: string;
      name: string;
      type: string;
      department: string;
      role: string;
      sections: string[];
    }[];
  };
  form: {
    sections: {
      key: string;
      title: string;
      fields: number;
      edit: string[];
      view: string[];
      hidden: string[];
      requester_access: string;
    }[];
  };
  model?: string | null;
  generated_at?: string | null;
}

// ── Phase (c): the review step ──────────────────────────────────────

export interface PublishGate {
  step_id: string;
  step_name: string;
  kind: string;
  department_id: string;
  department: string;
  role: string;
  sections: string[];
  people: { id: string; name: string; email: string }[];
}

export interface PublishCase {
  key: string;
  label: string;
  ending: string;
  trail: string[];
  branches: string[];
  pins: { field: string; value: any }[];
  initial_sections: string[];
  unwritten_sections: string[];
  gates: {
    step_name: string;
    kind: string;
    decision: string;
    sections: string[];
    wait_seconds: number;
  }[];
}

export interface SampleRun {
  case: string;
  label: string;
  ending?: string;
  branches?: string[];
  run_id?: string;
  instance_id?: string;
  status: string;
  error?: string | null;
  latency_ms?: number;
  trace_run_id?: string | null;
  raised_by?: string;
  started_at?: string | null;
  finished_at?: string | null;
  output?: string;
  trail?: {
    step: string;
    action: string;
    by: string;
    email: string;
    wait_seconds: number;
  }[];
}

export interface PublishRecord {
  version: number;
  form_id: string;
  form_version: number;
  workflow_id: string;
  form_slug: string;
  workflow_slug: string;
  published_by_email?: string;
  published_at?: string;
  departments: string[];
  gates: PublishGate[];
  coverage: {
    branches: string[];
    covered: string[];
    uncovered: string[];
    paths_enumerated?: number;
    paths_reachable?: number;
  };
  dropped: { ending: string; trail: string[]; why: string[] }[];
  sample_runs: SampleRun[];
  seed_script: string;
  seed_script_filename: string;
  unpublished_at?: string;
  removed?: Record<string, number>;
}

export interface PublishPreview {
  version: number;
  can_publish: boolean;
  refusals: { code: string; message: string; node_id?: string | null }[];
  warnings: { code: string; message: string; severity?: string }[];
  already_published: { form_id: string | null; workflow_id: string | null } | null;
  names: {
    form_name: string;
    form_slug: string;
    workflow_name: string;
    workflow_slug: string;
    form_description: string;
    workflow_description: string;
  };
  form: {
    sections: GraphDraft["form"]["sections"];
    sections_total: number;
    fields_total: number;
    attachments: number;
    computed: { section: string; field: string; label: string; formula: string }[];
  };
  workflow: {
    steps: number;
    edges: number;
    by_type: Record<string, number>;
    gates: PublishGate[];
    departments: string[];
  };
  samples: {
    coverage: PublishRecord["coverage"];
    dropped: PublishRecord["dropped"];
    cases: PublishCase[];
  };
  seed_script: { filename: string; lines: number; bytes: number; text: string };
}

export interface ExtractionJob {
  id: string;
  organization_id?: string | null;
  name: string;
  status:
    | "queued"
    | "running"
    | "extracted"
    | "interviewing"
    | "generated"
    | "published"
    | "failed"
    | "cancelled";
  phase?: string | null;
  phase_label?: string | null;
  progress: number;
  error?: string | null;
  error_code?: string | null;
  source_filename: string;
  source_size_bytes: number;
  page_count?: number | null;
  model_used?: string | null;
  agent_run_id?: string | null;
  prompt_tokens?: number | null;
  completion_tokens?: number | null;
  cost_usd?: number | null;
  duration_ms?: number | null;
  stats: Record<string, any>;
  schema_issues: string[];
  warnings: ExtractionFlag[];
  created_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  extracted_at?: string | null;
  reviewed_at?: string | null;
  interview_answered?: number;
  interview_total?: number;
  interview_ready?: boolean;
  graph_valid?: boolean | null;
  graph_steps?: number | null;
  graph_warnings?: number;
  /** (c) what the publish produced. */
  form_id?: string | null;
  workflow_id?: string | null;
  sample_runs?: number;
  sample_runs_ok?: number;
  branches_covered?: number;
  branches_total?: number;
  /** Only on the detail endpoint. */
  draft?: ExtractionDraft | null;
  graph_draft?: GraphDraft | null;
}

/** Same frame vocabulary as WorkflowStreamEvent, plus a monotonic `seq`
 *  so a reconnect can resume with `after_seq` instead of replaying. */
export interface ExtractionStreamEvent {
  type: "job_started" | "phase" | "job_finished" | "job_error" | "stream_timeout" | string;
  seq: number;
  job_id?: string;
  filename?: string;
  phase?: string;
  label?: string;
  message?: string;
  progress?: number;
  error?: string;
  code?: string;
  job?: ExtractionJob;
}

export const askAiApi = {
  list: (limit = 100) =>
    api<ExtractionJob[]>("/api/v1/form-extraction/jobs" + qs({ limit })),
  get: (id: string) =>
    api<ExtractionJob>(`/api/v1/form-extraction/jobs/${encodeURIComponent(id)}`),
  /** Multipart — the browser sets the Content-Type boundary itself. */
  upload: (file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return apiUpload<ExtractionJob>("/api/v1/form-extraction/jobs", form);
  },
  rename: (id: string, name: string) =>
    api<ExtractionJob>(`/api/v1/form-extraction/jobs/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { name },
    }),
  remove: (id: string) =>
    api(`/api/v1/form-extraction/jobs/${encodeURIComponent(id)}`, { method: "DELETE" }),
  /** Fire-and-forget start; poll `get()` for progress. */
  run: (id: string) =>
    api<ExtractionJob>(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/run`,
      { method: "POST" }
    ),
  /** Start (or attach to) the run and stream progress — same SSE shape as
   *  workflowsApi.runStream, through the same apiSse helper. */
  runStream: (
    id: string,
    onEvent: (e: ExtractionStreamEvent) => void,
    afterSeq = 0,
    signal?: AbortSignal
  ) =>
    apiSse(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/run/stream`,
      { after_seq: afterSeq },
      onEvent,
      signal
    ),
  /** The reviewer's corrections. Provenance and review state are decided
   *  server-side — anything sent in `meta` beyond `review_state` is
   *  ignored. */
  saveDraft: (id: string, sections: ExtractedSection[]) =>
    api<ExtractionJob>(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/draft`,
      { method: "PUT", body: { sections } }
    ),
  // ── Phase (b) ────────────────────────────────────────────────────
  /** The agenda, rebuilt from the current draft, plus answers so far. */
  interview: (id: string) =>
    api<InterviewState>(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/interview`
    ),
  /** Partial answers. Splits and permission matrices are applied to the
   *  draft server-side, so the response carries the NEW agenda. */
  answer: (id: string, answers: Record<string, any>) =>
    api<InterviewState>(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/interview`,
      { method: "PUT", body: { answers } }
    ),
  /** Fire-and-forget generation; poll `get()`. */
  generate: (id: string) =>
    api<ExtractionJob>(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/generate`,
      { method: "POST" }
    ),
  /** Generation with the validator loop streamed as it happens. */
  generateStream: (
    id: string,
    onEvent: (e: ExtractionStreamEvent) => void,
    afterSeq = 0,
    signal?: AbortSignal
  ) =>
    apiSse(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/generate/stream`,
      { after_seq: afterSeq },
      onEvent,
      signal
    ),
  // ── Phase (c): the review step, the publish, the seed script ─────
  /** Everything the publish WOULD create, before it creates any of it —
   *  including every gate re-resolved to named people, because the
   *  generation-time staffing snapshot may be days old. */
  publishPreview: (id: string) =>
    api<PublishPreview>(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/publish-preview`
    ),
  /** Creates a real published form and workflow, then drives the sample
   *  runs. `confirm` is required by the server, not by politeness. */
  publish: (id: string, sampleRuns = true) =>
    api<ExtractionJob>(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/publish`,
      { method: "POST", body: { confirm: true, sample_runs: sampleRuns } }
    ),
  /** Undo: the sample runs, their instances, the workflow and the form. */
  unpublish: (id: string) =>
    api<ExtractionJob>(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/publish`,
      { method: "DELETE" }
    ),
  /** The publish, as a re-runnable idempotent script. */
  seedScriptBlobUrl: async (id: string): Promise<string> => {
    const blob = await apiBlob(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/seed-script`
    );
    return URL.createObjectURL(blob);
  },
  sourceUrl: (id: string) =>
    `${API_BASE_URL}/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/source`,
  /** Authenticated fetch of the uploaded PDF → blob URL for an <iframe>. */
  sourceBlobUrl: async (id: string): Promise<string> => {
    const blob = await apiBlob(
      `/api/v1/form-extraction/jobs/${encodeURIComponent(id)}/source`
    );
    return URL.createObjectURL(blob);
  },
};

export const approvalsApi = {
  list: () => api<ApprovalItem[]>("/api/v1/approvals"),
  history: (limit = 20) => api<ApprovalItem[]>("/api/v1/approvals/history" + qs({ limit })),
  /** Gates on runs *I* initiated (pending + decided) — the requester view. */
  myRequests: (limit = 20) =>
    api<ApprovalItem[]>("/api/v1/approvals/my-requests" + qs({ limit })),
  /** Full audit trail of the run behind one approval row. */
  audit: (approvalId: string) =>
    api<ApprovalAudit>(`/api/v1/approvals/${encodeURIComponent(approvalId)}/audit`),
  /** NOTE: decisions are addressed by RUN id, not approval id. */
  approve: (runId: string, comment?: string) =>
    api<WorkflowRun>(`/api/v1/workflows/runs/${encodeURIComponent(runId)}/approve`, {
      method: "POST",
      body: comment?.trim() ? { comment: comment.trim() } : {},
    }),
  reject: (runId: string, comment?: string) =>
    api<WorkflowRun>(`/api/v1/workflows/runs/${encodeURIComponent(runId)}/reject`, {
      method: "POST",
      body: comment?.trim() ? { comment: comment.trim() } : {},
    }),
};
